// ── BeeLove i18n — Complete Translation System ────────────────────────────
// Covers ~95% of visible UI text. Slugs use dot notation: page.component.key
// Bee wordplay (Bee Loved, Bee Wise, Bee Honey, La Ruche) kept in all languages.

export const SUPPORTED_LANGS = [
  { code: 'en', label: 'English', flag: '🇬🇧', default: true },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

export const TRANSLATIONS = {
  en: {
    // ── NAVIGATION ────────────────────────────────────────────────────────
    'nav.discover':        'Discover',
    'nav.hive':            'Hive',
    'nav.events':          'Events',
    'nav.matches':         'Matches',
    'nav.messages':        'Messages',
    'nav.profile':         'Profile',
    'nav.settings_label':  'Display settings',
    'nav.lang_label':      'Language',
    'nav.theme_label':     'Theme',
    'nav.theme_dark':      'Dark',
    'nav.theme_light':     'Light',
    'nav.font_label':      'Text size',
    'nav.font_reset':      '{pct}% — reset',
    'nav.back':            'Back',

    // ── COMMON BUTTONS ────────────────────────────────────────────────────
    'common.save':         'Save',
    'common.cancel':       'Cancel',
    'common.back':         '← Back',
    'common.continue':     'Continue →',
    'common.done':         'Done ✦',
    'common.edit':         'Edit',
    'common.delete':       'Delete',
    'common.upgrade':      'Upgrade →',
    'common.copy':         'Copy',
    'common.copied':       '✓ Copied',
    'common.loading':      'Loading…',
    'common.verified':     '✓ Verified',
    'common.pass':         'Pass',
    'common.like':         'Like ♥',
    'common.write':        'Write ✉',
    'common.or':           'or',
    'common.soon':         'soon',
    'common.free':         'Free',
    'common.per_month':    '/mo',

    // ── AUTH ──────────────────────────────────────────────────────────────
    'auth.welcome_title':  'Find someone who has already been vouched for.',
    'auth.welcome_sub':    'BeeLove is the only dating app where friends answer a quiz about you.',
    'auth.signin':         'Sign in',
    'auth.signup':         'Create account',
    'auth.email':          'Email address',
    'auth.password':       'Password',
    'auth.signin_cta':     'Sign in →',
    'auth.quiz_tagline':   'Your friends know you. Their answers reveal your true compatibility.',
    'auth.quiz_step_label':'One last step — helps us find your best matches',
    'auth.step_name_q':    "What's your first name?",
    'auth.step_name_sub':  "This is how you'll appear to others.",
    'auth.step_name_ph':   'Your first name',
    'auth.step_age_q':     'How old are you?',
    'auth.step_age_sub':   'You must be 18 or older to join BeeLove.',
    'auth.step_age_ph':    'Your age',
    'auth.step_gender_q':  'I identify as…',
    'auth.step_gender_sub':'We use this to personalise your experience.',
    'auth.gender_man':     'Man',
    'auth.gender_woman':   'Woman',
    'auth.gender_nb':      'Non-binary / Other',
    'auth.step_seek_q':    "I'm looking for…",
    'auth.step_seek_sub':  'Select all that apply.',
    'auth.seek_man':       'Men',
    'auth.seek_woman':     'Women',
    'auth.seek_nb':        'Non-binary people',
    'auth.seek_all':       'Everyone',
    'auth.step_network_q': 'Choose your network',
    'auth.step_network_sub':'Join your local community to discover people nearby. More cities coming soon.',
    'auth.network_note':   "Networks let you connect with people in your area.\nYou can join additional networks later.",
    'auth.cta_start':      'Start — Bee Loved ✦',
    'auth.already_member': 'Already a member?',
    'auth.no_account':     'New to BeeLove?',

    // ── DISCOVER ──────────────────────────────────────────────────────────
    'discover.empty_title':     "You've seen everyone",
    'discover.empty_sub':       "You've seen everyone in your network for now. Come back tomorrow!",
    'discover.filter_all':      'All',
    'discover.filter_compatible':'Compatible',
    'discover.filter_top':      'Top rated',
    'discover.filter_nearby':   'Nearby',
    'discover.filter_verified': 'Verified',
    'discover.filter_friends':  'Friends first',
    'discover.friends_rated':   '{n} friends rated',
    'discover.match_title':     "It's a match!",
    'discover.match_sub':       'You and {name} liked each other.',
    'discover.match_send':      'Send a message',
    'discover.match_keep':      'Keep exploring',
    'discover.match_view':      'View profile',
    'discover.completion_title':'Complete your profile',
    'discover.completion_steps':'{n} step{s} left',
    'discover.quiz_unlock':     'Complete the quiz to unlock % compatibility scores with every profile.',
    'discover.quiz_resume':     '✦ Want to improve your compatibility score? Pick up where you left off.',
    'discover.quiz_cta':        'Resume quiz →',
    'discover.quiz_skip':       'Skip for now →',
    'discover.report_btn':      'Report or block',

    // ── PROFILE SECTIONS (view other's profile) ───────────────────────────
    'profile_view.bio_label':      'About',
    'profile_view.interests_label':'Interests',
    'profile_view.lifestyle_label':'Lifestyle',
    'profile_view.values_label':   'Values',
    'profile_view.looking_for':    'Looking for',
    'profile_view.compat_label':   'Compatibility',
    'profile_view.compat_sub':     'You vs {name}',
    'profile_view.friends_label':  'From their friends',
    'profile_view.music_label':    'Music',
    'profile_view.prompts_label':  'Prompts',
    'profile_view.tab_about':      'About',
    'profile_view.tab_prompts':    'Prompts',
    'profile_view.tab_compat':     'Compatibility',
    'profile_view.drinking':       'Drinking',
    'profile_view.smoking':        'Smoking',
    'profile_view.exercise':       'Exercise',
    'profile_view.diet':           'Diet',
    'profile_view.pets':           'Pets',
    'profile_view.love_language':  'Love language',
    'profile_view.sleep':          'Sleep',

    // ── MY PROFILE ────────────────────────────────────────────────────────
    'my_profile.tab_profile':   'Profile',
    'my_profile.tab_about':     'About Me',
    'my_profile.tab_insights':  'My Insights',
    'my_profile.tab_invite':    'Invite',
    'my_profile.tab_journey':   'Journey',
    'my_profile.tab_settings':  'Settings',
    'my_profile.add_photo':     'Add photo',
    'my_profile.bio_label':     'About',
    'my_profile.prompts_label': 'Prompts',
    'my_profile.interests_label':'Interests',
    'my_profile.completion_title':'Profile strength',
    'my_profile.completion_sub': 'Complete your profile to attract better matches.',
    'my_profile.stats_title':   'This week',
    'my_profile.stats_views':   'Profile views',
    'my_profile.stats_likes':   'Likes received',
    'my_profile.stats_match':   'Match rate',
    'my_profile.add_interests': 'Add interests →',
    'my_profile.bio_placeholder':'Tell people who you really are…',
    'my_profile.prompt_placeholder':'Tap to answer…',
    'my_profile.prompt_choose': 'Choose a prompt…',

    // ── PROFILE SETTINGS ──────────────────────────────────────────────────
    'settings.quiz_title':          'Compatibility Quiz',
    'settings.quiz_completed':      '✦ Completed',
    'settings.quiz_sub_done':       'Your answers power every compatibility score you see. Update them anytime if your outlook has changed.',
    'settings.quiz_sub_pending':    'Complete the quiz to unlock % compatibility scores with every profile.',
    'settings.quiz_update_cta':     'Update answers ✦',
    'settings.quiz_take_cta':       'Take the quiz ✦',
    'settings.discovery_title':     'Discovery',
    'settings.max_distance':        'Max distance',
    'settings.age_range':           'Age range',
    'settings.account_title':       'Account',
    'settings.edit_email':          'Edit email',
    'settings.change_password':     'Change password',
    'settings.verify_identity':     'Verify identity',
    'settings.linked_accounts':     'Linked accounts',
    'settings.download_data':       'Download my data',
    'settings.delete_account':      'Delete account',
    'settings.network_title':       'Network',
    'settings.network_active':      'Active',
    'settings.network_coming':      'More cities coming soon — Toronto, Vancouver, Paris and more.',
    'settings.pause_label':         'Pause my profile',
    'settings.pause_desc':          "Your profile won't appear in Discover while paused. Your matches and messages are preserved.",
    'settings.paused_notice':       '⏸ Your profile is currently paused',
    'settings.blocked_title':       'Blocked users',
    'settings.unblock':             'Unblock',
    'settings.admin_link':          'Admin dashboard',
    'settings.pause_title':         'Pause & Privacy',

    // ── INVITE TAB ────────────────────────────────────────────────────────
    'invite.rate_title':       'Friends who rate you',
    'invite.rate_cta':         'Get more ratings',
    'invite.progress_live':    '✦ Your compatibility profile is active. More friends = richer insights.',
    'invite.progress_needed':  'You need {n} more friend response{s} to unlock your compatibility profile.',
    'invite.how_title':        'How friend ratings work',
    'invite.how_1':            'Your friend opens your link — no account needed',
    'invite.how_2':            'They answer 10 questions about your personality and character',
    'invite.how_3':            'Their answers are anonymously aggregated — individual responses are never shown',
    'invite.how_4':            'Once 5 friends complete the quiz, your compatibility profile is revealed',
    'invite.how_5':            'The more friends respond, the richer and more accurate your profile becomes',
    'invite.referral_title':   'Invite friends to BeeLove 🐝',
    'invite.referral_reward':  'For every 2 friends who join, you get 1 month of Bee Honey free.',
    'invite.friends_joined':   'Friends joined',
    'invite.months_earned':    'Months earned',
    'invite.next_reward':      'Next reward in',
    'invite.more':             'more',
    'invite.copy_code':        'Copy code',

    // ── MATCHES ───────────────────────────────────────────────────────────
    'matches.new_title':    'New Matches',
    'matches.all_title':    'All Matches',
    'matches.matched':      'Matched',
    'matches.empty_title':  'Your matches will appear here.',
    'matches.empty_sub':    'Keep swiping to find compatible people.',
    'matches.expiry':       '⏱ {days}d left to connect',
    'matches.expired':      'Expired',
    'matches.compat_label': '{pct}% match',
    'matches.tap_compat':   'Tap to see compatibility →',
    'matches.no_compat':    'Complete the quiz to unlock compatibility →',

    // ── MESSAGES ──────────────────────────────────────────────────────────
    'messages.tab_matched':   'Matched',
    'messages.tab_received':  'Received',
    'messages.sort_recent':   'Recent',
    'messages.sort_compat':   'Compat',
    'messages.empty_matched': 'No conversations yet.',
    'messages.empty_received':'No messages received.',
    'messages.ice_label':     '✦ Conversation starters',
    'messages.ice_own':       'Write my own →',
    'messages.like_back':     'Like back',
    'messages.ignore':        'Ignore',
    'messages.received_notice':"They don't know you've read this.",
    'messages.received_sub':  'Like their profile to match and start chatting.',
    'messages.input_ph':      'Message…',
    'messages.send':          'Send',
    'messages.wrote_to_you':  '· wrote to you',
    'messages.near_you':      'Near you',
    'matches.compat_title':    'Compatibility',
    'matches.compat_breakdown': 'Compatibility breakdown',
    'matches.personality':      'Personality overlap',
    'matches.connections':      'Your connections',
    'matches.empty_bee':        'Bee Patient',
    'matches.empty_swipe':      'Start swiping to find your matches.',
    'messages.empty_bee':       'Bee In Contact',
    'messages.write_own':       'Write my own →',
    'messages.no_matches':      'No matches yet',
    'messages.swipe_hive':      'Start swiping to find your hive ✦',
    'messages.say_hello':       'Say hello ✦',
    'messages.unread_notice':   "They don't know if you've read their message",
    'messages.no_incoming':     'No incoming messages',
    'messages.incoming_sub':    'When someone writes before matching, it appears here.',

    // ── BEEHIVE ───────────────────────────────────────────────────────────
    'hive.title':          'BeeLove Community',
    'hive.subtitle':       'Your hive. Real connections.',
    'hive.tab_community':  'Community',
    'hive.tab_journey':    'My Journey',
    'hive.tab_week':       'This Week',
    'hive.all_groups':     'All groups',
    'hive.no_members':     'No members in this group yet.',
    'hive.members':        '{n} members',
    'hive.beewise_title':  'Bee Wise',
    'hive.beewise_sub':    'Insights on love, communication & connection',
    'hive.stories_title':  'Stories from the hive',
    'hive.season_sub':     'Matching profiles get boosted visibility',
    'hive.season_note':    'Questions and prompts this week are curated around this theme. Show up authentically.',
    'hive.week_title':     'Current Season',
    'hive.stats_title':    'Hive This Week',
    'hive.role_label':     'Your Bee role',
    'hive.mood_label':     'Current mood',
    'hive.hive_score':     'Hive Score',

    // ── EVENTS ────────────────────────────────────────────────────────────
    'events.title':        'Events',
    'events.subtitle':     'Meet your matches IRL — curated for compatibility',
    'events.partners_label':'Bee Date partners — show your app for discounts',
    'events.free':         'Free',
    'events.free_gold':    '✦ Free',
    'events.register':     'Register',
    'events.waitlist':     'Join waitlist',
    'events.spots_left':   'Only {n} left!',
    'events.spots':        'Spots',
    'events.remaining':    '{n} of {max} remaining',
    'events.included':     "What's included",
    'events.pricing':      'Pricing',
    'events.price_standard':'Standard',
    'events.price_honey':  'Bee Honey',
    'events.price_gold':   'Bee Gold',
    'events.price_free_gold':'Free ✦',
    'events.price_off_10': '(10% off)',
    'events.price_off_20': '(20% off)',
    'events.reserve_free': 'Reserve my spot — Free ✦',
    'events.register_cta': 'Register — ${price} CAD',
    'events.export_ics':   'Add to calendar (.ics)',
    'events.export_google':'Google Calendar',
    'events.export_label': 'Export:',
    'events.ics_label':    '.ics / Apple / Outlook',
    'events.coming_soon':  'Events coming soon',
    'events.coming_sub':   "BeeLove events are being planned for Montréal. Stay tuned — you'll be the first to know.",
    'events.save_pct':     'Honey & Gold members save 10–20%',
    'events.date_time':    'Date & Time',
    'events.location':     'Location',

    // ── REPORT/BLOCK ──────────────────────────────────────────────────────
    'report.title':            'Report or Block {name}',
    'report.report_cta':       'Report {name}',
    'report.report_sub':       'Something feels wrong',
    'report.block_cta':        'Block',
    'report.block_sub':        "They won't be able to see you or contact you",
    'report.both_cta':         'Report & Block',
    'report.both_sub':         'Report then block immediately',
    'report.reason_label':     "What's going on?",
    'report.reason_anon':      'Your report is anonymous and helps keep BeeLove safe.',
    'report.urgent_badge':     'urgent',
    'report.submit_report':    'Submit report',
    'report.submit_both':      'Submit report & block',
    'report.block_confirm':    'Block {name}?',
    'report.block_warning':    "They won't be able to see your profile, send you messages, or appear in your Discover feed. You can unblock them from your profile settings.",
    'report.block_yes':        'Yes, block {name}',
    'report.done_blocked':     'Blocked',
    'report.done_both':        'Reported & blocked',
    'report.done_reported':    'Report submitted',
    'report.done_blocked_msg': "{name} has been blocked. You won't see each other anymore.",
    'report.done_sub':         'Thank you for helping keep BeeLove safe. Our team reviews all reports within 24 hours.',

    // ── PROFILE COMPLETION ────────────────────────────────────────────────
    'completion.banner_title': 'Complete your profile',
    'completion.steps_left':   '{n} step{s} left',
    'completion.go':           'Go →',
    'completion.milestone_25': 'Good start! Add a few more details to attract better matches.',
    'completion.milestone_50': "You're halfway there. Profiles with 50%+ get 3× more likes.",
    'completion.milestone_75': 'Almost complete! Profiles above 75% get 5× more matches.',
    'completion.milestone_100':"Perfect profile! You're showing your best self. 🐝",
    'completion.stats_sub':    'Profiles above 75% complete get 5× more matches.',
    'completion.boost_link':   'Boost your profile →',

    // ── UPGRADE / PLANS ───────────────────────────────────────────────────
    'upgrade.title':           'Upgrade your dating game',
    'upgrade.sub':             'Cancel anytime. No hidden fees.',
    'upgrade.tab_plans':       'Plans',
    'upgrade.tab_addons':      'Add-ons',
    'upgrade.current_plan':    '✓ Current plan',
    'upgrade.trial':           '{n}-day free trial',
    'upgrade.start_trial':     'Start {n}-day trial →',
    'upgrade.most_popular':    'MOST POPULAR',
    'upgrade.stay_free':       'Stay free',

    // ── BADGES ────────────────────────────────────────────────────────────
    'badges.earned_title':     'My badges',
    'badges.locked_title':     'To unlock',
    'badges.visible':          '● visible',
    'badges.hidden':           '○ hidden',
    'badges.always_shown':     'always shown',
    'badges.private':          'private',

    // ── BEE WISE ─────────────────────────────────────────────────────────
    'beewise.title':           'Bee Wise',
    'beewise.subtitle':        'Insights on love, communication, and connection',
    'beewise.featured':        'Featured',
    'beewise.more':            'More articles',
    'beewise.read_time':       '{n} min read',
    'beewise.cat_all':         'All',

    // ── SPOTIFY ───────────────────────────────────────────────────────────
    'spotify.connect':         'Connect Spotify',
    'spotify.connecting':      'Connecting…',
    'spotify.connected':       'Connected',
    'spotify.top_artists':     'Your top artists',
    'spotify.disconnect':      'Disconnect',
    'spotify.sub':             'Show your music taste on your profile',
    'spotify.connected_sub':   'Connected · {n} top artists shared',

    // ── LANDING ───────────────────────────────────────────────────────────
    'landing.hero_title':      'Find someone who has already been vouched for.',
    'landing.hero_sub':        'BeeLove is the only dating app where your friends answer a quiz about you. Their answers reveal compatibility no algorithm can fake.',
    'landing.hero_cta':        'Start for free',
    'landing.hero_signin':     'Sign in',
    'landing.stat_members':    'Members',
    'landing.stat_quiz':       'Quiz match rate',
    'landing.stat_matches':    'More matches',
    'landing.stat_networks':   'active networks',
    'landing.how_title':       'Four steps to being Bee Loved',
    'landing.trust_title':     'Built on real trust',
    'landing.trust_sub':       'Every profile is friend-vouched. Every match is earned. No bots, no fakes, no ghosts.',
    'landing.cta_title':       'Ready to be truly known?',
    'landing.cta_sub':         'The only dating app where your friends vouch for you. Real people. Real compatibility. Real love.',
    'landing.cta_btn':         'Start for free — Bee Loved ✦',
    'landing.signin_link':     'Already a member? Sign in',
    'landing.nav_how':      'How it works',
    'landing.nav_profiles': 'Profiles',
    'landing.nav_pricing':  'Pricing',
    'landing.nav_faq':      'FAQ',
    'landing.hero_sub': 'Your friends know you best. Their answers reveal who you\'re truly compatible with.',
    'landing.how_step1_title': 'Create your profile',
    'landing.how_step1_body':  'Photos, bio, a few prompts. Takes 3 minutes.',
    'landing.how_step2_title': 'Invite your friends',
    'landing.how_step2_body':  'Share your unique link. Friends answer 10 questions about you — anonymously.',
    'landing.how_step3_title': 'Bee Elevated',
    'landing.how_step3_body':  'Your compatibility profile deepens. The algorithm surfaces people who genuinely match yours.',
    'landing.how_step4_title': 'Bee Loved',
    'landing.how_step4_body':  'Meet IRL at BeeLove events. Build something real.',
    'landing.trust_feature1_title': 'Friend-verified profiles',
    'landing.trust_feature1_desc':  'Minimum 5 friends answer a quiz about you before your profile is complete. No self-promotion.',
    'landing.trust_feature2_title': 'Real compatibility',
    'landing.trust_feature2_desc':  '10-question quiz from people who actually know you, not a self-written bio.',
    'landing.trust_feature3_title': 'Safe community',
    'landing.trust_feature3_desc':  'All profiles moderated. Report and block always available.',
    // ── AUTO-MERGED FROM PATCHES ─────────────────────────────────────────
    'landing.nav_concept': 'The concept',
    'landing.tagline': 'The social dating app',
    'landing.problems_fixed': "Dating apps are broken. We fixed them.",
    'landing.real_members': 'Real members',
    'landing.real_stories': 'Real stories',
    'landing.questions_label': 'Questions',
    'landing.ready': 'Ready?',
    'landing.hundreds_more': 'Hundreds more profiles — verified, rated, real.',
    'landing.plan_free_label': 'Free forever',
    'landing.plan_honey_label': 'Most popular',
    'landing.plan_gold_label': 'For the devoted',
    'landing.plan_royal_label': 'For serious daters',
    'landing.go_royal': 'Go Royal ✦',
    'landing.the_one': 'The One',

    // Auth extra,
    'auth.or_email': 'OR WITH EMAIL',
    'auth.welcome_back': 'Welcome back',
    'auth.sign_out':         'Sign out',
    'auth.forgot_password':  'Forgot password?',
    'auth.reset_sent':       'Reset link sent — check your inbox.',
    'auth.error_email_required': 'Please enter your email address.',
    'auth.privacy_policy': 'Privacy Policy',
    'auth.network_note2': 'You can join additional networks later.',
    'auth.or': 'or',

    // Discover extra,
    'discover.bee_noticed': 'Bee Noticed',
    'discover.you_seen_all': "You've seen them all",
    'discover.start_over': 'Start over',
    'discover.report_received': 'Report received',
    'discover.report_anon': 'Your report is anonymous. We review every report within 24h.',
    'discover.report_title': 'Report this profile',
    'discover.we_review': "We'll review this profile within 24 hours.",
    'discover.skip_now': 'Skip for now',

    // Profile extra — bio generator,
    'profile.bio_answer_prompts': 'Answer a few prompts for a better bio:',
    'profile.answer_all': 'Answer all to save',
    'profile.based_on': 'Based on',
    'profile.basics_label': 'Basics',
    'profile.anonymous': 'Anonymous',
    'profile.anon_average': 'Anonymous average',
    'profile.bee_mood': 'Bee Mood',
    'profile.bee_role': 'Bee Role',
    'profile.current_season': 'Current season theme',
    'profile.earn_honey': 'Earn free Bee Honey',
    'profile.honey_reward_desc': 'For every 2 friends who join, you get 1 month of Bee Honey free.',
    'profile.active_label': 'Active',
    'profile.share_rating': 'Share your rating link',
    'profile.friends_know': 'Send this link to at least 5 friends. They answer questions about your character — not just your photos.',
    'profile.friends_responded': '{n} friends responded',
    'profile.account_label': 'Account',
    'profile.edit_email': 'Edit email',
    'profile.change_password': 'Change password',
    'profile.verify_identity': 'Verify identity',
    'profile.linked_accounts': 'Linked accounts',
    'profile.download_data': 'Download my data',
    'profile.delete_account': 'Delete account',
    'profile.seeking_label': 'Seeking',
    'profile.visible_label': 'Profile visible',
    'profile.incognito_label': 'Incognito mode',
    'profile.notifs_label': 'Notifications',
    'profile.on': 'On',
    'profile.off': 'Off',
    'profile.yes': 'Yes',
    'profile.no': 'No',
    'profile.network_active': 'Active',
    // Journey tab,
    'profile.journey_stage': 'stages',
    'profile.bee_loved_label': 'Bee Loved',
    'profile.remaining': 'remaining',
    'profile.stage_completed': 'Completed',
    'profile.hive_score_label': 'Hive Score',
    'profile.your_role': 'Your role',
    'profile.your_mood': 'Your mood',
    // Ratings/insights tab,
    'profile.ratings_visible': 'Visible on your card, your matches and in messages.',
    'profile.ratings_7days': 'Visible on your profile for 7 days',
    'profile.coherence_label': 'Coherence',
    'profile.axis_label': 'Profile axes',
    // About tab,
    'profile.location_label': 'Location',
    'profile.city_label':     'City',
    'profile.bio_label':      'About me',
    'profile.job_label': 'Job',
    'profile.languages_label': 'Languages',
    'profile.add_location': 'Add location…',
    'profile.add_job': 'Add job…',

    // BeeHive extra,
    'hive.week_this': 'This week in the hive',
    'hive.earned_desc': 'Earned through genuine engagement. Never decreases.',
    'hive.find_people': 'Find your people by what you love.',
    'hive.role_desc': 'Your role reveals itself as you engage.',
    'hive.roles_list': 'Scout · Builder · Connector · Queen Bee',
    'hive.your_answer':      'Your answer',
    'hive.answer_ph':        'Your honest answer...',
    'hive.weekly_question':  'Question of the week',
    'hive.answer_btn':       'Answer',
    'hive.weekly_challenge': "This week's challenge",
    'hive.coming_soon':      'BeeHive is coming soon',
    'hive.coming_soon_sub':  'Stay tuned — this feature is on its way.',

    'beeconnect.coming_soon': 'BeeConnect is coming soon',
    'circles.coming_soon':    'Scout is coming soon',
    'common.stay_tuned':      'Stay tuned — this feature is on its way.',

    'profile.generate_bio':   'Generate Bio',

    // Events extra,
    'events.no_upcoming': 'No upcoming events in this category.',
    'events.upgrade_cta': 'Upgrade →',
    'events.whats_included': "What's included",
    'events.export_label2': 'Export:',
    'events.city': 'Montréal',

    // Navbar extra,
    'nav.your_networks': 'Your networks',
    'nav.more_cities': 'More cities coming. Networks let you connect with people nearby.',
    'nav.bee_the_one': 'Bee The One ✦',

    // ProfileCompletion extra,
    'completion.profile_strength': 'Profile strength',
    'completion.this_week': 'This week',
    'completion.boost': 'Boost your profile →',

    // ReportBlock extra,
    'report.details_ph': 'Additional details (optional)',
    'report.anon_help': "What's going on? Your report is anonymous and helps keep BeeLove safe.",

    // StaticPage,
    'static.coming_soon': 'Coming soon',
    'static.being_prepared': 'This page is being prepared.',

    // Common additions,
    'common.ignore': 'Ignore',
    'common.name': 'Name',
    'common.or_with_email': 'or with email',
    'common.on': 'On',
    'common.off': 'Off',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.close': 'Close',
    'common.settings': 'Settings',
    'common.search': 'Search…',
    'common.km': 'km',
    'common.select': 'Select',
    'common.none': 'None',

    // ── COMPREHENSIVE PATCH v2 ────────────────────────────────────────────
    // Landing,
    'landing.join_free': 'Join thousands who chose honesty over algorithms.',
    'landing.no_dark': 'No dark patterns. No fake urgency. Upgrade only when it makes sense for you.',
    'landing.no_swiping': 'No swiping in the dark. No fake profiles. Just real people, vouched for.',
    'landing.every_other_app': 'Every other app gives everyone the same shot. We give the best shot to the most genuine people.',
    'landing.how_it_works': 'How it works →',
    'landing.free_join': 'Free to join · No credit card · Cancel anytime',
    'landing.plan_honey': 'Honey',
    'landing.step_tag_free': 'Free forever',
    'landing.step_tag_friends': 'Min. 5 friends',
    'landing.step_tag_smart': 'Smart matching',
    'landing.step_tag_real': 'Real connections',
    'landing.footer_links_company': 'Company',
    'landing.footer_links_legal': 'Legal',
    // Auth,
    'auth.join_free': 'Bee Loved — join free →',
    'auth.continue_google': 'Continue with Google',
    'auth.continue_apple': 'Continue with Apple',
    'auth.continue_fb': 'Continue with Facebook',
    'auth.sign_in_email': 'Sign in with email',
    'auth.previous': 'Previous',
    'auth.step_quiz_label': 'One last step — helps us find your best matches',
    // Discover,
    'discover.like_btn': 'LIKE',
    'discover.pass_btn': 'PASS',
    'discover.keep_exploring': 'Keep Exploring',
    'discover.filter_bestmatch': 'Best Match',
    'discover.filter_toprated': 'Top Rated',
    'discover.label_lifestyle': 'Lifestyle',
    'discover.label_values': 'Values',
    'discover.label_interests': 'Interests',
    'discover.label_prompts': 'Prompts',
    'discover.label_friends': 'From their friends',
    'discover.label_looking': 'Looking for',
    'discover.label_compat': 'Compatibility',
    'discover.open_friends': 'Open to Friends',
    'discover.super_like': 'Super like',
    'discover.view_profile': 'View profile',
    // Matches,
    'matches.close': 'Close',
    'matches.discover_cta': 'Discover profiles →',
    'matches.axis_stability': 'Identity stability',
    'matches.axis_boredom': 'Boredom threshold',
    'matches.axis_conflict': 'Conflict style',
    'matches.axis_generosity': 'Generosity',
    'matches.axis_presence': 'Presence',
    'matches.axis_repair': 'Repair',
    'matches.axis_self_aware': 'Self-aware',
    'matches.axis_social': 'Social',
    'matches.axis_accountability': 'Accountability',
    // Messages,
    'messages.like_match': 'Like back — match now',
    'messages.icebreakers_label': '✦ Icebreakers',
    // BeeHive,
    'hive.close': 'Close',
    'hive.edit': 'Edit',
    'hive.open_profile': 'Open Profile',
    'hive.new_members': 'New members',
    'hive.conversations': 'Conversations',
    'hive.this_week_stats': 'Hive This Week',
    'hive.your_stages': 'Your stages',
    // Events,
    'events.all_events': 'All events',
    'events.spots_label': 'Spots',
    'events.free_gold': '✦ Free for Gold',
    'events.add_ics': 'Add to calendar (.ics)',
    'events.coming_sub2': "BeeLove events are being planned for Montréal. Stay tuned — you'll be the first to know.",
    // Upgrade,
    'upgrade.premium_title': 'Bee Premium',
    'upgrade.cad_mo': 'CAD/mo',
    'upgrade.no_fees': 'Cancel anytime. No hidden fees.',
    'upgrade.addons_sub': 'One-time purchases and monthly add-ons. Stack with any plan.',
    'upgrade.one_time': 'one-time',
    'upgrade.back_profile': 'Back to profile',
    // ProfileCompletion,
    'completion.go': 'Go →',
    'completion.views': 'Profile views',
    'completion.likes': 'Likes received',
    'completion.match_rate': 'Match rate',
    'completion.almost': 'Almost there!',
    // Spotify,
    'spotify.music': 'Music',
    'spotify.top_artists_label': 'Your top artists',
    // Notifications,
    'notifications.title': 'Notifications',
    // Common,
    'common.done_btn': 'Done ✦',
    'common.back_btn': '← Back',
    'common.close_btn': 'Close',
    'common.urgent': 'urgent',
    'common.go': 'Go →',
    'common.report': 'Report',

    // ── PATCH v3 — remaining strings ──────────────────────────────────────
    // Landing step titles & features,
    'landing.step1_title': 'Bee Presented',
    'landing.step2_title': 'Bee Endorsed',
    'landing.step3_title': 'Bee Elevated',
    'landing.step4_title': 'Bee In Love',
    'landing.feat1_title': 'Friend-verified profiles',
    'landing.feat1_desc': 'Minimum 5 friends answer a quiz about you before your compatibility profile is complete. No self-promotion. No fake hype. Just honest insight from people who know you.',
    'landing.feat2_title': 'Values-based matching',
    'landing.feat2_desc': 'Our 10-question compatibility quiz goes beyond hobbies. We match on what actually matters — lifestyle, values, and how you handle life together.',
    'landing.feat3_title': 'Built-in fake protection',
    'landing.feat3_desc': 'You cannot fake a friend rating. No bot can ask real humans to vouch for it. Every verified profile is a real person with real relationships.',
    'landing.feat4_title': 'AI-powered bio writing',
    'landing.feat4_desc': 'Answer 5 quick questions. The AI writes your bio in your voice — warm, genuine, you.',
    'landing.footer_product': 'Product',
    // Auth,
    'auth.network_montreal': 'Montréal',
    'auth.network_montreal_area': 'Greater Montréal area',
    // Discover,
    'discover.bee_patient': 'Bee Patient',
    'discover.submit_report': 'Submit report',
    // Profile data dropdowns,
    'profile.edu_highschool': 'High school',
    'profile.edu_college': 'Some college',
    'profile.edu_bachelor': 'Bachelor',
    'profile.edu_master': 'Master',
    'profile.edu_trade': 'Trade / Vocational',
    'profile.edu_other': 'Other',
    'profile.intent_relationship': 'Relationship',
    'profile.intent_casual': 'Something casual',
    'profile.intent_friends': 'Friendship first',
    'profile.intent_marriage': 'Marriage-minded',
    'profile.intent_notsure': 'Not sure yet',
    'profile.drink_never': 'Never',
    'profile.drink_rarely': 'Rarely',
    'profile.drink_socially': 'Socially',
    'profile.drink_regularly': 'Regularly',
    'profile.smoke_never': 'Never',
    'profile.smoke_rarely': 'Rarely',
    'profile.smoke_socially': 'Socially',
    'profile.smoke_daily': 'Daily',
    'profile.exercise_never': 'Never',
    'profile.exercise_sometimes': 'Sometimes',
    'profile.exercise_often': 'Often',
    'profile.exercise_daily': 'Daily',
    'profile.diet_omnivore': 'Omnivore',
    'profile.diet_vegetarian': 'Vegetarian',
    'profile.diet_vegan': 'Vegan',
    'profile.diet_pescatarian': 'Pescatarian',
    'profile.diet_halal': 'Halal',
    'profile.diet_kosher': 'Kosher',
    'profile.diet_glutenfree': 'Gluten-free',
    'profile.pet_none': 'No pets',
    'profile.pet_dog': 'Dog(s)',
    'profile.pet_cat': 'Cat(s)',
    'profile.pet_other': 'Other pets',
    'profile.sleep_early': 'Early bird',
    'profile.sleep_night': 'Night owl',
    'profile.sleep_flexible': 'Flexible',
    'profile.lang_english': 'English',
    'profile.lang_french': 'French',
    'profile.lang_spanish': 'Spanish',
    'profile.lang_arabic': 'Arabic',
    'profile.lang_portuguese': 'Portuguese',
    'profile.lang_mandarin': 'Mandarin',
    'profile.love_words': 'Words of affirmation',
    'profile.love_time': 'Quality time',
    'profile.love_gifts': 'Gifts',
    'profile.love_service': 'Acts of service',
    'profile.love_touch': 'Physical touch',
    'profile.politics_liberal': 'Liberal',
    'profile.politics_moderate': 'Moderate',
    'profile.politics_conservative': 'Conservative',
    'profile.politics_apolitical': 'Apolitical',
    'profile.religion_none': 'Not religious',
    'profile.religion_christian': 'Christian',
    'profile.religion_muslim': 'Muslim',
    'profile.religion_jewish': 'Jewish',
    'profile.religion_hindu': 'Hindu',
    'profile.religion_buddhist': 'Buddhist',
    'profile.religion_spiritual': 'Spiritual',
    'profile.religion_agnostic': 'Agnostic',
    'profile.religion_atheist': 'Atheist',
    'profile.cannabis_no': 'No',
    'profile.cannabis_sometimes': 'Sometimes',
    'profile.cannabis_yes': 'Yes',
    'profile.use_this_bio': 'Use this bio',
    'profile.choose_prompt': 'Choose a prompt…',
    'profile.identity_values': 'Identity & Values',
    'profile.lifestyle_label': 'Lifestyle',
    // Matches axis labels,
    'matches.axis_conflict2': 'Conflict',
    'matches.axis_stability2': 'Stability',
    'matches.axis_boredom2': 'Boredom',
    'matches.axis_stakes': 'Stakes',
    // BeeHive stat labels,
    'hive.stat_matches': 'Matches',
    'hive.stat_stories': 'Stories',
    // BeeWise article data — translated at article level,
    'beewise.cat_comm': 'Communication',
    'beewise.cat_self': 'Self-knowledge',
    'beewise.cat_dating': 'Dating psychology',
    'beewise.cat_flags': 'Red flags',
    'beewise.cat_dates': 'First dates',
    'beewise.art1_title': 'The 5 things your first message says about you',
    'beewise.art1_sub': 'First impressions in text are everything.',
    'beewise.art2_title': 'What your friend ratings are really telling you',
    'beewise.art2_sub': 'The gap between how you see yourself and how others see you.',
    'beewise.art3_title': 'Why compatible values matter more than common hobbies',
    'beewise.art3_sub': 'Research shows what actually predicts long-term relationship satisfaction.',
    'beewise.art4_title': 'The 7 early signs that someone is genuinely kind',
    'beewise.art4_sub': 'Not what they say — what they do when no one is watching.',
    'beewise.art5_title': 'The best first date in Montréal for every personality type',
    'beewise.art5_sub': 'Local recommendations matched to compatibility profiles.',
    'beewise.art6_title': 'How to know when to move from app to real life',
    'beewise.art6_sub': 'The right moment to suggest meeting — and how to do it.',

    // Landing — comprehensive missing strings,
    'landing.how_arrow': 'How it works →',
    'landing.concept': 'The concept',
    'landing.four_steps': 'Four steps to',
    'landing.people_worth': 'People worth',
    'landing.knowing': 'knowing',
    'landing.hundreds': 'Hundreds more profiles — verified, rated, real.',
    'landing.see_waiting': 'See who is waiting for you ✦',
    'landing.why_title': '✦ Why BeeLove',
    'landing.broken_1': 'Dating apps are',
    'landing.broken_2': 'broken.',
    'landing.fixed': 'We fixed them.',
    'landing.found_love': 'They found love.',
    'landing.bee_together': 'Now they',
    'landing.bee_together_2': 'together.',
    'landing.start_free': 'Start free.',
    'landing.bee_royal_ready': "when you're ready.",
    'landing.plan_feat_likes': '10 likes per day',
    'landing.plan_feat_quiz': 'Compatibility quiz',
    'landing.plan_feat_ai_bio': 'AI bio generator',
    'landing.plan_feat_invites': '5 friend invitations',
    'landing.plan_feat_hive': 'Community BeeHive access',
    'landing.plan_feat_liked': 'See who liked you',
    'landing.plan_feat_unlimited': 'Unlimited invitations',
    'landing.plan_feat_boost': 'Weekly boost',
    'landing.start_trial': 'Start 7-day free trial ✦',
    'landing.everything_need': 'Everything you need',
    'landing.to_bee_ready': 'to know to',
    'landing.faq_q1': 'How does the friend quiz work?',
    'landing.faq_a1': 'Your friends receive a private link and answer 10 questions about you — covering your personality, how you communicate, and your relationship style. You need at least 5 friends to complete the quiz before your compatibility profile is complete. Their answers are anonymously aggregated. Individual responses are never shown — even to you.',
    'landing.stop_waiting': 'Stop waiting.',
    'landing.bee_the_one_find': 'they find.',
    'landing.footer_how': 'How it works',
    'landing.footer_pricing': 'Pricing',
    'landing.footer_signup': 'Sign up',
    'landing.footer_signin': 'Sign in',
    'landing.footer_about': 'About',
    'landing.footer_blog': 'Blog',
    'landing.footer_press': 'Press',
    'landing.footer_careers': 'Careers',
    'landing.footer_privacy': 'Privacy Policy',
    'landing.footer_terms': 'Terms of Use',
    'landing.footer_cookies': 'Cookie Policy',
    'landing.footer_contact': 'Contact',
    'landing.join_free_cta': 'Join free — ',
    'landing.profiles_nav': 'Profiles',
    'landing.faq_q2': 'Can people fake the friend quiz?',
    'landing.faq_a2': 'By design, this is impossible at scale. Faking a result requires convincing multiple real people to lie for you.',
    'landing.faq_q3': 'What is the compatibility quiz and can I skip it?',
    'landing.faq_a3': 'The quiz has 10 questions across three categories — Lifestyle, Values, and Personality. It takes about 3 minutes. We use your answers to calculate a compatibility percentage with every other member, which appears directly on their profile cards. You can update your answers anytime from your profile settings.',
    'landing.faq_q4': 'Is BeeLove available in my city?',
    'landing.faq_a4': 'BeeLove is available in French and English-speaking countries: Canada, France, Belgium, Switzerland, the US, UK, Ireland, Australia and New Zealand. Montréal, Paris and Lorient are the first active networks.',
    'landing.faq_q5': "What if my friends' answers reveal something unexpected?",
    'landing.faq_a5': 'Their feedback is private — only you see the aggregated insights. Think of it as a gift: an outside perspective on what makes you genuinely remarkable. Most members say it changed how they present themselves — not by performing, but by being more authentically themselves.',
    'landing.faq_q6': 'How is BeeLove different from Hinge, Bumble, or Tinder?',
    'landing.faq_a6': 'Every other major app gives everyone an equal starting point regardless of who they are in real life. BeeLove is the first app to bring social proof into dating — your reputation among real friends directly affects your visibility. We also weight values and lifestyle compatibility over appearance.',
    'landing.hero_dont_just': "Don't just",
    'landing.hero_bee_here': 'there —',
    'landing.plans_label': 'Plans',


    // Plan feature strings — Honey + Royal
    'landing.plan_honey_likes':   'Unlimited likes',
    'landing.plan_honey_liked':   'See who liked you',
    'landing.plan_honey_quiz':    'Compatibility quiz',
    'landing.plan_honey_bio':     'AI bio generator',
    'landing.plan_honey_invites': 'Unlimited invitations',
    'landing.plan_honey_boost':   '1 weekly boost',
    'landing.plan_honey_analytics':'Profile analytics',
    'landing.plan_royal_all':     'Everything in Honey',
    'landing.plan_royal_ai':      'AI smart matching',
    'landing.plan_royal_priority':'Priority in feed',
    'landing.plan_royal_boosts':  '3 weekly boosts',
    'landing.plan_royal_vip':     'VIP events access',
    'landing.plan_royal_receipts':'Read receipts',
    'landing.plan_royal_filters': 'Advanced filters',
    'landing.plan_royal_support': 'Dedicated support',
    // Mixed JSX phrases
    'landing.four_steps_to':      'Four steps to',
    'landing.people_worth_knowing':'People worth knowing',
    'landing.see_waiting':        'See who is waiting for you ✦',
    'landing.dating_broken':      'Dating apps are',
    'landing.we_fixed':           'We fixed them.',
    'landing.they_found':         'They found love.',
    'landing.now_they':           'Now they',
    'landing.together':           'together.',
    'landing.start_free_cta':     'Start free.',
    'landing.everything_need':    'Everything you need to know',
    'landing.to_know':            'to know to',
    'landing.stop_waiting':       'Stop waiting.',
    'landing.signin_arrow':       'Sign in →',
    'landing.join_free_link':     'Join free — ',
    'landing.free_join_note':     'Free to join · No credit card · Cancel anytime',
    'landing.month_label':        '/ month',
    'landing.per_mo':             '/mo',

    // Auth onboarding strings
    'auth.step_name_sub':         "This is how you'll appear to others.",
    'auth.step_age_sub2':         'You must be 18 or older to join BeeLove.',
    'auth.step_gender_sub':       'We use this to personalise your experience.',
    'auth.step_seek_sub':         'Select all that apply.',
    'auth.step_network_sub':      'Join your local community. More cities coming soon.',
    'auth.network_connect':       'Networks let you connect with people in your area.',
    'auth.step_of':               'Step {n} of {total}',
    'auth.by_continuing':         'By continuing you accept our',
    'auth.terms_label':           'Terms',
    'auth.and':                   'and',
    'auth.no_account':            'No account?',
    'auth.create_account':        'Create account',
    'auth.sign_in_btn':           'Sign in →',
    // Landing fixes
    'landing.all_rights':         '© 2025 BeeLove.me — All rights reserved.',
    'landing.give_best_shot':     'We give the best shot to the people who deserve it.',
    'landing.join_free_em':       'Join free — ',
    'landing.ready_label':        'ready',
    'landing.the_one_find':       'The One they find.',

    // Landing hero
    'landing.hero_dont_just':     "Don't just",
    'landing.hero_bee_here': 'là —',
    'landing.hero_bee_loved':     'Bee Loved.',
    'landing.hero_friends_know':  'Your friends know you. Their answers reveal your true compatibility.',
    // Auth steps
    'auth.complete_profile':      'Complete your profile — {n} step left',
    'auth.complete_quiz':         'Complete the compatibility quiz',
    // Discover
    'discover.profiles_remaining':'profiles remaining · Swipe or tap to navigate photos',
    'discover.label_adventure':   'Adventure',
    // Navbar
    'nav.beehive':                'BeeHive',
    // BeeHive
    'hive.earned_place':          "People who've earned their place.",
    'hive.my_journey':            'My Journey & Badges',
    'hive.journey_in_profile':    'View your progress and badges in your profile →',
    // Hive tab — Profile
    'hive.bee_role':              'Bee Role',
    'hive.bee_mood':              'Bee Mood',
    'hive.journey_title':         'My Journey',
    'hive.stages':                'stages',
    'hive.remaining':             'remaining',
    'hive.next_stage':            'Next',
    'hive.my_badges':             'My Badges',
    'hive.obtained':              'obtained',
    'hive.to_unlock':             'To unlock',
    'hive.current_season':        'Current Season',
    'hive.season_theme':          "This week's theme",
    'hive.share_to_hive':         'Share to the hive',
    'hive.no_badges':             'No badges earned yet',
    // Ratings tab
    'ratings.how_seen':           'How your friends see you',
    'ratings.no_data':            'No friend ratings yet',
    'ratings.youll_rate':         'Share your link — friends answer questions about you. Their answers shape your compatibility profile.',
    'ratings.what_they_say':      'What they say',
    // Plan
    'plan.founder_tag':           'Founder access',
    'hive.season_adventure':      'Adventure Season',
    // Profile badge labels
    'profile.badge_height':       '{n} cm',
    'profile.badge_bachelor':     "Bachelor's",
    'profile.badge_music':        'Music',
    'profile.badge_casual':       'Casual',
    'profile.badge_away':         '{n} km away',
    // Common
    'common.members':             'members',

    // Auth step questions (inside ONBOARDING array)
    'auth.step_age_q2':           'How old are you?',
    'auth.step_gender_q2':        'I identify as…',
    // Discover — report reasons
    'discover.report_fake':       'Fake profile or impersonation',
    'discover.report_content':    'Inappropriate or offensive content',
    'discover.report_spam':       'Spam or scam attempt',
    'discover.report_harassment': 'Harassment or threatening behaviour',
    'discover.report_underage':   'Underage person',
    // Discover — lifestyle values in profile cards
    'discover.not_sure':          'Not sure',
    'discover.love_words':        'Words of affirmation',
    'discover.love_service':      'Acts of service',
    'discover.love_gifts':        'Gift giving',
    'discover.love_time':         'Quality time',
    'discover.love_touch':        'Physical touch',
    // Profile — prompts
    'profile.prompt_sunday':      'A perfect Sunday looks like',
    'profile.prompt_truths':      'Two truths and a lie',
    'profile.prompt_along':       'I get along best with',
    'profile.prompt_love_lang':   'My love language is',
    'profile.prompt_opinion':     'An unpopular opinion I hold',
    'profile.prompt_heart':       'The way to my heart is',
    'profile.prompt_friends':     'My friends describe me as',
    'profile.prompt_dealbreaker': 'My biggest dealbreaker is',
    // Profile — missing dropdown values
    'profile.edu_phd':            'PhD',
    'profile.edu_other2':         'Other',
    'profile.rel_agnostic':       'Agnostic',
    'profile.rel_atheist':        'Atheist',
    'profile.rel_catholic':       'Catholic',
    'profile.pol_very_liberal':   'Very liberal',
    'profile.pol_prefer_not':     'Prefer not to say',
    'profile.cannabis_allergic':  'Allergic',
    'profile.pet_allergic':       'Allergic to pets',
    'profile.exercise_1_2':       '1–2×/week',
    'profile.exercise_3_4':       '3–4×/week',
    'profile.exercise_daily2':    'Daily',
    // Messages — sort labels (in static array)
    'messages.sort_name':         'Name',
    // Matches — axis labels
    'matches.repair_speed':       'Repair speed',
    'matches.social_energy':      'Social energy',
    'matches.stakes_tolerance':   'Stakes tolerance',
    'matches.send_notice':        'Your message is sent once you tap send — nothing is sent automatically.',
    // BeeHive
    'hive.welcome':               'Welcome to the hive',
    'hive.all_complete':          'All stages complete — Bee Loved',
    // Events
    'events.reserve':             'Reserve my spot ✦',
    'events.free_ticket':         'Free ✦',
    'events.waitlist':            'Join waitlist',
    'events.reserve_free':        'Reserve my spot — Free ✦',
    // Upgrade
    'upgrade.free_label2':        'Free',
    'upgrade.stay_free':          'Stay free',
    'upgrade.back_free':          'Back to Free',
    'upgrade.plan_active':        'Your current plan remains active until end of billing period.',
    'upgrade.done':               'Done ✦',
    'upgrade.most_popular':       'MOST POPULAR',
    'upgrade.current_plan':       '✓ Current plan',
    // ReportBlock
    'report.block_warning2':      "They won't be able to see your profile or contact you.",
    'report.then_block':          'Report then block immediately',
    // Navbar static labels (module-level — used as fallback)
    'nav.discover_label':         'Discover',
    'nav.events_label':           'Events',
    'nav.matches_label':          'Matches',
    'nav.messages_label':         'Messages',
    'nav.profile_label':          'Profile',
    'nav.network_label':          'Network',

    'profile.prompt_proud':       "The thing I'm most proud of",
    'profile.prompt_looking':     "I'm looking for someone who",
    'profile.prompt_skill':       "A skill I'm embarrassingly good at",
    'profile.prompt_teach':       "Something I'd love to teach you",
    'profile.prompt_along2':      "I get along best with people who",

    // ProfileCompletion steps
    'completion.add_photo':       'Add at least one photo',
    'completion.write_bio':       'Write your bio',
    'completion.add_prompt':      'Answer a prompt',
    'completion.add_interests':   'Add your interests',
    'completion.take_quiz':       'Complete the compatibility quiz',
    'completion.invite_friend':   'Invite 1 friend to rate you',
    'completion.fill_basics':     'Fill in your basics (age, height)',
    'completion.rated_profile':   'rated your profile',
    'completion.liked_profile':   'liked your profile',
    'completion.almost':          'Almost complete! Profiles above 75% get 5× more matches.',
    'completion.boost_note':      'Profiles above 75% complete get 5× more matches. ·',
    // Profile tabs
    'profile.tab_score':          'My Score',
    // Profile interests/interests section
    'profile.prompts_label':      'Prompts',
    'profile.interests_label':    'Interests',
    // Interest categories
    'interest.strategy':          'Strategy',
    'interest.coffee':            'Coffee',
    'interest.urban_cycling':     'Urban cycling',
    'interest.design':            'Design',
    'interest.photography':       'Photography',
    'interest.jazz':              'Jazz',
    // Landing
    'landing.hero_sub2':          'Your friends know you. Their answers reveal your true compatibility.',
    // Navbar settings
    'nav.lang_label2':            'Language',
    'nav.theme_label2':           'Theme',
    'nav.font_label2':            'Text Size',
    'nav.font_reset2':            'Reset',

    // Profile milestones
    'completion.m25':             'Good start! Add a few more details to attract better matches.',
    'completion.m50':             "You're halfway there. Profiles with 50%+ get 3× more likes.",
    'completion.m75':             'Almost complete! Profiles above 75% get 5× more matches.',
    'completion.m100':            "Perfect profile! You're showing your best self. 🐝",
    // Profile completion CTA labels
    'completion.cta_photo':       'Add photo',
    'completion.cta_bio':         'Write bio',
    'completion.cta_prompt':      'Add prompt',
    'completion.cta_interests':   'Add interests',
    'completion.cta_quiz':        'Take quiz',
    'completion.cta_friends':     'Invite friend',
    'completion.cta_details':     'Add details',
    // Notifications
    'notif.rated_profile':        'rated your profile',
    'notif.liked_profile':        'liked your profile',
    // Profile interests section label
    'profile.interests_section':  'Interests',
    'profile.prompts_section':    'Prompts',
    // Profile dropdown missing values
    'profile.cat_none':           'None',
    'profile.cat_dog':            'Dog',
    'profile.cat_cat':            'Cat',
    'profile.cat_both':           'Both',
    'profile.gifts':              'Receiving gifts',
    // Profile tab score
    'profile.tab_score':          'My Score',
    // Profile share options
    'profile.share_whatsapp':     'Share on WhatsApp',
    'profile.share_link':         'Share link',
    'profile.copy_link':          'Copy link',
    // Upgrade features list
    'upgrade.feat_see_liked':     'See who liked you',
    'upgrade.feat_invites':       'Unlimited friend invites',
    'upgrade.feat_boost':         'Weekly visibility boost',
    'upgrade.feat_priority':      'Priority matching',
    'upgrade.feat_receipts':      'Read receipts',

    // Monetization plan features
    'plan.free_f1':   'Unlimited swipes',
    'plan.free_f2':   'Up to 5 matches/month',
    'plan.free_f3':   'Basic messaging (10 msg/conv)',
    'plan.free_f4':   'BeeHive read access',
    'plan.free_f5':   '1 profile prompt',
    'plan.free_f6':   '3 friend invitations',
    'plan.free_f7':   'Basic badges',
    'plan.honey_f1':  'Unlimited matches',
    'plan.honey_f2':  'See who liked you',
    'plan.honey_f3':  'Weekly visibility boost',
    'plan.honey_f4':  'Unlimited friend invitations',
    'plan.honey_f5':  '3 profile prompts',
    'plan.honey_f6':  'Advanced filters (MBTI, languages, values)',
    'plan.honey_f7':  'Incognito mode',
    'plan.honey_f8':  'Read receipts',
    'plan.honey_f9':  'Discounted event tickets (10% off)',
    'plan.honey_f10': '5 Super Likes/day',
    'plan.gold_f1':   'Everything in Bee Honey',
    'plan.gold_f2':   'View full profiles before matching',
    'plan.gold_f3':   'Unlimited Super Likes',
    'plan.gold_f4':   'Daily visibility boost',
    'plan.gold_f5':   'Priority match access (new members first)',
    'plan.gold_f6':   'Profile analytics dashboard',
    'plan.gold_f7':   'Priority messaging (appear first)',
    'plan.gold_f8':   'Gold badge on public profile',
    'plan.gold_f9':   '1 free VIP event/quarter',
    'plan.gold_f10':  'Unlimited event discounts (20% off)',
    'plan.free_name':   'Free',
    'plan.honey_tag':   'For serious daters',
    'plan.gold_tag':    'Maximum visibility',
    // Ala carte
    'alacarte.boost_1':    'Boost 24h',
    'alacarte.boost_1_d':  'Push your profile to the top of the feed for 24 hours',
    'alacarte.boost_5':    '5 Boosts pack',
    'alacarte.boost_5_d':  'Use anytime — never expire',
    'alacarte.boost_5_b':  'Save 33%',
    'alacarte.sl_10':      '10 Super Likes',
    'alacarte.sl_10_d':    'Stand out from the crowd',
    'alacarte.sl_sub':     'Unlimited Super Likes',
    'alacarte.sl_sub_d':   'Monthly add-on — cancel anytime',
    'alacarte.stats':      'Profile analytics',
    'alacarte.stats_d':    'Views, match rate, photo performance',
    'alacarte.verified':   'Advanced Verification',
    'alacarte.verified_d': 'Video or ID verification — permanent badge',

    // Notifications
    'notif.new_match':      'New match with {name}!',
    'notif.rated':          '{name} rated your profile',
    'notif.liked_anon':     'Someone liked your profile',
    'notif.boost_active':   'Daily Boost activated! 🚀',
    'notif.msg_music':      'Hey! I saw your profile and loved your taste in music 🎵',
    'notif.msg_common':     'We seem to have a lot in common! Would love to chat sometime.',
    'notif.msg_reviews':    'Your friends gave you such great reviews. Curious to know you!',
    'notif.msg_reach':      "Hope you don't mind me reaching out directly 🙂",

    // Badge names and descriptions
    'badge.verified_name':    'Verified',
    'badge.verified_desc':    'Your photo matches a real person.',
    'badge.mirror_name':      'Mirror',
    'badge.mirror_desc':      'You answered the same quiz as your friends.',
    'badge.honest_name':      'Honest',
    'badge.honest_desc':      'Your profile is thoughtfully completed.',
    'badge.active_name':      'Active',
    'badge.active_desc':      'You are using BeeLove this week.',
    'badge.convo_name':       'Conversational',
    'badge.convo_desc':       'You genuinely engage with your matches.',
    'badge.aligned_name':     'Aligned',
    'badge.aligned_desc':     'You see yourself the way your friends see you.',
    'badge.selfaware_name':   'Self-Aware',
    'badge.selfaware_desc':   "You have viewed your coherence map.",
    'badge.anchored_name':    'Anchored',
    'badge.anchored_desc':    'Your friends really know you.',
    'badge.connector_name':   'Connector',
    'badge.connector_desc':   'Your community is active around you.',
    'badge.growing_name':     'Growing',
    'badge.growing_desc':     'You reduced a self/friends gap this month.',
    // Pillar labels
    'pillar.authenticity':    'Authenticity',
    'pillar.engagement':      'Engagement',
    'pillar.lucidity':        'Lucidity',
    'pillar.social':          'Social',
    'pillar.evolution':       'Growth',
    // Interests / activities
    'interest.music':         'Music',
    'interest.coffee':        'Coffee',
    'interest.climbing':      'Climbing',
    'interest.reading':       'Reading',
    'interest.hiking':        'Hiking',
    'interest.cooking':       'Cooking',
    'interest.travel':        'Travel',
    'interest.yoga':          'Yoga',
    'interest.cinema':        'Cinema',
    'interest.art':           'Art',
    'interest.gaming':        'Gaming',
    'interest.running':       'Running',
    'interest.cycling':       'Cycling',
    'interest.dancing':       'Dancing',
    'interest.writing':       'Writing',
    'interest.meditation':    'Meditation',
    'interest.volunteering':  'Volunteering',
    'interest.wine':          'Wine',
    'interest.theater':       'Theater',
    'interest.photography2':  'Photography',
    // Completion quiz step
    'completion.quiz_cta':    'Complete the compatibility quiz',

    // HiveGroups
    'hive.group_interests_name':  'Shared interests',
    'hive.group_interests_desc':  'Based on what members list as interests',
    'hive.group_vibe_name':       'Community vibe',
    'hive.group_vibe_desc':       'Based on mood and how members show up',
    'hive.group_lifestyle_name':  'Lifestyle affinity',
    'hive.group_values_name':     'Shared values',
    'hive.group_compat_name':     'High compatibility',
    'hive.group_compat_desc':     'Based on compatibility quiz scores with current user',
    'hive.group_hood_name':       'Neighbourhood',
    'hive.group_hood_desc':       'Based on neighbourhood or area of the city',
    'hive.group_active_name':     'Active & Outdoors',
    'hive.group_active_desc':     'Hikers, climbers, runners and outdoor lovers',
    'hive.group_food_name':       'Food & Drink',
    'hive.group_food_desc':       'Food lovers, home chefs, wine enthusiasts and culinary adventurers',
    'hive.group_arts_name':       'Arts & Creativity',
    'hive.group_arts_desc':       'Photographers, musicians, writers, designers and visual thinkers',
    'hive.group_wellness_name':   'Wellness & Mindfulness',
    'hive.group_wellness_desc':   'Members drawn to balance, nature, health and intentional living',
    'hive.group_curious_name':    'Curious Minds',
    'hive.group_curious_desc':    'Readers, thinkers, builders, gamers and lifelong learners',
    'hive.group_night_name':      'Night Owls',
    'hive.group_plant_name':      'Plant-based Living',
    'hive.group_plant_desc':      'Members embracing plant-based, vegan or vegetarian lifestyles',
    'hive.group_open_name':       'Open & Exploring',
    'hive.group_french_name':     'French-speaking',
    'hive.group_french_desc':     'Francophones and French learners in the community',
    'hive.group_highcompat_name': 'High Compatibility',
    // Events
    'event.desc_premat':          'Pre-matched by compatibility algorithm',
    'event.perk_verified':        'Verified members only',
    'event.desc_cooking':         'Cook together, connect naturally',
    'event.perk_ingredients':     'Ingredients provided',
    'event.desc_vip':             'VIP experience for Gold members',
    'event.perk_openbar':         'Open bar all night',
    'event.perk_jazz':            'Live jazz trio',
    // Safety report reasons
    'report.reason_fake':         'Fake profile or photos',
    'report.reason_harassment':   'Harassment or bullying',
    'report.reason_content':      'Inappropriate content',
    'report.reason_spam':         'Spam or scam',
    'report.reason_underage':     'Appears to be underage',
    'report.reason_offensive':    'Offensive language or photos',
    'report.reason_other':        'Other',
    // BioGenerator questions
    'bio.q1':                     'Your perfect Saturday?',
    'bio.q2':                     'One thing you absolutely cannot compromise on?',
    'bio.q3':                     'Something you are quietly proud of?',
    'bio.q4':                     'What kind of connection are you hoping to find?',
    // Icebreakers
    'ice.q1':                     "What's the story behind their best photo?",
    'ice.q2':                     'What would their perfect Saturday look like?',
    'ice.q3':                     "Ask them what their friends would say is their most surprising quality",
    'ice.q4':                     "What's one thing their profile doesn't tell you about them?",
    'ice.q5':                     "What's the last thing that made them laugh out loud?",
    'ice.q6':                     "What's their go-to comfort meal after a long week?",
    // CMS page titles
    'cms.how':                    'How it works',
    'cms.privacy':                'Privacy Policy',
    'cms.terms':                  'Terms of Use',
    'cms.safety':                 'Safety',
    'cms.contact':                'Contact',
    'cms.pricing':                'Pricing',
    'cms.help':                   'Help Center',
    'cms.press':                  'Press & Media',
    'cms.careers':                'Careers',
    // BeeJourney stages
    'journey.s1_desc':            'You joined the hive. Your journey begins.',
    'journey.s1_reward':          'Profile visible to the community',
    'journey.s3_reward':          'Friend Score badge unlocked',
    'journey.s5_reward':          'Priority placement in Discover',
    // BeeloveLogo tagline
    'logo.tagline':               'THE SOCIAL DATING APP',

    // Lifestyle section labels (Discover/Profile cards)
    'life.drinking':      'Drinking',
    'life.smoking':       'Smoking',
    'life.cannabis':      'Cannabis',
    'life.exercise':      'Exercise',
    'life.diet':          'Diet',
    'life.pets':          'Pets',
    'life.love_lang':     'Love language',
    'life.sleep':         'Sleep',
    'life.religion':      'Religion',
    'life.politics':      'Politics',
    'life.education':     'Education',
    'life.goal':          'Looking for',
    // Section headers
    'section.prompts':    'Prompts',
    'section.interests':  'Interests',
    'section.lifestyle':  'Lifestyle',
    'section.values':     'Values',
    'section.about':      'About',
    'section.basics':     'Basics',
    'section.from_friends': 'From their friends',
    // Drink/smoke/exercise values
    'val.never':          'Never',
    'val.rarely':         'Rarely',
    'val.socially':       'Socially',
    'val.regularly':      'Regularly',
    'val.daily':          'Daily',
    'val.sometimes':      'Sometimes',
    'val.often':          'Often',
    // Education values
    'val.bachelors':      "Bachelor's",
    'val.masters':        "Master's",
    'val.phd':            'PhD',
    'val.trade':          'Trade',
    // Goal values
    'val.relationship':   'Relationship',
    'val.casual':         'Casual',
    'val.marriage':       'Marriage',
    'val.friendship':     'Friendship',
    // Pet values
    'val.cat':            'Cat 🐱',
    'val.dog':            'Dog 🐶',
    'val.both_pets':      'Both 🐾',
    'val.no_pets':        'No pets',
    'val.allergic':       'Allergic',
    // Love language short
    'val.words':          'Words of affirmation',
    'val.acts':           'Acts of service',
    'val.gifts':          'Gifts',
    'val.quality':        'Quality time',
    'val.touch':          'Physical touch',
    // Sleep
    'val.early_bird':     'Early bird',
    'val.night_owl':      'Night owl',
    'val.flexible':       'Flexible',
    // Verified badge label
    'val.verified':       'Verified ✓',

    // Matches compatibility section headers
    'matches.compat_section':     'Compatibility',
    'matches.how_relate':         'How they relate',
    'matches.who_they_are':       'Who they are',
    // Matches axis full labels
    'matches.axis_repair':        'Repair',
    'matches.axis_conflict3':     'Conflict',
    'matches.axis_account2':      'Accountability',
    'matches.axis_generosity2':   'Generosity',
    'matches.axis_presence2':     'Presence',
    'matches.axis_narrative':     'Narrative Identity',
    'matches.axis_boredom3':      'Boredom',
    'matches.axis_stakes2':       'Stakes',
    'matches.axis_social2':       'Social',
    // Discover card labels
    'discover.love_language_label': 'Love language',
    'discover.looking_for_label': 'Looking for',
    'discover.pass_btn2':         'Pass',
    // Profile tab labels (in DEFAULT_TABS key field)
    'profile.tab_about2':         'About',
    // Upgrade feature short labels
    'upgrade.feat_see_liked2':    'See who liked you',
    'upgrade.feat_invites2':      'Unlimited invites',
    'upgrade.feat_boost2':        'Weekly boost',
    'upgrade.discover_cta':       'Discover Profiles',
    // Completion banner short
    'completion.boost_note2':     'Profiles above 75% complete get 5× more matches.',
    // Networks
    'network.lorient':            'Lorient',
    'network.lorient_area':       'Lorient & Bretagne sud',

    // Scoring axis labels
    'axis.narrative':      'Narrative ownership',
    'axis.stability':      'Identity stability',
    'axis.boredom':        'Boredom threshold',
    'axis.social':         'Social energy',
    'axis.stakes':         'Stakes tolerance',
    'axis.repair':         'Repair speed',
    'axis.conflict':       'Conflict style',
    'axis.accountability': 'Accountability',
    'axis.generosity':     'Generosity',
    'axis.presence':       'Presence',
    // Scoring axis descs
    'axis.narrative_d':    'Do they own their part in past conflicts?',
    'axis.stability_d':    'Are they the same person across contexts?',
    'axis.boredom_d':      'Do they need constant stimulation?',
    'axis.social_d':       'Do crowds drain or energise them?',
    'axis.stakes_d':       'Are they paralysed by uncertainty?',
    'axis.repair_d':       'Do they let conflict fester or fix it fast?',
    'axis.conflict_d':     'Do they shut down or engage directly?',
    'axis.account_d':      'Do they own mistakes or deflect?',
    'axis.generosity_d':   'Do they give as much as they take?',
    'axis.presence_d':     'Are they fully present or half-distracted?',
    // Layer labels
    'axis.layer_a':        'Who they are',
    'axis.layer_b':        'How they relate',

    // BeeJourney moods
    'mood.growing':       'Growing',
    'mood.exploring':     'Exploring',
    'mood.ready':         'Ready',
    'mood.connecting':    'Connecting',
    'mood.low_key':       'Low key',
    // BeeJourney roles
    'role.elder':         'Hive Elder',
    'role.pillar':        'Hive Pillar',
    'role.member':        'Hive Member',
    'role.new_bee':       'New Bee',
    // Quiz categories
    'quiz.cat_identity':  'Identity',
    'quiz.cat_lifestyle': 'Lifestyle',
    'quiz.cat_relational':'Relational',
    // Event types
    'event.type_speed':   'Speed Dating',
    'event.type_apero':   'Apéro réseau',
    'event.type_theme':   'Soirée thème',
    'event.type_vip':     'VIP',
    'event.type_partner': 'Partner',
    'event.type_outdoor': 'Outdoor',
    // PremiumModal perks
    'perk.see_liked':     'See who liked you',
    'perk.see_liked_sub': 'No more guessing — see every profile that rated or liked you.',
    'perk.invites':       'Unlimited friend invites',
    'perk.invites_sub':   'Invite as many friends as you want to rate your profile.',
    'perk.boost':         'Weekly visibility boost',
    'perk.boost_sub':     'Your profile is pushed to the top of the Discover feed once a week.',
    'perk.priority':      'Priority matching',
    'perk.priority_sub':  'Get matched first with highly compatible profiles.',
    'perk.receipts':      'Read receipts',
    'perk.receipts_sub':  'Know when your messages have been read.',
    // Moderation actions (admin — EN only fine, but translate for consistency)
    'mod.warn':           'Send warning',
    'mod.suspend':        'Suspend 7 days',
    'mod.ban':            'Permanent ban',
    'mod.dismiss':        'Dismiss report',

    'common.email_label': 'Email',
    'common.sms_label': 'SMS',

    // Quiz questions — axis-named keys (1-to-1 with radar axes)
    // RELATIONAL axes (how I am with others)
    'quiz.repair':        'After hurting someone you care about — even unintentionally — you typically…',
    'quiz.repair_o1':     'Give it time — the tension usually fades on its own',
    'quiz.repair_o2':     'Wait for the right moment, then bring it up',
    'quiz.repair_o3':     'Reach out fairly quickly to clear the air',
    'quiz.repair_o4':     "Address it right away — I can't rest knowing I caused pain",
    'quiz.conflict':      "When there's a real disagreement with someone you care about, you…",
    'quiz.conflict_o1':   'Go quiet and hope it resolves on its own',
    'quiz.conflict_o2':   'Need time alone first, then can talk it through',
    'quiz.conflict_o3':   'Take a breath, then bring it up calmly',
    'quiz.conflict_o4':   'Address it directly as soon as possible',
    'quiz.account':       'When something goes wrong in a relationship because of you, your first move is to…',
    'quiz.account_o1':    'Look at what the situation or the other person caused',
    'quiz.account_o2':    'Try to understand both sides before taking a position',
    'quiz.account_o3':    'Reflect on what I could have done differently',
    'quiz.account_o4':    "Own my part straight away — I don't wait to be called out",
    'quiz.generosity':    'When someone close to you is going through a hard time, you…',
    'quiz.generosity_o1': "Struggle to know how to help — expressing care doesn't come naturally",
    'quiz.generosity_o2': 'Give them space unless they ask for support',
    'quiz.generosity_o3': 'Reach out, offer help, and check in regularly',
    'quiz.generosity_o4': "Drop what I'm doing and show up — being there is everything",
    'quiz.presence':      'During a conversation that really matters to you, you are…',
    'quiz.presence_o1':   'Easily distracted — staying fully focused is hard for me',
    'quiz.presence_o2':   'Often doing two things at once — I can still follow',
    'quiz.presence_o3':   'Mostly present, though my mind sometimes wanders',
    'quiz.presence_o4':   'Fully there — phone away, everything else can wait',
    // IDENTITY axes (how I am as a person)
    'quiz.narrative':     'When a past relationship ended badly, how do you usually tell that story?',
    'quiz.narrative_o1':  'I was unlucky — the other person was the problem',
    'quiz.narrative_o2':  "They had issues I couldn't fix — I did my best",
    'quiz.narrative_o3':  'It was complicated — we both had a part in it',
    'quiz.narrative_o4':  'I was mostly at fault — I know where I went wrong',
    'quiz.stability':     "How much does your personality shift depending on who you're with?",
    'quiz.stability_o1':  "I'm quite different depending on the group",
    'quiz.stability_o2':  'I shift a fair bit — I read the room and adjust',
    'quiz.stability_o3':  'I adapt my tone but stay fundamentally myself',
    'quiz.stability_o4':  'Pretty much the same person in any situation',
    'quiz.boredom':       'A completely empty Saturday with no plans. You feel…',
    'quiz.boredom_o1':    'Anxious — I need structure to feel okay',
    'quiz.boredom_o2':    'Restless — I need at least one thing to do',
    'quiz.boredom_o3':    "Fine — I'll probably text someone to hang out",
    'quiz.boredom_o4':    "Bliss — I'll fill it with whatever I feel like",
    'quiz.social':        'After a big social event with lots of people, you feel…',
    'quiz.social_o1':     'Exhausted — I need a full day of quiet to recover',
    'quiz.social_o2':     'Drained — I need a few hours alone to recharge',
    'quiz.social_o3':     'Happy but ready for a quieter end to the night',
    'quiz.social_o4':     'Energised — I could keep going all night',
    'quiz.stakes':        'A major life change lands on you with 48 hours notice. You…',
    'quiz.stakes_o1':     'Feel frozen — change without preparation is very hard for me',
    'quiz.stakes_o2':     'Feel anxious — I need time to process before acting',
    'quiz.stakes_o3':     'Feel stressed but manage it step by step',
    'quiz.stakes_o4':     'Feel excited — I thrive in change',

    // Legacy numbered quiz keys (kept for backwards compat)
    'quiz.q1':            'When a past relationship ended badly, how do you usually tell that story?',
    'quiz.q1_o1':         'I was mostly at fault — I know where I went wrong',
    'quiz.q1_o2':         'It was complicated — we both had a part in it',
    'quiz.q1_o3':         "They had issues I couldn't fix — I did my best",
    'quiz.q1_o4':         'I was unlucky — the other person was the problem',
    'quiz.q2':            'A completely empty Saturday with no plans. You feel…',
    'quiz.q2_o1':         'Bliss — I will fill it with whatever I feel like',
    'quiz.q2_o2':         'Fine, but I will probably text someone to hang out',
    'quiz.q2_o3':         'A little restless — I need at least one thing to do',
    'quiz.q2_o4':         'Anxious — I need structure to feel okay',
    'quiz.q3':            'After a big social event with lots of people, you feel…',
    'quiz.q3_o1':         'Energised — I could keep going all night',
    'quiz.q3_o2':         'Happy but ready for a quieter end to the night',
    'quiz.q3_o3':         'Drained — I need a few hours alone to recharge',
    'quiz.q3_o4':         'Exhausted — I need a full day of nothing to recover',
    'quiz.q4':            "How much does your personality shift depending on who you're with?",
    'quiz.q4_o1':         'I am pretty much the same in any situation',
    'quiz.q4_o2':         'I adapt my tone but stay fundamentally myself',
    'quiz.q4_o3':         'I shift quite a bit — I read the room and adjust',
    'quiz.q4_o4':         'I am a different person in different contexts',
    'quiz.q5':            'A major life change lands on you with 48 hours notice. You…',
    'quiz.q5_o1':         'Feel excited — I thrive in change',
    'quiz.q5_o2':         'Feel stressed but manage it step by step',
    'quiz.q5_o3':         'Feel anxious — I need time to process before acting',
    'quiz.q5_o4':         'Feel frozen — change without preparation is very hard for me',
    'quiz.q6':            'Kids — where do you stand?',
    'quiz.q6_o1':         "I want kids and it's a priority for me",
    'quiz.q6_o2':         "I'd like kids but it's not a dealbreaker",
    'quiz.q6_o3':         'Open to it if the right person comes along',
    'quiz.q6_o4':         "I don't want kids",
    'quiz.q7':            'Your natural daily rhythm?',
    'quiz.q7_o1':         'Early bird — I am at my best before noon',
    'quiz.q7_o2':         'Morning person but flexible',
    'quiz.q7_o3':         'Night owl — I come alive in the evening',
    'quiz.q7_o4':         'Unpredictable — it changes constantly',
    'quiz.q8':            'Where do you see yourself in 5 years?',
    'quiz.q8_o1':         "I have a clear plan and I'm working toward it",
    'quiz.q8_o2':         'I have a direction but I stay open to change',
    'quiz.q8_o3':         'I have some ideas but nothing concrete',
    'quiz.q8_o4':         'I prefer to see where life takes me',
    'quiz.q9':            "When there's a real disagreement with someone you care about, you…",
    'quiz.q9_o1':         'Address it directly as soon as possible',
    'quiz.q9_o2':         'Take a breath, then bring it up calmly',
    'quiz.q9_o3':         'Need time to think before saying anything',
    'quiz.q9_o4':         'Tend to go quiet and hope it resolves on its own',
    'quiz.q10':           'What does your ideal relationship look like day-to-day?',
    'quiz.q10_o1':        'Very intertwined — shared routines, shared decisions',
    'quiz.q10_o2':        'Close but with our own space and projects',
    'quiz.q10_o3':        'Two independent people who choose each other daily',
    'quiz.q10_o4':        'Still figuring that out',
    // FriendRating criteria
    'fr.repair_q':        'After a conflict or cold moment, how quickly do they reach out to fix things?',
    'fr.repair_lo':       'Lets it fester for days',
    'fr.repair_hi':       'Fixes it within hours',
    'fr.conflict_q':      'When there is a real disagreement, what do they typically do?',
    'fr.conflict_lo':     'Shuts down or avoids',
    'fr.conflict_hi':     'Engages directly but fairly',
    'fr.account_q':       'When something goes wrong because of them, what happens?',
    'fr.account_lo':      'Deflects or blames others',
    'fr.account_hi':      'Owns it immediately',
    'fr.generosity_q':    'In friendships and relationships, how balanced is the give and take?',
    'fr.generosity_lo':   'Takes more than they give',
    'fr.generosity_hi':   'Gives as much as they take',
    'fr.presence_q':      'When you are together, how mentally present are they?',
    'fr.presence_lo':     'Often distracted, half-there',
    'fr.presence_hi':     'Fully focused and engaged',
    'fr.narrative_q':     'When they talk about past conflicts or relationships, do they own their part?',
    'fr.narrative_lo':    'Always the hero or victim',
    'fr.narrative_hi':    'Acknowledges their role honestly',
    'fr.stability_q':     'Are they the same person regardless of who they are with?',
    'fr.stability_lo':    'Very different across contexts',
    'fr.stability_hi':    'Consistent in all situations',
    'fr.boredom_q':       'How comfortable are they with an empty schedule and silence?',
    'fr.boredom_lo':      'Needs constant stimulation',
    'fr.boredom_hi':      'Perfectly at ease doing nothing',
    'fr.social_q':        'After a long social event, what do they need?',
    'fr.social_lo':       'Full isolation to recover',
    'fr.social_hi':       'Still energised, wants more',
    'fr.stakes_q':        'When facing a major uncertain decision, how do they handle it?',
    'fr.stakes_lo':       'Paralysed, needs everything planned',
    'fr.stakes_hi':       'Calm and decisive under pressure',
    // BeeJourney stage descriptions
    'journey.s1_label':   'Bee Registered',
    'journey.s2_label':   'Bee Honest',
    'journey.s3_label':   'Bee Endorsed',
    'journey.s4_label':   'Bee Connected',
    'journey.s5_label':   'Bee Loved',
    // BeeWise read time
    'beewise.min_read':   '{n} min read',
    // Dynamic year
    'common.year':        '2025',
    // Auth BEE_WORDS (hero animation)
    'auth.bee_word1':     'Bee Loved',
    'auth.bee_word2':     'Bee Patient',
    'auth.bee_word3':     'Bee Authentic',
    'auth.bee_word4':     'Bee Seen',
    'auth.bee_word5':     'Bee Brave',
    'auth.bee_word6':     'Bee The One',
    // Events — "Montréal" as city label
    'events.city_label':  'Montréal',

    // Additional interests
    'interest.vinyl': 'Vinyl',
    'interest.games': 'Games',
    'interest.live_music': 'Live music',
    'interest.surfing': 'Surfing',
    'interest.indie_rock': 'Indie rock',
    'interest.alpinism': 'Alpinism',
    'interest.skiing': 'Skiing',
    'interest.sunrise': 'Sunrise',
    'interest.herbal_tea': 'Herbal tea',
    'interest.indie_music': 'Indie music',
    'interest.law': 'Law',
    'interest.african_cuisine': 'African cuisine',
    'interest.activism': 'Activism',
    'interest.organic_farming': 'Organic farming',
    'interest.dogs': 'Dogs',
    'interest.folk_music': 'Folk music',
    'interest.space': 'Space',
    'interest.astronomy': 'Astronomy',
    'interest.gym': 'Gym',
    'interest.architecture': 'Architecture',
    'interest.urban_photo': 'Urban photography',
    'interest.theatre': 'Theatre',
    'interest.crossfit': 'CrossFit',
    'interest.nutrition': 'Nutrition',
    'interest.true_crime': 'True crime',
    'interest.cats': 'Cats',
    'interest.novels': 'Novels',
    'interest.nomadic': 'Nomadic living',
    'interest.techno': 'Techno',
    'interest.tech': 'Tech',
    'interest.anime': 'Anime',
    'interest.spicy_food': 'Spicy food',
    'interest.makeup': 'Makeup',
    'interest.bollywood': 'Bollywood',
    'interest.indian_cuisine': 'Indian cuisine',
    'interest.electronic': 'Electronic music',
    'interest.production': 'Production',
    'interest.street_food': 'Street food',
    'interest.contemporary_art': 'Contemporary art',
    'interest.champagne': 'Champagne',
    'interest.construction': 'Construction',
    'interest.urban_planning': 'Urban planning',
    'interest.improv': 'Improv theatre',
    'interest.animals': 'Animals',
    'interest.tea': 'Tea',
    'interest.ghibli': 'Studio Ghibli',
    'interest.chess': 'Chess',
    'interest.philosophy': 'Philosophy',
    'interest.craft_beer': 'Craft beer',
    'interest.sailing': 'Sailing',
    'interest.ocean': 'Ocean',
    'interest.diving': 'Diving',
    'interest.sunsets': 'Sunsets',
    'interest.piano': 'Piano',
    'interest.asian_cuisine': 'Asian cuisine',
    'interest.children': 'Children',
    'interest.gardening': 'Gardening',
    'interest.swimming': 'Swimming',
    'interest.herbalism': 'Herbalism',
    'interest.ai': 'AI',
    'interest.abstract_games': 'Abstract games',
    'interest.ambient': 'Ambient music',
    'interest.literature': 'Literature',
    'interest.natural_wine': 'Natural wine',
    'interest.trail': 'Trail running',
    'interest.series': 'Series',
    'interest.conferences': 'Conferences',
    'interest.opera': 'Opera',
    'interest.intellectual': 'Intellectual dinners',
    'interest.triathlon': 'Triathlon',
    'interest.sushi': 'Sushi',
    'interest.comics': 'Comics',
    'interest.rpg': 'RPG',
    'interest.stargazing': 'Stargazing',
    'interest.motorcycles': 'Motorcycles',
    'interest.mechanics': 'Mechanics',
    'interest.rock': 'Rock',
    'interest.performance': 'Performance',
    'interest.fashion': 'Fashion',
    'interest.queer': 'Queer culture',
    'interest.farming': 'Farming',
    'interest.livestock': 'Livestock',
    'interest.countryside': 'Countryside',
    'interest.pastry': 'Pastry',
    'interest.japanese_tea': 'Japanese tea',
    'interest.japanese_culture': 'Japanese culture',
    'interest.origami': 'Origami',
    'interest.punk': 'Punk',
    'interest.printmaking': 'Print making',
    'interest.paris_night': 'Paris by night',
    'interest.concerts': 'Concerts',
    'interest.naturopathy': 'Naturopathy',
    'interest.gastronomy': 'Gastronomy',
    'interest.sun': 'Sun',
    'interest.veganism': 'Veganism',
    'interest.trees': 'Trees',
    'interest.camping': 'Camping',
    'interest.ecology': 'Ecology',
    'interest.data': 'Data',
    'interest.board_games': 'Board games',
    'interest.ramen': 'Ramen',
    'interest.circus': 'Circus',
    'interest.acrobatics': 'Acrobatics',
    'interest.performing_arts': 'Performing arts',
    'interest.navigation': 'Navigation',
    'interest.islands': 'Islands',
    'interest.meteorology': 'Meteorology',
    'interest.health2': 'Health',
    'interest.brewing': 'Home brewing',
    'interest.tattoo': 'Tattoo art',
    'interest.metal': 'Metal',
    'interest.reptiles': 'Reptiles',
    'interest.goth': 'Goth culture',
    'interest.puzzles': 'Puzzles',
    'interest.press': 'Press',
    'interest.cities': 'Cities',
    'interest.salsa': 'Salsa',
    'interest.history': 'History',
    'interest.archives': 'Archives',
    'interest.trains': 'Trains',
    'interest.classical': 'Classical music',
    'interest.mountains': 'Mountains',
    'interest.archery': 'Archery',
    'interest.poker': 'Poker',
    'interest.pizza': 'Pizza',
    'interest.sports': 'Sports',
    'interest.podcasts': 'Podcasts',
    'interest.bbq': 'BBQ',
    'interest.fishing': 'Fishing',
    'interest.science': 'Science',
    'interest.nature': 'Nature',
    'interest.baking': 'Baking',
    'interest.dance': 'Dance',

    // Ratings tab
    'ratings.quality_breakdown':  'Quality breakdown',
    'ratings.based_on':           'Based on {n} friend ratings · Individual votes are never shown',
    'ratings.unlock_compat':      'Unlock compatibility scores',
    'ratings.quiz_prompt':        'Take the 3-min quiz to see your % match with every profile.',
    'ratings.start_btn':          'Start ✦',
    'ratings.who_rated':          'Who rated you',
    'ratings.anonymous':          'Anonymous',
    'ratings.no_ratings':         'No ratings yet — invite friends from the Invite tab.',
    // Invite tab
    'invite.friends_who_rate':    'Friends who rate you',
    'invite.get_more':            'Get more ratings',
    'invite.rate_me_msg':         'Rate me on BeeLove!',
    'invite.referral_title':      'Invite friends & earn rewards',
    'invite.referral_desc':       'For every 2 friends who join, you get 1 month of Bee Honey free.',
    'invite.how_it_works':        'How friend ratings work',
    'invite.how_desc':            'Once 5 friends complete the quiz, your compatibility profile is revealed.',
    // Journey tab
    'journey.welcome':            'Welcome',
    'journey.progress':           'Your journey',
    'journey.stages_left':        '{n} remaining',
    'journey.next_badge':         'Next',
    'journey.no_role':            'No role yet',
    'journey.hive_score':         'Hive Score',
    'journey.season_theme':       'Current season theme',
    'journey.weekly_q_title':     "This Week's Question",
    'journey.weekly_visible':     'Visible on your profile for 7 days',
    'journey.mood_title':         'Your Mood this week',
    'journey.share_answer':       'Share your answer…',
    // Settings tab
    'settings.compat_quiz_title': 'Compatibility Quiz',
    'settings.quiz_completed':    '✦ Completed',
    'settings.update_answers':    'Update your answers',
    'settings.discovery':         'Discovery',
    'settings.max_distance':      'Max distance',
    'settings.age_range':         'Age range',
    'settings.network_label2':    'Network',
    'settings.pause_title':       'Pause Discovery',
    'settings.pause_desc':        "You won't appear in Discover while paused. Your matches and messages are preserved.",
    // Profile About tab
    'about.add_job':              'Add your job',
    'about.height_label':         'Height',
    'about.field_study':          'Field of study',
    'about.school':               'School / University',
    'about.job_title':            'Job title',
    'about.languages':            'Languages',
    'about.zodiac':               'Zodiac',
    'about.toujours_visible':     'Always visible',
    'about.friends_note':         'The more friends respond, the richer and more accurate your profile becomes.',
    // Common visibility
    'common.visible':             'Visible',
    'common.hidden':              'Hidden',
    'common.everyone':            'Everyone',
    'common.anonymous':           'Anonymous',
    'common.quality_breakdown':   'Quality breakdown',

    // BeeJourney stage extra
    'journey.s2_desc': 'At least 5 friends rated you. The hive knows you.',
    'journey.s3_desc': 'Your profile is complete — bio, prompts, and who you really are.',
    'journey.s4_desc': 'You matched and had real conversations.',
    'journey.s5_desc': 'You found something real. ✦',

    // BeeJourney roles
    'role.scout':           'Scout',
    'role.scout_desc':      'You explore and invite — always looking for the next great connection.',
    'role.builder':         'Builder',
    'role.builder_desc':    'You create the conditions for others to connect and thrive.',
    'role.connector':       'Connector',
    'role.connector_desc':  'You bring people together — relationships flourish around you.',
    'role.queenbee':        'Queen Bee',
    'role.queenbee_desc':   'You lead by example — your presence elevates everyone in the hive.',
    'role.guardian':        'Guardian',
    'role.elder2':          'Elder',

    // Testimonials
    'testimonial.1_quote':  "I matched with someone who had a 91% compatibility score. We argued about the same books, hated the same things, and got engaged six months later. BeeLove knew before we did.",
    'testimonial.1_role':   'Lyon · Member since 2024',
    'testimonial.2_quote':  "Two weeks on BeeLove and I met someone who actually showed up. The friend rating system is genius — it filters out the noise instantly.",
    'testimonial.2_role':   'Paris · Member since 2024',
    'testimonial.3_quote':  "I already know what his friends think of him before saying hello. There is something incredibly reassuring about that. It changed how I approach every conversation.",
    'testimonial.3_role':   'Nantes · Member since 2023',
    'testimonial.4_quote':  "After more friends answered the quiz, I started understanding what people genuinely appreciate about me. That changed how I date, not just on BeeLove.",
    'testimonial.4_role':   'Lille · Member since 2024',

    // Quiz remaining options (matching.js)
    'quiz.q3_o3b':        'A bit drained — I need some wind-down time',
    'quiz.q3_o4b':        'Exhausted — I need a full day alone to recover',
    'quiz.q4_o1b':        "Not at all — I'm the same person everywhere",
    'quiz.q4_o3b':        "I tend to mirror people — it's easier that way",
    'quiz.q4_o4b':        "I'm quite different depending on the group",
    'quiz.q5_o1b':        'Feel a rush — change is exciting',
    'quiz.q5_o2b':        "Adapt quickly — I'll figure it out",
    'quiz.q5_o4b':        'Struggle — I need time and a plan to feel okay',
    'quiz.q6_o1b':        'Definitely want children — it is a priority',
    'quiz.q6_o3b':        'Undecided — I will see how life unfolds',
    'quiz.q6_o4b':        'I do not want children — my choice is made',
    'quiz.q7_o1b':        'Early bird — up at 6, productive by 7',
    'quiz.q7_o3b':        'Night owl — I come alive after 10pm',
    'quiz.q7_o4b':        'Completely chaotic — I have no schedule',
    'quiz.q8_city':       'City life — energy, culture, everything close',
    'quiz.q8_suburbs':    'Suburbs — calm but connected',
    'quiz.q8_rural':      'Small town or countryside — space and simplicity',
    'quiz.q8_abroad':     'Abroad — I want to live somewhere new',
    'quiz.q9_o1b':        "Address it immediately — I can't rest until it's resolved",
    'quiz.q9_o3b':        'Need space first — then I can talk it through',
    'quiz.q10_o1b':       'Deeply intertwined — we share almost everything',
    'quiz.q10_o4b':       "Still figuring it out — I'll know when I feel it",
    // Icebreakers templates
    'ice.t_interest1':    'You both love {interest} — ask them about their favourite {interest} experience in Montréal?',
    'ice.t_interest2':    'Both into {interest}! What got them started?',
    'ice.t_interest3':    "Fellow {interest} fan — what would be their dream {interest} day?",
    'ice.t_prompt1':      "They answered '{prompt}' with '{answer}' — what's your take on that?",
    'ice.t_prompt2':      "Their prompt '{prompt}' caught my eye — ask them to tell you more.",
    'ice.t_lifestyle1':   'You both {lifestyle} — perfect first date material?',
    'ice.t_lifestyle2':   'Two {lifestyle} people — compare notes on your routines?',
    'ice.t_mbti1':        "You're {myMbti} and they're {theirMbti} — classic combo. Ask them how they recharge.",
    'ice.t_mbti2':        "Your {myMbti} / {theirMbti} dynamic could be interesting — what's their take on it?",
    'ice.t_zodiac':       'A {myZodiac} and a {theirZodiac} — what do they think about astrology and compatibility?',
    'ice.t_lang':         'You both speak {lang} — surprise them with a message in {lang}?',
    'ice.t_age':          'Ask them about the best thing they did in their {age}s.',
    'ice.t_local':        "Ask them: what's something Montréal locals know that visitors never discover?",
    'ice.t_dinner':       'If they could have dinner with anyone — living or not — who would it be?',
    // Engagement messages
    'engage.no_more_title':   "You're a catch — check back tomorrow",
    'engage.no_more_sub':     'We add new profiles every morning. Your perfect match might be signing up right now.',
    'engage.patient_title':   'Bee Patient — good things take time',
    'engage.patient_sub':     'The best relationships start slowly. Widen your distance filter to discover more.',
    'engage.seen_all_title':  "You've seen everyone nearby",
    'engage.seen_all_sub':    'Consider expanding your filters — sometimes the right person is just a bit further.',
    'engage.score_title':     'Your score is working for you',
    'engage.score_sub':       "Even while you're here, people are seeing your profile. Someone might be typing right now.",
    // BeeHive stories
    'hive.story1':        "Two Connectors met during Connection Season. They've exchanged 340+ messages.",
    'hive.story2':        'A Scout and a Builder discovered they lived 3 blocks apart and shared 8 interests.',
    'hive.story3':        "Two Queen Bees matched at 94% compatibility. Both had 'Presence' as their top trait.",
    'hive.story4':        'A Builder invited 7 friends. Three of them are now active members.',
    'hive.story5':        "They both answered this week's question with almost the exact same words.",
    'hive.story6':        "Both in 'Connecting' mood, both Builders — they met for coffee as friends first.",
    // BeeHive weekly questions
    'hive.wq1':           "What's the bravest thing you've ever done for a connection?",
    'hive.wq2':           'Describe a moment when a stranger changed your perspective.',
    'hive.wq3':           "What's one thing most people don't know about you that you wish they did?",
    'hive.wq4':           'What does a perfect morning look like — in full detail?',
    'hive.wq5':           "What's the most spontaneous thing you've done in the last year?",
    'hive.wq6':           "What's something you changed your mind about completely?",
    'hive.wq7':           'What kind of energy do you bring into a room without trying?',
    'hive.wq8':           "What would your closest friend say is your best quality?",
    // Discover
    'discover.compat_high':   '✦ You both score high on',
    'discover.super_like':    'Super like',
    'discover.write_direct':  'Write directly',
    'discover.view_profile':  'View profile',
    'discover.report_btn':    'Report',
    'discover.report_title':  'Report this profile',
    'discover.report_anon':   'Your report is anonymous. We review every report within 24h.',
    'discover.report_recv':   'Report received',
    'discover.verified_chip': '✓ Verified',
    'discover.star_btn':      'STAR ★',
    'discover.prev_btn':      '← Previous',
    // Matches
    'matches.expired':        'Expired',
    'matches.high_on':        '✦ You both score high on',
    // Messages
    'messages.icebreakers':   '✦ Suggested icebreakers',
    'messages.ice_title':     'Icebreaker suggestions',
    // FriendRating
    'fr.greatest_quality':    'Their greatest quality according to you:',
    'fr.youll_rate':          "You'll rate them on",
    'fr.how_relate':          'How they relate',
    'fr.drives_score':        'Drives profile score',
    'fr.who_are':             'Who they are',
    'fr.drives_compat':       'Drives compatibility',
    'fr.your_name':           'Your name (optional — stays anonymous from {name})',
    'fr.be_honest':           'Bee Honest',
    'fr.tap_number':          'Tap a number. Be honest — it matters more than you think.',
    'fr.overall_rating':      'Your overall rating',
    'fr.optional':            '(optional)',
    'fr.example_review':      'The most genuine person I know — always shows up.',
    // BeeHive page
    'beehive.unlocked':       '✓ unlocked',
    'beehive.next':           '↑ next',
    'beehive.weekly_visible': 'Visible on your profile for 7 days',
    'beehive.stories_title':  'Stories from the hive',
    'beehive.your_stages':    'Your stages',
    'beehive.bee_mood':       'Bee Mood',
    'beehive.mood_visible':   'Visible on your card, your matches and in messages.',
    'beehive.bee_role':       'Bee Role',
    'beehive.role_desc':      'Your role reveals itself as you engage.',
    'beehive.role_list':      'Scout · Builder · Connector · Queen Bee',
    'beehive.hive_score':     'Hive Score',
    'beehive.score_desc':     'Earned through genuine engagement. Never decreases.',
    'beehive.bee_wise':       'Bee Wise',
    'beehive.wise_sub':       'Insights on love, communication & connection',
    'beehive.current_season': 'Current Season',
    'beehive.season_boost':   'Matching profiles get boosted visibility',
    'beehive.hive_week':      'Hive This Week',
    // Profile remaining
    'profile.main_photo':     'Main',
    'profile.bio_prompts':    'Answer a few prompts for a better bio:',
    'profile.friend_score':   'Friend Score',
    'profile.your_profile':   'Your Profile',
    'profile.answer_ph':      'Your answer…',
    'profile.answer_all':     'Answer all to save',
    'profile.blocked_users':  'Blocked users',
    'profile.invite_friends': 'Invite friends to BeeLove 🐝',
    'profile.prive':          'privé',
    'profile.upgrade_honey':  'Bee Honey',
    'profile.upgrade_perks':  'See who liked you · Unlimited invites · Weekly boost',
    'profile.upgrade_cta':    'Upgrade — 7 days free',
    // Upgrade
    'upgrade.free_label':     'Free',
    'upgrade.cad_mo':         'CAD/mo',
    'upgrade.dating_game':    'dating game',
    'upgrade.one_time':       'One-time purchases and monthly add-ons. Stack with any plan.',
    // Events
    'events.export':          'Export:',
    // Navbar
    'nav.more_cities':        'More cities coming. Networks let you connect with people nearby.',
    'nav.display_settings':   'Display settings',
    'nav.bee_the_one':        'Bee The One ✦',
    // ReportBlock
    'report.block_note':      "They won't be able to see you or contact you",
    'report.whats_going':     "What's going on? Your report is anonymous and helps keep BeeLove safe.",
    'report.blocked_note':    "They won't be able to see your profile, send you messages, or appear in Discover.",
    'report.title':           'Report or block',
    // RadarChart
    'radar.how_relate':       'How they relate',
    'radar.who_are':          'Who they are',
    // Icons
    'icons.score_conf':       'Score confidence:',
    // Auth
    'auth.bee_loved':         'Bee Loved',
    // StaticPage
    'static.bee_loved':       'Bee Loved',
    // Landing
    'landing.bee_loved_h':    'Bee Loved',
    'landing.bee_royal':      'Bee Royal',
    'landing.here':           'Here',
    'landing.honey_plan':     'Honey',
    'landing.royal_plan':     'Royal',
    'landing.bee_the_one':    'Bee The One ✦',
    // AppContext conversation messages
    'conv.listener':          'Great listener!',
    'conv.reliable':          'So reliable',

    // Additional interests batch 2
    'interest.literary_cafes': 'Literary cafés',
    'interest.geology': 'Geology',
    'interest.herbal_infusions': 'Herbal infusions',
    'interest.plants': 'Plants',
    'interest.forest': 'Forest',

    // BeeHive page titles
    'beehive.title':          'The BeeHive',
    'beehive.all_groups':     'All groups',

    // Journey/Profile/Settings missing strings
    'journey.register_btn': 'Register',
    'journey.discover_cta': 'Discover Profiles',
    'journey.weekly_q_current': "What's something you changed your mind about completely?",
    'about.public_info_note': 'This information appears on your public profile. The more you share, the better your matches.',
    'about.comp_science': 'Computer Science',
    'about.relationship_label': 'Relationship',
    'invite.compat_active': 'Your compatibility profile is active. More friends = richer insights.',
    'invite.anon_note': 'Their answers are anonymously aggregated — individual responses are never shown',
    'invite.reveal_note': 'Once 5 friends complete the quiz, your compatibility profile is revealed',
    'invite.more_friends_note': 'The more friends respond, the richer and more accurate your profile becomes',
    'journey.s3_desc_full': 'Your profile is complete — bio, prompts, and who you really are.',
    'journey.s4_desc_full': 'You matched and had a real conversation — 10+ messages.',
    'journey.s5_friend_desc': '3 friends joined BeeLove through your invite link.',
    'journey.s6_desc': 'The hive loves you back — top score, super likes, or exceptional match.',
    'role.hive_member': 'Hive Member',
    'settings.more_networks': 'More networks coming soon — Toronto, Vancouver, London, New York and more.',
    'settings.pause_desc2': "Your profile won't appear in Discover while paused. Your matches and messages are preserved.",
    'settings.active_network': 'Your active network',
    'journey.s6_label': 'Bee Loved',
    'edu.computer_science': 'Computer Science',
    'edu.bachelors2': "Bachelor's",

    // BeeJourney remaining stage rewards/descs
    'journey.s4_reward':      'Compatibility radar unlocked for all matches',
    'journey.s5_hive_label':  'Bee Hive',
    'journey.s5_hive_desc':   '3 friends joined BeeLove through your invite link.',
    'journey.s5_hive_reward': 'Builder role unlocked + permanent Hive badge',
    'journey.s6_reward':      'Queen/King Bee status + golden profile frame',
    // Profile About
    'about.public_info':      'This information appears on your public profile. The more you share, the better your matches.',
    'about.languages_val':    'French, English',
    // Settings network
    'settings.active_network2': 'Your active network',
    'settings.pause_note':    "Your profile won't appear in Discover while paused. Your matches and messages are preserved.",
    // Invite remaining
    'invite.anon_agg':        'Their answers are anonymously aggregated — individual responses are never shown',
    'invite.reveal_5':        'Once 5 friends complete the quiz, your compatibility profile is revealed',
    // Weekly question current
    'journey.this_weeks_q':   "This Week's Question",
    // Register button (auth)
    'auth.register':          'Register',
    // Discover CTA in journey
    'profile.discover_cta':   'Discover Profiles',

    // Match overlay popup
    'match.its_a_match':      "✦ It's a Match!",
    'match.liked_each_other': 'You and {name} liked each other.',
    'match.send_message':     'Send a message',
    'match.keep_exploring':   'Keep exploring',
    'match.compatibility':    'Compatibility',
    // Quiz category labels (inline in quiz)
    'quiz.cat_label_identity':  'Identity',
    'quiz.cat_label_lifestyle': 'Lifestyle',
    'quiz.cat_label_relational':'Relational',

    'discover.quiz_left_1':   'Only ',
    'discover.quiz_left_2':   ' question{plural} left — takes less than 2 minutes.',
    'discover.report_thanks': "Thank you for keeping BeeLove safe. We'll review this profile within 24 hours.",
    'match.view_profile':     'View Profile',

    // Quiz interstitial popup
    'quiz.modal_title':       'BeeLove — Compatibility',
    'quiz.match_prompt':      "You just got a match! Tell us a bit about yourself to unlock compatibility scores.",
    'quiz.resume_prompt':     '✦ Want to improve your compatibility score? Pick up where you left off.',
    'quiz.skip_now2':         'Skip for now',

    // Events — Past/Upcoming tabs + QR
    'events.tab_upcoming':    'Upcoming',
    'events.tab_past':        'Past',
    'events.qr_title':        'Your ticket',
    'events.qr_registered':   'Registered',
    'events.qr_show':         'Show this QR code at the entrance',
    'events.qr_validated':    '✓ Attendance confirmed',
    'events.attendees_title': 'Who was there',
    'events.attendees_sub':   'People who showed up — only visible to attendees',
    'events.no_past':         "You haven't attended any events yet",
    'events.attended_badge':  '✓ Attended',
    'events.capacity_label':  '{n} attendees confirmed',
    'events.you_attended':    'You were there',
    'events.scan_note':       'Scan at the door to confirm attendance',

    // CML (Community Manager Local) dashboard
    'cml.title':              'Community Manager',
    'cml.subtitle':           'Your local network dashboard',
    'cml.network_label':      'Network',
    'cml.stage_ambassador':   'Ambassador',
    'cml.stage_coordinator':  'Coordinator',
    'cml.stage_director':     'Local Director',
    'cml.revenue_month':      'Revenue this month',
    'cml.revenue_total':      'Total earned',
    'cml.revenue_pending':    'Pending',
    'cml.members_active':     'Active members',
    'cml.members_new':        'New this month',
    'cml.events_hosted':      'Events hosted',
    'cml.next_payout':        'Next payout',
    'cml.my_events':          'My events',
    'cml.new_event':          'New event',
    'cml.sponsors_title':     'Sponsors',
    'cml.add_sponsor':        'Add sponsor',
    'cml.notif_title':        'Send notification',
    'cml.notif_compose':      'Compose message',
    'cml.notif_send':         'Send to {n} members',
    'cml.notif_sent':         '✓ Sent!',
    'cml.stats_title':        'Network stats',
    'cml.stats_members':      'Members',
    'cml.stats_retention':    'Retention',
    'cml.stats_avg_score':    'Avg. score',
    'cml.stats_matches':      'Matches made',
    'cml.stats_events':       'Events total',
    'cml.sponsor_prospect':   'Prospect',
    'cml.sponsor_contacted':  'Contacted',
    'cml.sponsor_signed':     'Signed',
    'cml.sponsor_active':     'Active',
    'cml.budget_request':     'Request budget',
    'cml.budget_sent':        'Budget request sent',
    'cml.rev_events':         'Events',
    'cml.rev_sponsors':       'Sponsoring',
    'cml.rev_subscriptions':  'Subscriptions',
    'cml.rev_alacarte':       'À la carte',
    'cml.breakdown':          'Revenue breakdown',
    'cml.qr_scanner':         'QR Scanner',
    'cml.qr_scan_prompt':     "Scan a member\'s ticket QR code",
    'cml.qr_validated':       '✓ Presence confirmed',
    'cml.qr_invalid':         '✗ Invalid QR code',
    'cml.members_present':    '{n} members confirmed present',

    // Payment flow
    'payment.title':          'Secure checkout',
    'payment.card_info':      'Card information',
    'payment.name_on_card':   'Name on card',
    'payment.email_label':    'Email for confirmation',
    'payment.pay_now':        'Pay {amount}',
    'payment.processing':     'Processing...',
    'payment.success':        '✓ Payment confirmed',
    'payment.failed':         'Payment failed — try again',
    'payment.total':          'Total',
    'payment.secure':         'Secured by Stripe · 256-bit SSL',
    'payment.refund_policy':  'Full refund up to 48h before the event',
    'payment.subtotal':       'Subtotal',
    'payment.discount':       'Member discount',
    'payment.email_conf':     'Confirmation sent to',
    'payment.ticket_ready':   'Your ticket is ready',
    'payment.add_calendar':   'Add to calendar',
    'payment.view_ticket':    'View ticket',
    'payment.cancel_title':   'Cancel registration',
    'payment.cancel_confirm': 'Cancel my spot',
    'payment.cancel_policy':  'Cancellations up to 48h before the event are fully refunded.',
    'payment.cancel_too_late':'Less than 48h remaining — no refund available.',
    'payment.refund_confirm': 'Refund of {amount} will be processed in 5–7 business days.',
    'payment.refunded':       '✓ Refund initiated',
    'payment.or_free':        'Free for Gold members',
    'payment.spots_left':     '{n} spot{s} left',
    'payment.waitlist_join':  'Join waitlist',
    'payment.waitlisted':     "✓ On waitlist — we'll notify you if a spot opens",

    // Scout (formerly Scout)
    'circles.title':            'Scout',
    'circles.subtitle':         'A curated group, a shared moment',
    'circles.desc':             'The algo composes a group of 4–8 compatible people around shared interests. You meet first — scores are revealed after.',
    'circles.join_cta':         'Join the waiting list',
    'circles.joined':           'You are on the list',
    'circles.prefs_title':      'Your preferences',
    'circles.type_label':       'Activity type',
    'circles.avail_label':      'Availability',
    'circles.avail_weekday':    'Weekday evenings',
    'circles.avail_weekend':    'Weekend',
    'circles.avail_both':       'Both',
    'circles.how_title':        'How it works',
    'circles.how_1':            'You join the waiting list with your preferences',
    'circles.how_2':            'The algo finds a compatible group (4–8 people)',
    'circles.how_3':            'You receive a proposal — date, time, venue',
    'circles.how_4':            'Confirm within 48h to reserve your spot',
    'circles.how_5':            'Meet the group — no scores yet',
    'circles.how_6':            'Vote anonymously after — matches revealed',
    'circles.proposal_title':   'Scout proposal',
    'circles.proposal_sub':     'A group has been found for you',
    'circles.confirm_cta':      'Confirm my spot',
    'circles.decline_cta':      "Can't make it",
    'circles.confirmed':        '✓ Spot confirmed',
    'circles.declined':         'Declined',
    'circles.deadline':         'Confirm before',
    'circles.min_warning':      'Minimum 4 confirmations needed',
    'circles.cancelled':        'Scout cancelled — not enough confirmations',
    'circles.waitlist_pos':     'Position {n} on the list',
    'circles.interests_match':  '{n} shared interests',
    'circles.post_title':       'Your Scout took place',
    'circles.post_vote':        'Who would you like to see again?',
    'circles.post_match':       "It's a mutual match",
    'circles.post_no_match':    'No mutual match this time',
    'circles.reveal_scores':    'See compatibility scores',
    'circles.score_with':       'Your score with {name}',
    'circles.free_honey_gold':  'Free for Honey & Gold members',
    'circles.price_free':       'Free',
    'circles.status_waiting':   'Searching for a group...',
    'circles.status_proposed':  'Proposal waiting for your confirmation',
    'circles.status_confirmed': 'Circle confirmed',
    'circles.status_past':      'Past Circle',
    'circles.type_dinner':      'Dinner',
    'circles.type_apero':       'Apéro',
    'circles.type_coffee':      'Coffee / Brunch',
    'circles.type_outdoor':     'Outdoor / Sport',
    'circles.type_culture':     'Culture',
    'circles.type_board_games': 'Board Games',
    'circles.type_sport':       'Sport',
    'circles.type_workshop':    'Workshop',
    // Circle waitlist — days
    'circles.day_mon': 'Mon', 'circles.day_tue': 'Tue', 'circles.day_wed': 'Wed',
    'circles.day_thu': 'Thu', 'circles.day_fri': 'Fri', 'circles.day_sat': 'Sat', 'circles.day_sun': 'Sun',
    // Circle waitlist — time slots
    'circles.slot_lunch':      'Lunch',
    'circles.slot_after_work': 'After-work',
    'circles.slot_dinner':     'Dinner',
    'circles.slot_brunch':     'Brunch',
    // Circle waitlist — language pref
    'circles.lang_fr': 'Français', 'circles.lang_en': 'English', 'circles.lang_both': 'Both',
    // Circle waitlist — group size
    'circles.size_small': 'Small (4–5)', 'circles.size_large': 'Large (6–8)', 'circles.size_any': 'No preference',
    // Circle waitlist — section headers
    'circles.days_label':      'Available days',
    'circles.time_label':      'Time of day',
    'circles.lang_label':      'Language',
    'circles.size_label':      'Group size',
    'circles.waitlist_info':   'The algorithm will find 4–8 compatible people. You\'ll receive a proposal with date and venue one week ahead, with 48h to confirm.',
    // Network features — CML section
    'feature.city_features':      'City Features',
    'feature.city_features_info': 'Activate or deactivate features for your city. Changes take effect immediately for all members in your network.',
    'circles.members_confirmed':'{n} of {total} confirmed',
    'circles.venue_tbd':        'Venue — confirmed to participants',
    'circles.expires_in':       'Expires in {h}h',
    // Network / app features
    'feature.events':         'Events',
    'feature.events_desc':    'Public events open to all members (concerts, meetups, guided tours…)',
    'feature.circles':        'Scout',
    'feature.circles_desc':   'Private & group events — algorithmically matched or host-invited',
    'feature.beeconnect':     'BeeConnect',
    'feature.beeconnect_desc':'Direct-connection discovery between compatible members',
    'feature.beehive':        'BeeHive',
    'feature.beehive_desc':   'Community space: weekly questions, hive stories, seasons',
    // Hive seasons
    'hive.season_depth':      'Depth Season',
    'hive.season_adventure':  'Adventure Season',
    'hive.season_connection': 'Connection Season',
    'hive.season_reflection': 'Reflection Season',
    // Hive score tiers
    'hive.tier_elder':  'Hive Elder',
    'hive.tier_pillar': 'Hive Pillar',
    'hive.tier_member': 'Hive Member',
    'hive.tier_newbee': 'New Bee',

    // Identity verification
    'verify.email_title':      'Verify your email',
    'verify.email_sub':        'We sent a 6-digit code to {email}',
    'verify.sms_title':        'Verify your phone',
    'verify.sms_sub':          'We sent a code to {phone}',
    'verify.code_label':       'Verification code',
    'verify.code_placeholder': '6-digit code',
    'verify.confirm':          'Verify',
    'verify.resend':           'Resend code',
    'verify.resend_in':        'Resend in {s}s',
    'verify.success_email':    '✓ Email verified',
    'verify.success_phone':    '✓ Phone verified',
    'verify.error':            'Invalid code — try again',
    'verify.expired':          'Code expired — request a new one',
    'verify.badge_email':      'Email verified',
    'verify.badge_phone':      'Phone verified',
    'verify.phone_label':      'Phone number',
    'verify.phone_placeholder':'+1 (514) 000-0000',
    'verify.send_code':        'Send code',
    'verify.skip':             'Skip for now',
    'verify.why':              'Verified profiles get 3× more visibility',
    'verify.both_done':        '✓ Identity verified',

    // Referral — structured
    'referral.title':          'Invite & earn',
    'referral.subtitle':       'Each friend who joins earns you rewards',
    'referral.your_code':      'Your referral code',
    'referral.your_link':      'Your invite link',
    'referral.copy_link':      'Copy link',
    'referral.copied':         '✓ Copied!',
    'referral.share_whatsapp': 'Share on WhatsApp',
    'referral.share_sms':      'Share by SMS',
    'referral.share_email':    'Share by email',
    'referral.stats_invited':  'Invited',
    'referral.stats_joined':   'Joined',
    'referral.stats_earned':   'Months earned',
    'referral.reward_referrer':'1 month Honey per 2 friends who join',
    'referral.reward_invitee': 'Your friend gets 7 days Honey free',
    'referral.double_sided':   'Both of you benefit',
    'referral.milestone_1':    'First friend joined!',
    'referral.milestone_2':    '1 month Honey unlocked',
    'referral.milestone_5':    'Super recruiter — 2.5 months earned',
    'referral.progress_to':    '{n} more to next reward',
    'referral.history':        'Referral history',
    'referral.pending':        'Pending',
    'referral.rewarded':       'Rewarded',
    'referral.message_tmpl':   'I use BeeLove, a dating app based on real compatibility. Join with my code {code} and get 7 days free: {link}',

    // Viral share cards
    'viral.share_score':       'Share my score',
    'viral.share_quiz':        'Share my quiz result',
    'viral.share_circle':      'Share my Circle',
    'viral.card_score_title':  'My BeeLove score',
    'viral.card_quiz_title':   'My compatibility profile',
    'viral.card_event_title':  'I was at this Circle',
    'viral.copy_image':        'Copy image',
    'viral.download':          'Download',
    'viral.or_share':          'Or share directly',
    'viral.tagline':           'Find compatible people on BeeLove.me',

    // BeeConnect — friendly mode
    'beeconnect.title':           'BeeConnect',
    'beeconnect.subtitle':        'Genuine connections, shared interests',
    'beeconnect.mode_switch':     'Switch mode',
    'beeconnect.mode_romantic':   'BeeLove — romantic',
    'beeconnect.mode_friendly':   'BeeConnect — friendly',
    'beeconnect.mode_label':      'Friendly connections',
    'mode.romantic':              'Romantic',
    'mode.friendly':              'Friendly',
    'beeconnect.discover_title':  'Connect',
    'beeconnect.interests_first': 'Shared interests',
    'beeconnect.connect_cta':     'Connect',
    'beeconnect.connected':       '✓ Request sent',
    'beeconnect.connections':     'Connections',
    'beeconnect.connection':      'Connection',
    'beeconnect.filter_new':      'New',
    'beeconnect.filter_near':     'Nearby',
    'beeconnect.filter_shared':   'Shared interests',
    'beeconnect.filter_field':    'Same field',
    'beeconnect.no_score':        '{n} shared interests',
    'beeconnect.icebreaker_1':    'What are you working on lately?',
    'beeconnect.icebreaker_2':    'What book changed your perspective recently?',
    'beeconnect.icebreaker_3':    'What activity would you recommend to anyone?',
    'beeconnect.icebreaker_4':    'What project are you most excited about?',
    'beeconnect.icebreaker_5':    'What did you do last weekend you would repeat?',
    'beeconnect.filter_label':    'Filter by interest',
    'beeconnect.empty_title':     'No connections yet',
    'beeconnect.empty_sub':       'Be the first to reach out',
    'beeconnect.switch_prompt':   'Looking for more? Switch to romantic mode',
    'beeconnect.profile_badge':   'Open to friendships',
    'beeconnect.mutual_friends':  '{n} mutual connections',

    // Age verification
    'auth.age_gate_title':    'Age verification',
    'auth.age_gate_sub':      'BeeLove is for adults 18 and older.',
    'auth.age_dob_label':     'Date of birth',
    'auth.age_too_young':     'You must be 18 or older to join BeeLove.',
    'auth.age_too_young_sub': 'We take this requirement seriously to protect our community.',
    'auth.age_valid':         "You\'re good to go",
    'auth.age_why':           'Why do we verify age?',
    'auth.age_why_text':      'BeeLove is an adult platform. We verify age to keep our community safe.',
    'auth.age_legal':         'By continuing you confirm you are 18 or older.',
    'auth.age_day':           'Day',
    'auth.age_month':         'Month',
    'auth.age_year':          'Year',

    // Navigation labels
    'nav.discover':    'Discover',
    'nav.matches':     'Matches',
    'nav.messages':    'Messages',
    'nav.beehive':     'BeeHive',
    'nav.profile':     'Profile',
    'nav.events':      'Events',
    'nav.circles':     'Scout',
    'nav.scout':       'Scout',
    'nav.beeconnect':  'Connect',
    'nav.network':     'Network',
    'nav.notifications':'Notifications',

    // BeeConnect hardcoded strings
    'beeconnect.interests_label': 'Interests',
    'beeconnect.suggested_opener': 'Suggested opener',

    // Circles hardcoded strings
    'circles.your_group':      'Your group',
    'circles.love_circles':    'Love Circles',
    'circles.pending_proposal':'Proposal pending',


    // ── AGENDA ──────────────────────────────────────────────────────────────
    'nav.agenda':              'Agenda',
    'agenda.tab_upcoming':     'Upcoming',
    'agenda.tab_mine':         'My schedule',
    'agenda.tab_past':         'Past',
    'agenda.coming_soon':      'Events coming soon',
    'agenda.none_upcoming':    'Nothing coming up',
    'agenda.none_upcoming_sub':'BeeLove events and Circles for your network will appear here.',
    'agenda.none_mine':        'Nothing in your schedule yet',
    'agenda.none_mine_sub':    'Register for an event or join a Circle waiting list.',
    'agenda.none_past':        'No past events',

    // ── MESSAGES (new keys) ──────────────────────────────────────────────
    'messages.all':            'All',
    'messages.romantic':       'Matches',
    'messages.friendly':       'Connections',
    'messages.no_connections': 'No connections yet',
    'messages.no_connections_sub': 'Connect with people in BeeConnect to start a conversation.',
    'messages.no_matches_sub': 'Like someone back in Discover to start a conversation.',
    'messages.icebreakers':    'Suggested openers',
    'messages.placeholder':    'Write a message…',

    // ── SUBSCRIPTIONS ────────────────────────────────────────────────────────
    'subs.launch_free':       'Free during launch',
    'subs.launch_free_sub':   'All features are free while we grow. Subscriptions coming later.',
    'upgrade.launch_free':    "You're a Founding Member — everything is free, for life.",
    'upgrade.launch_free_sub':"You joined during the founding phase. When paid plans launch later this year, you keep full access — no subscription, no trial, no expiry.",

    // ── INVITE SYSTEM ────────────────────────────────────────────────────────
    'invite.step_title':            'Enter your invitation code',
    'invite.step_sub':              'BeeLove is currently invite-only. Enter the code you received.',
    'invite.invalid_code':          'Invalid or already used code.',
    'invite.expired_code':          'This code has expired.',
    'invite.founding_welcome':      '✦ Founding member code — welcome to the first wave.',
    'invite.valid_welcome':         '✓ Valid code — welcome to BeeLove.',
    'invite.waitlist_title':        'Request your invitation',
    'invite.waitlist_sub':          "BeeLove is growing by invitation. Leave your email and we'll send you a code.",
    'invite.waitlist_confirmed_title': "You're on the list",
    'invite.waitlist_confirmed_sub': "We'll send you an invitation code when it's your turn.",
    'invite.already_have':          'Already have a code?',
    'invite.remaining':             '{n} invitation{s} remaining',
    'invite.share_title':           'Invite friends to BeeLove',
    'invite.share_sub':             'Each friend who joins makes your profile more credible.',
    'invite.copy_link':             'Copy invite link',
    'invite.founding_badge':        'Founding Member',

    // ── CML TAX REPORTS ───────────────────────────────────────────────────────
    'cml.tax_report':       'Tax report',
    'cml.tax_year':         'Tax year',
    'cml.tax_generate':     'Generate report',
    'cml.tax_print':        'Print / Save PDF',
    'cml.tax_legal_name':   'Legal name',
    'cml.tax_number':       'Tax ID / SIN / SIRET',
    'cml.tax_address':      'Business address',
    'cml.tax_note':         'This document is for tax declaration purposes. Consult your tax advisor.',
    'gamification.streak':  'Day streak',
    'gamification.journey': 'Your journey',
    'gamification.next':    'Next milestone',
    'gamification.milestone_first_match': 'First match!',
    'gamification.milestone_first_like':  'First like sent',

    // ── ADMIN CONTROL CENTER ─────────────────────────────────────────────────
    'admin.control_center':     'Control center',
    'admin.pages_sections':     'Pages & sections',
    'admin.system_features':    'System features',
    'admin.plan_matrix':        'Plan × feature',
    'admin.founders_lab':       'Founders Lab',
    'admin.deploy_to_all':      'Deploy to all',
    'admin.founders_only':      'Founders only',
    'admin.wave':               'Wave',

    // ── FRIEND RATING (public questionnaire) ─────────────────────────────
    'rate.social_proof':        '{n} {word} already answered for {name}',
    'rate.social_word_one':     'person has',
    'rate.social_word_many':    'people have',
    'rate.phase_break_title':   'Halfway there!',
    'rate.phase_break_sub':     "You've answered the 5 key questions. Submit now or go deeper with 5 more.",
    'rate.phase_continue':      'Continue — 5 more questions →',
    'rate.phase_submit_now':    'Submit with 5 answers',
    'rate.share_title':         'Know another friend who could help {name}?',
    'rate.share_sub':           'Forward this link — their answers add up.',
    'rate.share_copy':          'Copy link',
    'rate.share_copied':        '✓ Copied!',
    'rate.share_native':        'Share →',
    'rate.answers_saved':       'Your answers are already saved.',
  },

  fr: {
    // ── NAVIGATION ────────────────────────────────────────────────────────
    'nav.discover':        'Découvrir',
    'nav.hive':            'La Ruche',
    'nav.events':          'Événements',
    'nav.matches':         'Matchs',
    'nav.messages':        'Messages',
    'nav.profile':         'Profil',
    'nav.settings_label':  "Paramètres d'affichage",
    'nav.lang_label':      'Langue',
    'nav.theme_label':     'Thème',
    'nav.theme_dark':      'Sombre',
    'nav.theme_light':     'Clair',
    'nav.font_label':      'Taille du texte',
    'nav.font_reset':      '{pct}% — réinitialiser',
    'nav.back':            'Retour',

    // ── COMMON BUTTONS ────────────────────────────────────────────────────
    'common.save':         'Enregistrer',
    'common.cancel':       'Annuler',
    'common.back':         '← Retour',
    'common.continue':     'Continuer →',
    'common.done':         'Terminé ✦',
    'common.edit':         'Modifier',
    'common.delete':       'Supprimer',
    'common.upgrade':      'Améliorer →',
    'common.copy':         'Copier',
    'common.copied':       '✓ Copié',
    'common.loading':      'Chargement…',
    'common.verified':     '✓ Vérifié',
    'common.pass':         'Passer',
    'common.like':         'Liker ♥',
    'common.write':        'Écrire ✉',
    'common.or':           'ou',
    'common.soon':         'bientôt',
    'common.free':         'Gratuit',
    'common.per_month':    '/mois',

    // ── AUTH ──────────────────────────────────────────────────────────────
    'auth.welcome_title':  "Trouve quelqu'un qui a déjà été validé par ses amis.",
    'auth.welcome_sub':    'BeeLove est la seule app de rencontres où tes amis répondent à un quiz sur toi.',
    'auth.signin':         'Se connecter',
    'auth.signup':         'Créer un compte',
    'auth.email':          'Adresse e-mail',
    'auth.password':       'Mot de passe',
    'auth.signin_cta':     'Se connecter →',
    'auth.quiz_tagline':   'Tes amis te connaissent. Leurs réponses révèlent ta vraie compatibilité.',
    'auth.quiz_step_label':'Dernière étape — nous aide à trouver tes meilleurs matchs',
    'auth.step_name_q':    'Quel est ton prénom ?',
    'auth.step_name_sub':  "C'est ainsi que tu apparaîtras aux autres.",
    'auth.step_name_ph':   'Ton prénom',
    'auth.step_age_q':     'Quel âge as-tu ?',
    'auth.step_age_sub':   'Tu dois avoir 18 ans ou plus pour rejoindre BeeLove.',
    'auth.step_age_ph':    'Ton âge',
    'auth.step_gender_q':  "Je m'identifie comme…",
    'auth.step_gender_sub':'Nous utilisons cela pour personnaliser ton expérience.',
    'auth.gender_man':     'Homme',
    'auth.gender_woman':   'Femme',
    'auth.gender_nb':      'Non-binaire / Autre',
    'auth.step_seek_q':    'Je cherche…',
    'auth.step_seek_sub':  "Sélectionne tout ce qui s'applique.",
    'auth.seek_man':       'Des hommes',
    'auth.seek_woman':     'Des femmes',
    'auth.seek_nb':        'Des personnes non-binaires',
    'auth.seek_all':       'Tout le monde',
    'auth.step_network_q': 'Choisis ton réseau',
    'auth.step_network_sub':'Rejoins ta communauté locale pour découvrir des gens près de chez toi. Plus de villes bientôt.',
    'auth.network_note':   "Les réseaux te permettent de te connecter avec des gens près de chez toi.\nTu pourras rejoindre des réseaux supplémentaires plus tard.",
    'auth.cta_start':      'Commencer — Bee Loved ✦',
    'auth.already_member': 'Déjà membre ?',
    'auth.no_account':     'Nouveau sur BeeLove ?',

    // ── DISCOVER ──────────────────────────────────────────────────────────
    'discover.empty_title':     "Tu as tout vu",
    'discover.empty_sub':       "Tu as vu tous les profils de ton réseau pour l'instant. Reviens demain !",
    'discover.filter_all':      'Tous',
    'discover.filter_compatible':'Compatibles',
    'discover.filter_top':      'Top profils',
    'discover.filter_nearby':   'À proximité',
    'discover.filter_verified': 'Vérifiés',
    'discover.filter_friends':  "Amis d'abord",
    'discover.friends_rated':   '{n} amis ont répondu',
    'discover.match_title':     "C'est un match !",
    'discover.match_sub':       'Toi et {name} vous êtes likés mutuellement.',
    'discover.match_send':      'Envoyer un message',
    'discover.match_keep':      'Continuer à explorer',
    'discover.match_view':      'Voir le profil',
    'discover.completion_title':'Complète ton profil',
    'discover.completion_steps':'{n} étape{s} restante{s}',
    'discover.quiz_unlock':     'Complète le quiz pour débloquer les scores de compatibilité.',
    'discover.quiz_resume':     "✦ Envie d'améliorer ta compatibilité ? Reprends où tu t'es arrêté(e).",
    'discover.quiz_cta':        'Reprendre le quiz →',
    'discover.quiz_skip':       "Passer pour l'instant →",
    'discover.report_btn':      'Signaler ou bloquer',

    // ── PROFILE SECTIONS ──────────────────────────────────────────────────
    'profile_view.bio_label':      'À propos',
    'profile_view.interests_label':"Centres d'intérêt",
    'profile_view.lifestyle_label':'Mode de vie',
    'profile_view.values_label':   'Valeurs',
    'profile_view.looking_for':    'Je cherche',
    'profile_view.compat_label':   'Compatibilité',
    'profile_view.compat_sub':     'Toi vs {name}',
    'profile_view.friends_label':  'De la part de ses amis',
    'profile_view.music_label':    'Musique',
    'profile_view.prompts_label':  'Prompts',
    'profile_view.tab_about':      'À propos',
    'profile_view.tab_prompts':    'Prompts',
    'profile_view.tab_compat':     'Compatibilité',
    'profile_view.drinking':       'Alcool',
    'profile_view.smoking':        'Tabac',
    'profile_view.exercise':       'Sport',
    'profile_view.diet':           'Alimentation',
    'profile_view.pets':           'Animaux',
    'profile_view.love_language':  "Langage de l'amour",
    'profile_view.sleep':          'Sommeil',

    // ── MY PROFILE ────────────────────────────────────────────────────────
    'my_profile.tab_profile':   'Profil',
    'my_profile.tab_about':     'À propos',
    'my_profile.tab_insights':  'Mes insights',
    'my_profile.tab_invite':    'Inviter',
    'my_profile.tab_journey':   'Parcours',
    'my_profile.tab_settings':  'Paramètres',
    'my_profile.add_photo':     'Ajouter une photo',
    'my_profile.bio_label':     'À propos',
    'my_profile.prompts_label': 'Prompts',
    'my_profile.interests_label':"Centres d'intérêt",
    'my_profile.completion_title':'Force du profil',
    'my_profile.completion_sub': 'Complète ton profil pour attirer de meilleurs matchs.',
    'my_profile.stats_title':   'Cette semaine',
    'my_profile.stats_views':   'Vues du profil',
    'my_profile.stats_likes':   'Likes reçus',
    'my_profile.stats_match':   'Taux de match',
    'my_profile.add_interests': 'Ajouter des intérêts →',
    'my_profile.bio_placeholder':'Dis aux gens qui tu es vraiment…',
    'my_profile.prompt_placeholder':'Tape pour répondre…',
    'my_profile.prompt_choose': 'Choisir un prompt…',

    // ── PROFILE SETTINGS ──────────────────────────────────────────────────
    'settings.quiz_title':          'Quiz de compatibilité',
    'settings.quiz_completed':      '✦ Complété',
    'settings.quiz_sub_done':       'Tes réponses alimentent chaque score de compatibilité que tu vois. Mets-les à jour si tes perspectives ont changé.',
    'settings.quiz_sub_pending':    'Complète le quiz pour débloquer les scores de compatibilité avec chaque profil.',
    'settings.quiz_update_cta':     'Mettre à jour les réponses ✦',
    'settings.quiz_take_cta':       'Passer le quiz ✦',
    'settings.discovery_title':     'Découverte',
    'settings.max_distance':        'Distance maximale',
    'settings.age_range':           "Tranche d'âge",
    'settings.account_title':       'Compte',
    'settings.edit_email':          "Modifier l'e-mail",
    'settings.change_password':     'Changer le mot de passe',
    'settings.verify_identity':     "Vérifier l'identité",
    'settings.linked_accounts':     'Comptes liés',
    'settings.download_data':       'Télécharger mes données',
    'settings.delete_account':      'Supprimer le compte',
    'settings.network_title':       'Réseau',
    'settings.network_active':      'Actif',
    'settings.network_coming':      'Plus de villes bientôt — Toronto, Vancouver, Paris et plus.',
    'settings.pause_label':         'Mettre le profil en pause',
    'settings.pause_desc':          "Ton profil n'apparaîtra pas dans Découvrir pendant la pause. Tes matchs et messages sont conservés.",
    'settings.paused_notice':       '⏸ Ton profil est actuellement en pause',
    'settings.blocked_title':       'Utilisateurs bloqués',
    'settings.unblock':             'Débloquer',
    'settings.admin_link':          'Tableau de bord admin',
    'settings.pause_title':         'Pause et confidentialité',

    // ── INVITE TAB ────────────────────────────────────────────────────────
    'invite.rate_title':       'Amis qui te notent',
    'invite.rate_cta':         'Obtenir plus de réponses',
    'invite.progress_live':    "✦ Ton profil de compatibilité est actif. Plus d'amis = des insights plus riches.",
    'invite.progress_needed':  "Tu as besoin de {n} réponse{s} d'ami{s} de plus pour débloquer ton profil.",
    'invite.how_title':        "Comment les notations d'amis fonctionnent",
    'invite.how_1':            'Ton ami ouvre ton lien — pas besoin de compte',
    'invite.how_2':            'Il répond à 10 questions sur ta personnalité et ton caractère',
    'invite.how_3':            'Les réponses sont agrégées anonymement — les réponses individuelles ne sont jamais affichées',
    'invite.how_4':            'Dès que 5 amis complètent le quiz, ton profil de compatibilité est révélé',
    'invite.how_5':            'Plus tes amis répondent, plus ton profil est riche et précis',
    'invite.referral_title':   'Invite des amis sur BeeLove 🐝',
    'invite.referral_reward':  'Pour chaque 2 amis qui rejoignent, tu reçois 1 mois de Bee Honey gratuit.',
    'invite.friends_joined':   'Amis inscrits',
    'invite.months_earned':    'Mois gagnés',
    'invite.next_reward':      'Prochain cadeau dans',
    'invite.more':             'de plus',
    'invite.copy_code':        'Copier le code',

    // ── MATCHES ───────────────────────────────────────────────────────────
    'matches.new_title':    'Nouveaux matchs',
    'matches.all_title':    'Tous les matchs',
    'matches.matched':      'Matché(e)',
    'matches.empty_title':  'Tes matchs apparaîtront ici.',
    'matches.empty_sub':    'Continue de swiper pour trouver des personnes compatibles.',
    'matches.expiry':       '⏱ {days}j pour connecter',
    'matches.expired':      'Expiré',
    'matches.compat_label': '{pct}% de compatibilité',
    'matches.tap_compat':   'Appuie pour voir la compatibilité →',
    'matches.no_compat':    'Complète le quiz pour débloquer la compatibilité →',

    // ── MESSAGES ──────────────────────────────────────────────────────────
    'messages.tab_matched':   'Matchs',
    'messages.tab_received':  'Reçus',
    'messages.sort_recent':   'Récents',
    'messages.sort_compat':   'Compat',
    'messages.empty_matched': "Aucune conversation pour l'instant.",
    'messages.empty_received':'Aucun message reçu.',
    'messages.ice_label':     '✦ Pour briser la glace',
    'messages.ice_own':       'Écrire soi-même →',
    'messages.like_back':     'Liker en retour',
    'messages.ignore':        'Ignorer',
    'messages.received_notice':'Ils ne savent pas que tu as lu ce message.',
    'messages.received_sub':  'Like leur profil pour matcher et commencer à chatter.',
    'messages.input_ph':      'Message…',
    'messages.send':          'Envoyer',
    'messages.wrote_to_you':  "· t'a écrit",
    'messages.near_you':      'Près de toi',
    'matches.compat_title':    'Compatibilité',
    'matches.compat_breakdown': 'Détail de la compatibilité',
    'matches.personality':      'Chevauchement de personnalité',
    'matches.connections':      'Tes connexions',
    'matches.empty_bee':        'Bee Patient(e)',
    'matches.empty_swipe':      'Continue de swiper pour trouver tes matchs.',
    'messages.empty_bee':       'Bee In Contact',
    'messages.write_own':       'Écrire soi-même →',
    'messages.no_matches':      "Aucun match pour l'instant",
    'messages.swipe_hive':      'Continue de swiper pour trouver ta ruche ✦',
    'messages.say_hello':       'Dis bonjour ✦',
    'messages.unread_notice':   "Ils ne savent pas si tu as lu leur message",
    'messages.no_incoming':     'Aucun message entrant',
    'messages.incoming_sub':    "Quand quelqu'un écrit avant le match, ça apparaît ici.",

    // ── BBEEHIVE ───────────────────────────────────────────────────────────
    'hive.title':          'Communauté BeeLove',
    'hive.subtitle':       'Ta ruche. De vraies connexions.',
    'hive.tab_community':  'Communauté',
    'hive.tab_journey':    'Mon Parcours',
    'hive.tab_week':       'Cette Semaine',
    'hive.all_groups':     'Tous les groupes',
    'hive.no_members':     "Aucun membre dans ce groupe pour l'instant.",
    'hive.members':        '{n} membres',
    'hive.beewise_title':  'Bee Wise',
    'hive.beewise_sub':    "Conseils sur l'amour, la communication et les connexions",
    'hive.stories_title':  'Histoires de la ruche',
    'hive.season_sub':     'Les profils correspondants obtiennent une visibilité boostée',
    'hive.season_note':    'Les questions et prompts de cette semaine sont organisés autour de ce thème. Sois authentique.',
    'hive.week_title':     'Saison actuelle',
    'hive.stats_title':    'La Ruche cette semaine',
    'hive.role_label':     'Ton rôle Bee',
    'hive.mood_label':     'Humeur actuelle',
    'hive.hive_score':     'Score de Ruche',

    // ── EVENTS ────────────────────────────────────────────────────────────
    'events.title':        'Événements',
    'events.subtitle':     'Rencontre tes matchs IRL — sélectionnés par compatibilité',
    'events.partners_label':'Partenaires Bee Date — montre ton app pour des réductions',
    'events.free':         'Gratuit',
    'events.free_gold':    '✦ Gratuit',
    'events.register':     "S'inscrire",
    'events.waitlist':     "Liste d'attente",
    'events.spots_left':   'Plus que {n} places !',
    'events.spots':        'Places',
    'events.remaining':    '{n} sur {max} restantes',
    'events.included':     "Ce qui est inclus",
    'events.pricing':      'Tarifs',
    'events.price_standard':'Standard',
    'events.price_honey':  'Bee Honey',
    'events.price_gold':   'Bee Gold',
    'events.price_free_gold':'Gratuit ✦',
    'events.price_off_10': '(10% de réduction)',
    'events.price_off_20': '(20% de réduction)',
    'events.reserve_free': 'Réserver ma place — Gratuit ✦',
    'events.register_cta': "S'inscrire — {price} $ CAD",
    'events.export_ics':   'Ajouter au calendrier (.ics)',
    'events.export_google':'Google Agenda',
    'events.export_label': 'Exporter :',
    'events.ics_label':    '.ics / Apple / Outlook',
    'events.coming_soon':  'Événements bientôt disponibles',
    'events.coming_sub':   "Les événements BeeLove sont en cours de planification pour Montréal. Reste à l'écoute !",
    'events.save_pct':     'Les membres Honey & Gold économisent 10–20%',
    'events.date_time':    'Date et heure',
    'events.location':     'Lieu',

    // ── REPORT/BLOCK ──────────────────────────────────────────────────────
    'report.title':            'Signaler ou bloquer {name}',
    'report.report_cta':       'Signaler {name}',
    'report.report_sub':       'Quelque chose semble anormal',
    'report.block_cta':        'Bloquer',
    'report.block_sub':        "Ils ne pourront plus te voir ni te contacter",
    'report.both_cta':         'Signaler et bloquer',
    'report.both_sub':         'Signaler puis bloquer immédiatement',
    'report.reason_label':     "Que se passe-t-il ?",
    'report.reason_anon':      'Ton signalement est anonyme et aide à garder BeeLove sûr.',
    'report.urgent_badge':     'urgent',
    'report.submit_report':    'Soumettre le signalement',
    'report.submit_both':      'Signaler et bloquer',
    'report.block_confirm':    'Bloquer {name} ?',
    'report.block_warning':    "Ils ne pourront plus voir ton profil, t'envoyer des messages, ni apparaître dans Découvrir. Tu peux les débloquer depuis les paramètres.",
    'report.block_yes':        'Oui, bloquer {name}',
    'report.done_blocked':     'Bloqué',
    'report.done_both':        'Signalé et bloqué',
    'report.done_reported':    'Signalement soumis',
    'report.done_blocked_msg': "{name} a été bloqué(e). Vous ne vous verrez plus.",
    'report.done_sub':         "Merci d'aider à garder BeeLove sûr. Notre équipe examine tous les signalements dans les 24 heures.",

    // ── PROFILE COMPLETION ────────────────────────────────────────────────
    'completion.banner_title': 'Complète ton profil',
    'completion.steps_left':   '{n} étape{s} restante{s}',
    'completion.go':           'Aller →',
    'completion.milestone_25': 'Bon début ! Ajoute quelques détails pour attirer de meilleurs matchs.',
    'completion.milestone_50': "Tu es à mi-chemin. Les profils à 50%+ obtiennent 3× plus de likes.",
    'completion.milestone_75': 'Presque complet ! Les profils au-dessus de 75% obtiennent 5× plus de matchs.',
    'completion.milestone_100':"Profil parfait ! Tu te montres sous ton meilleur jour. 🐝",
    'completion.stats_sub':    'Les profils complétés à 75%+ obtiennent 5× plus de matchs.',
    'completion.boost_link':   'Booster ton profil →',

    // ── UPGRADE / PLANS ───────────────────────────────────────────────────
    'upgrade.title':           'Améliore ton expérience',
    'upgrade.sub':             'Annule à tout moment. Aucun frais caché.',
    'upgrade.tab_plans':       'Forfaits',
    'upgrade.tab_addons':      'Options',
    'upgrade.current_plan':    '✓ Forfait actuel',
    'upgrade.trial':           "{n} jours d'essai gratuit",
    'upgrade.start_trial':     'Commencer {n} jours gratuits →',
    'upgrade.most_popular':    'LE PLUS POPULAIRE',
    'upgrade.stay_free':       'Rester gratuit',

    // ── BADGES ────────────────────────────────────────────────────────────
    'badges.earned_title':     'Mes badges',
    'badges.locked_title':     'À débloquer',
    'badges.visible':          '● visible',
    'badges.hidden':           '○ caché',
    'badges.always_shown':     'toujours affiché',
    'badges.private':          'privé',

    // ── BEE WISE ─────────────────────────────────────────────────────────
    'beewise.title':           'Bee Wise',
    'beewise.subtitle':        "Conseils sur l'amour, la communication et les connexions",
    'beewise.featured':        'À la une',
    'beewise.more':            "Plus d'articles",
    'beewise.read_time':       '{n} min de lecture',
    'beewise.cat_all':         'Tous',

    // ── SPOTIFY ───────────────────────────────────────────────────────────
    'spotify.connect':         'Connecter Spotify',
    'spotify.connecting':      'Connexion…',
    'spotify.connected':       'Connecté',
    'spotify.top_artists':     'Tes artistes préférés',
    'spotify.disconnect':      'Déconnecter',
    'spotify.sub':             'Montre tes goûts musicaux sur ton profil',
    'spotify.connected_sub':   'Connecté · {n} artistes partagés',

    // ── LANDING ───────────────────────────────────────────────────────────
    'landing.hero_title':      "Trouve quelqu'un qui a déjà été validé par ses amis.",
    'landing.hero_sub':        "BeeLove est la seule app de rencontres où tes amis répondent à un quiz sur toi. Leurs réponses révèlent une compatibilité qu'aucun algorithme ne peut simuler.",
    'landing.hero_cta':        'Commencer gratuitement',
    'landing.hero_signin':     'Se connecter',
    'landing.stat_members':    'Membres',
    'landing.stat_quiz':       'Taux de match quiz',
    'landing.stat_matches':    'Plus de matchs',
    'landing.how_title':       'Quatre étapes pour être Bee Loved',
    'landing.trust_title':     'Basé sur une vraie confiance',
    'landing.trust_sub':       'Chaque profil est cautionné par des amis. Chaque match est mérité. Pas de robots, pas de faux, pas de fantômes.',
    'landing.cta_title':       'Prêt(e) à être vraiment connu(e) ?',
    'landing.cta_sub':         'La seule app de rencontres où tes amis te cautionnent. De vraies personnes. Une vraie compatibilité. Un vrai amour.',
    'landing.cta_btn':         'Commencer gratuitement — Bee Loved ✦',
    'landing.signin_link':     'Déjà membre ? Se connecter',
    'landing.nav_how':      'Comment ça marche',
    'landing.nav_profiles': 'Profils',
    'landing.nav_pricing':  'Tarifs',
    'landing.nav_faq':      'FAQ',
    'landing.how_step1_title': 'Crée ton profil',
    'landing.how_step1_body':  'Photos, bio, quelques prompts. 3 minutes.',
    'landing.how_step2_title': 'Invite tes amis',
    'landing.how_step2_body':  'Partage ton lien unique. Tes amis répondent à 10 questions sur toi — anonymement.',
    'landing.how_step3_title': 'Bee Elevated',
    'landing.how_step3_body':  "Ton profil de compatibilité s'approfondit. L'algorithme fait remonter les gens qui te correspondent vraiment.",
    'landing.how_step4_title': 'Bee Loved',
    'landing.how_step4_body':  "Rencontre IRL lors d'événements BeeLove. Construis quelque chose de vrai.",
    'landing.trust_feature1_title': 'Profils vérifiés par des amis',
    'landing.trust_feature1_desc':  "Minimum 5 amis répondent à un quiz sur toi avant que ton profil soit complet. Pas d'autopromotion.",
    'landing.trust_feature2_title': 'Vraie compatibilité',
    'landing.trust_feature2_desc':  "Quiz de 10 questions répondues par des gens qui te connaissent vraiment, pas une bio auto-écrite.",
    'landing.trust_feature3_title': 'Communauté sécurisée',
    'landing.trust_feature3_desc':  "Tous les profils sont modérés. Signalement et blocage toujours disponibles.",
    // ── AUTO-MERGED FROM PATCHES ─────────────────────────────────────────
    'landing.nav_concept': 'Le concept',
    'landing.tagline': "L'app de rencontres sociale",
    'landing.problems_fixed': "Les apps de rencontres sont cassées. On a réparé ça.",
    'landing.real_members': 'Vrais membres',
    'landing.real_stories': 'Vraies histoires',
    'landing.questions_label': 'Questions',
    'landing.ready': 'Prêt(e) ?',
    'landing.hundreds_more': 'Des centaines de profils — vérifiés, notés, réels.',
    'landing.plan_free_label': 'Gratuit pour toujours',
    'landing.plan_honey_label': 'Le plus populaire',
    'landing.plan_gold_label': 'Pour les dévoués',
    'landing.plan_royal_label': 'Pour les sérieux',
    'landing.go_royal': 'Go Royal ✦',
    'landing.the_one': "L'élu(e)",

    // Auth extra,
    'auth.or_email': 'OU AVEC EMAIL',
    'auth.welcome_back': 'Bon retour',
    'auth.sign_out':         'Se déconnecter',
    'auth.forgot_password':  'Mot de passe oublié ?',
    'auth.reset_sent':       'Lien envoyé — vérifie ta boîte mail.',
    'auth.error_email_required': 'Veuillez saisir votre adresse e-mail.',
    'auth.privacy_policy': 'Politique de confidentialité',
    'auth.network_note2': 'Tu pourras rejoindre des réseaux supplémentaires plus tard.',
    'auth.or': 'ou',

    // Discover extra,
    'discover.bee_noticed': 'Bee Remarqué(e)',
    'discover.you_seen_all': 'Tu les as tous vus',
    'discover.start_over': 'Recommencer',
    'discover.report_received': 'Signalement reçu',
    'discover.report_anon': 'Ton signalement est anonyme. Nous examinons chaque rapport dans les 24h.',
    'discover.report_title': 'Signaler ce profil',
    'discover.we_review': 'Nous examinerons ce profil dans les 24 heures.',
    'discover.skip_now': "Passer pour l'instant",

    // Profile extra,
    'profile.bio_answer_prompts': 'Réponds à quelques prompts pour une meilleure bio :',
    'profile.answer_all': 'Réponds à tout pour sauvegarder',
    'profile.based_on': 'Basé sur',
    'profile.basics_label': 'Informations de base',
    'profile.anonymous': 'Anonyme',
    'profile.anon_average': 'Moyenne anonyme',
    'profile.bee_mood': 'Humeur Bee',
    'profile.bee_role': 'Rôle Bee',
    'profile.current_season': 'Thème de la saison actuelle',
    'profile.earn_honey': 'Gagne du Bee Honey gratuit',
    'profile.honey_reward_desc': 'Pour chaque 2 amis qui rejoignent, tu reçois 1 mois de Bee Honey gratuit.',
    'profile.active_label': 'Actif',
    'profile.share_rating': 'Partage ton lien de notation',
    'profile.friends_know': 'Envoie ce lien à au moins 5 amis. Ils répondent à des questions sur ton caractère — pas seulement tes photos.',
    'profile.friends_responded': '{n} amis ont répondu',
    'profile.account_label': 'Compte',
    'profile.edit_email': "Modifier l'e-mail",
    'profile.change_password': 'Changer le mot de passe',
    'profile.verify_identity': "Vérifier l'identité",
    'profile.linked_accounts': 'Comptes liés',
    'profile.download_data': 'Télécharger mes données',
    'profile.delete_account': 'Supprimer le compte',
    'profile.seeking_label': 'Je cherche',
    'profile.visible_label': 'Profil visible',
    'profile.incognito_label': 'Mode incognito',
    'profile.notifs_label': 'Notifications',
    'profile.on': 'Activé',
    'profile.off': 'Désactivé',
    'profile.yes': 'Oui',
    'profile.no': 'Non',
    'profile.network_active': 'Actif',
    'profile.journey_stage': 'étapes',
    'profile.bee_loved_label': 'Bee Loved',
    'profile.remaining': 'restantes',
    'profile.stage_completed': 'Complété',
    'profile.hive_score_label': 'Score de Ruche',
    'profile.your_role': 'Ton rôle',
    'profile.your_mood': 'Ton humeur',
    'profile.ratings_visible': 'Visible sur ta carte, tes matchs et dans les messages.',
    'profile.ratings_7days': 'Visible sur ton profil pendant 7 jours',
    'profile.coherence_label': 'Cohérence',
    'profile.axis_label': 'Axes du profil',
    'profile.location_label': 'Localisation',
    'profile.city_label':     'Ville',
    'profile.bio_label':      'À propos de moi',
    'profile.job_label': 'Métier',
    'profile.languages_label': 'Langues',
    'profile.add_location': 'Ajouter une localisation…',
    'profile.add_job': 'Ajouter un métier…',

    // BeeHive extra,
    'hive.week_this': 'Cette semaine dans la ruche',
    'hive.earned_desc': "Gagné grâce à un engagement authentique. Ne diminue jamais.",
    'hive.find_people': 'Trouve tes gens par ce que tu aimes.',
    'hive.role_desc': "Ton rôle se révèle au fil de ton engagement.",
    'hive.roles_list': 'Explorateur · Constructeur · Connecteur · Reine des abeilles',
    'hive.your_answer':      'Ta réponse',
    'hive.answer_ph':        'Ta réponse honnête...',
    'hive.weekly_question':  'Question de la semaine',
    'hive.answer_btn':       'Répondre',
    'hive.weekly_challenge': 'Défi de la semaine',
    'hive.coming_soon':      'BeeHive arrive bientôt',
    'hive.coming_soon_sub':  'Restez connectés — cette fonctionnalité est en chemin.',

    'beeconnect.coming_soon': 'BeeConnect arrive bientôt',
    'circles.coming_soon':    'Scout arrive bientôt',
    'common.stay_tuned':      'Restez connectés — cette fonctionnalité est en chemin.',

    'profile.generate_bio':   'Générer ma bio',

    // Events extra,
    'events.no_upcoming': 'Aucun événement à venir dans cette catégorie.',
    'events.upgrade_cta': 'Améliorer →',
    'events.whats_included': 'Ce qui est inclus',
    'events.export_label2': 'Exporter :',
    'events.city': 'Montréal',

    // Navbar extra,
    'nav.your_networks': 'Tes réseaux',
    'nav.more_cities': 'Plus de villes bientôt. Les réseaux te connectent avec des gens proches.',
    'nav.bee_the_one': "Bee L'élu(e) ✦",

    // ProfileCompletion extra,
    'completion.profile_strength': 'Force du profil',
    'completion.this_week': 'Cette semaine',
    'completion.boost': 'Booster ton profil →',

    // ReportBlock extra,
    'report.details_ph': 'Détails supplémentaires (optionnel)',
    'report.anon_help': "Que se passe-t-il ? Ton signalement est anonyme et aide à garder BeeLove sûr.",

    // StaticPage,
    'static.coming_soon': 'Bientôt disponible',
    'static.being_prepared': 'Cette page est en cours de préparation.',

    // Common additions,
    'common.ignore': 'Ignorer',
    'common.name': 'Nom',
    'common.or_with_email': 'ou avec e-mail',
    'common.on': 'Activé',
    'common.off': 'Désactivé',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.add': 'Ajouter',
    'common.remove': 'Supprimer',
    'common.close': 'Fermer',
    'common.settings': 'Paramètres',
    'common.search': 'Rechercher…',
    'common.km': 'km',
    'common.select': 'Sélectionner',
    'common.none': 'Aucun',

    // ── COMPREHENSIVE PATCH v2 ────────────────────────────────────────────,
    'landing.join_free': "Rejoins des milliers de personnes qui ont choisi l'honnêteté.",
    'landing.no_dark': "Pas de dark patterns. Pas de fausse urgence. Améliore seulement quand ça t'apporte de la valeur.",
    'landing.no_swiping': 'Pas de swipe dans le vide. Pas de faux profils. Juste de vraies personnes, cautionnées.',
    'landing.every_other_app': "Chaque autre app donne la même chance à tout le monde. Nous donnons la meilleure chance aux personnes les plus authentiques.",
    'landing.how_it_works': 'Comment ça marche →',
    'landing.free_join': "Gratuit pour rejoindre · Pas de carte · Annule à tout moment",
    'landing.plan_honey': 'Honey',
    'landing.step_tag_free': 'Gratuit pour toujours',
    'landing.step_tag_friends': 'Min. 5 amis',
    'landing.step_tag_smart': 'Matching intelligent',
    'landing.step_tag_real': 'Vraies connexions',
    'landing.footer_links_company': 'Entreprise',
    'landing.footer_links_legal': 'Légal',
    // Auth,
    'auth.join_free': 'Bee Loved — rejoindre gratuitement →',
    'auth.continue_google': 'Continuer avec Google',
    'auth.continue_apple': 'Continuer avec Apple',
    'auth.continue_fb': 'Continuer avec Facebook',
    'auth.sign_in_email': "Se connecter avec l'e-mail",
    'auth.previous': 'Précédent',
    'auth.step_quiz_label': "Dernière étape — nous aide à trouver tes meilleurs matchs",
    // Discover,
    'discover.like_btn': 'LIKER',
    'discover.pass_btn': 'PASSER',
    'discover.keep_exploring': 'Continuer à explorer',
    'discover.filter_bestmatch': 'Meilleur match',
    'discover.filter_toprated': 'Mieux notés',
    'discover.label_lifestyle': 'Mode de vie',
    'discover.label_values': 'Valeurs',
    'discover.label_interests': "Centres d'intérêt",
    'discover.label_prompts': 'Prompts',
    'discover.label_friends': 'De la part de ses amis',
    'discover.label_looking': 'Je cherche',
    'discover.label_compat': 'Compatibilité',
    'discover.open_friends': 'Ouvert aux amis',
    'discover.super_like': 'Super like',
    'discover.view_profile': 'Voir le profil',
    // Matches,
    'matches.close': 'Fermer',
    'matches.discover_cta': 'Découvrir des profils →',
    'matches.axis_stability': 'Stabilité identitaire',
    'matches.axis_boredom': "Tolérance à l'ennui",
    'matches.axis_conflict': 'Style de conflit',
    'matches.axis_generosity': 'Générosité',
    'matches.axis_presence': 'Présence',
    'matches.axis_repair': 'Réparation',
    'matches.axis_self_aware': 'Conscience de soi',
    'matches.axis_social': 'Social',
    'matches.axis_accountability': 'Responsabilité',
    // Messages,
    'messages.like_match': 'Liker en retour — matcher maintenant',
    'messages.icebreakers_label': '✦ Brise-glaces',
    // BeeHive,
    'hive.close': 'Fermer',
    'hive.edit': 'Modifier',
    'hive.open_profile': 'Ouvrir le profil',
    'hive.new_members': 'Nouveaux membres',
    'hive.conversations': 'Conversations',
    'hive.this_week_stats': 'La Ruche cette semaine',
    'hive.your_stages': 'Tes étapes',
    // Events,
    'events.all_events': 'Tous les événements',
    'events.spots_label': 'Places',
    'events.add_ics': 'Ajouter au calendrier (.ics)',
    'events.coming_sub2': "Les événements BeeLove sont en cours de planification pour Montréal. Reste à l'écoute !",
    // Upgrade,
    'upgrade.premium_title': 'Bee Premium',
    'upgrade.cad_mo': 'CAD/mois',
    'upgrade.no_fees': "Annule à tout moment. Aucun frais caché.",
    'upgrade.addons_sub': "Achats ponctuels et options mensuelles. Cumulez avec n'importe quel forfait.",
    'upgrade.one_time': 'ponctuel',
    'upgrade.back_profile': 'Retour au profil',
    // ProfileCompletion,
    'completion.views': 'Vues du profil',
    'completion.likes': 'Likes reçus',
    'completion.match_rate': 'Taux de match',
    'completion.almost': 'Presque là !',
    // Spotify,
    'spotify.music': 'Musique',
    'spotify.top_artists_label': 'Tes artistes préférés',
    // Notifications,
    'notifications.title': 'Notifications',
    // Common,
    'common.done_btn': 'Terminé ✦',
    'common.back_btn': '← Retour',
    'common.close_btn': 'Fermer',
    'common.urgent': 'urgent',
    'common.go': 'Aller →',
    'common.report': 'Signaler',


    // ── COMPLETE FR TRANSLATIONS ADDED ──────────────────────────────────
    'landing.how_arrow': "Comment ça marche →",
    'landing.concept': "Le concept",
    'landing.four_steps': "Quatre étapes pour",
    'landing.people_worth': "Des personnes qui valent",
    'landing.knowing': "la peine",
    'landing.hundreds': "Des centaines de profils — vérifiés, notés, réels.",
    'landing.see_waiting': "Voir qui t'attend ✦",
    'landing.why_title': "✦ Pourquoi BeeLove",
    'landing.broken_1': "Les apps de rencontres sont",
    'landing.broken_2': "cassées.",
    'landing.fixed': "On a réparé ça.",
    'landing.feat1_title': "Profils vérifiés par des amis",
    'landing.feat1_desc': "Minimum 5 amis répondent à un quiz sur toi. Pas d'autopromotion. Pas de hype. Juste des retours honnêtes.",
    'landing.feat2_title': "Matching par valeurs",
    'landing.feat2_desc': "Notre quiz de 10 questions va au-delà des hobbies. Nous matchons sur ce qui compte vraiment.",
    'landing.feat3_title': "Protection anti-faux intégrée",
    'landing.feat3_desc': "Impossible de simuler une notation d'ami. Chaque profil vérifié est une vraie personne.",
    'landing.feat4_title': "Bio générée par IA",
    'landing.feat4_desc': "Réponds à 5 questions. Claude écrit ta bio dans ta voix.",
    'landing.found_love': "Ils ont trouvé l'amour.",
    'landing.bee_together': "Leur histoire a commencé sur",
    'landing.bee_together_2': "ensemble.",
    'landing.start_free': "Commencer gratuitement.",
    'landing.bee_royal_ready': "quand tu es prêt(e).",
    'landing.plan_feat_likes': "10 likes par jour",
    'landing.plan_feat_quiz': "Quiz de compatibilité",
    'landing.plan_feat_ai_bio': "Bio générée par IA",
    'landing.plan_feat_invites': "5 invitations d'amis",
    'landing.plan_feat_hive': "Accès communauté La Ruche",
    'landing.plan_feat_liked': "Voir qui t'a liké",
    'landing.plan_feat_unlimited': "Invitations illimitées",
    'landing.plan_feat_boost': "Boost hebdomadaire",
    'landing.start_trial': "Commencer 7 jours gratuits ✦",
    'landing.everything_need': "Tout ce qu'il faut",
    'landing.to_bee_ready': "savoir pour être",
    'landing.stop_waiting': "Arrête d'attendre.",
    'landing.bee_the_one_find': "qui te trouve.",
    'landing.footer_how': "Comment ça marche",
    'landing.footer_pricing': "Tarifs",
    'landing.footer_signup': "S'inscrire",
    'landing.footer_signin': "Se connecter",
    'landing.footer_about': "À propos",
    'landing.footer_blog': "Blog",
    'landing.footer_press': "Presse",
    'landing.footer_careers': "Carrières",
    'landing.footer_privacy': "Politique de confidentialité",
    'landing.footer_terms': "Conditions d'utilisation",
    'landing.footer_cookies': "Politique des cookies",
    'landing.footer_contact': "Contact",
    'landing.footer_product': "Produit",
    'landing.join_free_cta': "Rejoindre gratuitement — ",
    'landing.profiles_nav': "Profils",
    'landing.plans_label': "Nos forfaits",
    'landing.faq_q1': "Comment fonctionne le quiz des amis ?",
    'landing.faq_a1': "Tes amis reçoivent un lien privé et répondent à 10 questions sur toi. Il te faut au moins 5 amis. Les réponses sont agrégées anonymement.",
    'landing.faq_q2': "Peut-on simuler le quiz des amis ?",
    'landing.faq_a2': "Par conception, c'est impossible à grande échelle. Les bots ne peuvent pas convaincre de vraies personnes de répondre.",
    'landing.faq_q3': "Qu'est-ce que le quiz de compatibilité ?",
    'landing.faq_a3': "Le quiz comporte 10 questions en 3 catégories. Il prend 3 minutes et calcule ta compatibilité avec chaque membre.",
    'landing.faq_q4': "BeeLove est-il disponible dans ma ville ?",
    'landing.faq_a4': "BeeLove est disponible en France et au Canada. Nous nous développons rapidement.",
    'landing.faq_q5': "Et si les réponses de mes amis révèlent quelque chose d'inattendu ?",
    'landing.faq_a5': "Leurs retours sont privés — seul toi vois les insights agrégés.",
    'landing.faq_q6': "En quoi BeeLove est-il différent de Hinge ou Tinder ?",
    'landing.faq_a6': "BeeLove est la première app à intégrer la preuve sociale dans les rencontres.",
    'landing.step1_title': "Bee Presented",
    'landing.step2_title': "Bee Endorsed",
    'landing.step3_title': "Bee Elevated",
    'landing.step4_title': "Bee In Love",
    'landing.hero_dont_just': 'Sois toi-même.',
    'landing.hero_bee_here': 'présent(e) —',
    'discover.bee_patient': "Bee Patient(e)",
    'discover.submit_report': "Soumettre le signalement",
    'hive.stat_matches': "Matchs",
    'hive.stat_stories': "Histoires",
    'matches.axis_conflict2': "Conflit",
    'matches.axis_stability2': "Stabilité",
    'matches.axis_boredom2': "Ennui",
    'matches.axis_stakes': "Enjeux",
    'auth.network_montreal': "Montréal",
    'auth.network_montreal_area': "Grand Montréal",
    'profile.edu_highschool': "Lycée",
    'profile.edu_college': "Cégep / DEC",
    'profile.edu_bachelor': "Baccalauréat",
    'profile.edu_master': "Maîtrise",
    'profile.edu_trade': "Formation professionnelle",
    'profile.edu_other': "Autre",
    'profile.intent_relationship': "Relation sérieuse",
    'profile.intent_casual': "Quelque chose de léger",
    'profile.intent_friends': "D'abord des amis",
    'profile.intent_marriage': "Mariage",
    'profile.intent_notsure': "Pas encore sûr(e)",
    'profile.drink_never': "Jamais",
    'profile.drink_rarely': "Rarement",
    'profile.drink_socially': "Socialement",
    'profile.drink_regularly': "Régulièrement",
    'profile.smoke_never': "Jamais",
    'profile.smoke_rarely': "Rarement",
    'profile.smoke_socially': "Socialement",
    'profile.smoke_daily': "Quotidiennement",
    'profile.exercise_never': "Jamais",
    'profile.exercise_sometimes': "Parfois",
    'profile.exercise_often': "Souvent",
    'profile.exercise_daily': "Tous les jours",
    'profile.diet_omnivore': "Omnivore",
    'profile.diet_vegetarian': "Végétarien(ne)",
    'profile.diet_vegan': "Végétalien(ne)",
    'profile.diet_pescatarian': "Pescétarien(ne)",
    'profile.diet_halal': "Halal",
    'profile.diet_kosher': "Casher",
    'profile.diet_glutenfree': "Sans gluten",
    'profile.pet_none': "Pas d'animaux",
    'profile.pet_dog': "Chien(s)",
    'profile.pet_cat': "Chat(s)",
    'profile.pet_other': "Autres animaux",
    'profile.sleep_early': "Lève-tôt",
    'profile.sleep_night': "Couche-tard",
    'profile.sleep_flexible': "Flexible",
    'profile.love_words': "Mots d'affirmation",
    'profile.love_time': "Temps de qualité",
    'profile.love_gifts': "Cadeaux",
    'profile.love_service': "Actes de service",
    'profile.love_touch': "Contact physique",
    'profile.politics_liberal': "Libéral(e)",
    'profile.politics_moderate': "Modéré(e)",
    'profile.politics_conservative': "Conservateur/trice",
    'profile.politics_apolitical': "Apolitique",
    'profile.religion_none': "Non religieux/se",
    'profile.religion_christian': "Chrétien(ne)",
    'profile.religion_muslim': "Musulman(e)",
    'profile.religion_jewish': "Juif/ve",
    'profile.religion_hindu': "Hindou(e)",
    'profile.religion_buddhist': "Bouddhiste",
    'profile.religion_spiritual': "Spirituel(le)",
    'profile.religion_agnostic': "Agnostique",
    'profile.religion_atheist': "Athée",
    'profile.cannabis_no': "Non",
    'profile.cannabis_sometimes': "Parfois",
    'profile.cannabis_yes': "Oui",
    'profile.use_this_bio': "Utiliser cette bio",
    'profile.choose_prompt': "Choisir un prompt…",
    'profile.identity_values': "Identité & Valeurs",
    'profile.lifestyle_label': "Mode de vie",
    'profile.lang_english': "Anglais",
    'profile.lang_french': "Français",
    'profile.lang_spanish': "Espagnol",
    'profile.lang_arabic': "Arabe",
    'profile.lang_portuguese': "Portugais",
    'profile.lang_mandarin': "Mandarin",
    'beewise.cat_comm': "Communication",
    'beewise.cat_self': "Connaissance de soi",
    'beewise.cat_dating': "Psychologie des rencontres",
    'beewise.cat_flags': "Red flags",
    'beewise.cat_dates': "Premiers rendez-vous",
    'beewise.art1_title': "Les 5 choses que ton premier message révèle",
    'beewise.art1_sub': "Les premières impressions par texto comptent.",
    'beewise.art2_title': "Ce que tes notations d'amis révèlent",
    'beewise.art2_sub': "L'écart entre comment tu te vois et comment les autres te voient.",
    'beewise.art3_title': "Pourquoi les valeurs comptent plus que les hobbies",
    'beewise.art3_sub': "La recherche montre ce qui prédit la satisfaction relationnelle.",
    'beewise.art4_title': "Les 7 signes qu'une personne est vraiment gentille",
    'beewise.art4_sub': "Pas ce qu'ils disent — ce qu'ils font quand personne ne regarde.",
    'beewise.art5_title': "Le meilleur premier rendez-vous à Montréal",
    'beewise.art5_sub': "Recommandations locales adaptées aux profils.",
    'beewise.art6_title': "Comment savoir quand passer à la vraie vie",
    'beewise.art6_sub': "Le bon moment pour suggérer de se rencontrer.",

    // Plan feature strings — Honey + Royal
    'landing.plan_honey_likes':   'Likes illimités',
    'landing.plan_honey_liked':   "Voir qui t'a liké",
    'landing.plan_honey_quiz':    'Quiz de compatibilité',
    'landing.plan_honey_bio':     'Bio générée par IA',
    'landing.plan_honey_invites': 'Invitations illimitées',
    'landing.plan_honey_boost':   '1 boost hebdomadaire',
    'landing.plan_honey_analytics':'Analytique de profil',
    'landing.plan_royal_all':     'Tout dans Honey',
    'landing.plan_royal_ai':      'Matching IA intelligent',
    'landing.plan_royal_priority':'Priorité dans le fil',
    'landing.plan_royal_boosts':  '3 boosts hebdomadaires',
    'landing.plan_royal_vip':     'Accès événements VIP',
    'landing.plan_royal_receipts':'Accusés de lecture',
    'landing.plan_royal_filters': 'Filtres avancés',
    'landing.plan_royal_support': 'Support dédié',
    // Mixed JSX phrases
    'landing.four_steps_to':      'Quatre étapes pour',
    'landing.people_worth_knowing':'Des personnes qui valent la peine',
    'landing.dating_broken':      'Les apps de rencontres sont',
    'landing.we_fixed':           'On a réparé ça.',
    'landing.they_found':         "Ils ont trouvé l'amour.",
    'landing.now_they':           'Maintenant ils sont',
    'landing.together':           'Love. ✦',
    'landing.start_free_cta':     'Commencer gratuitement.',
    'landing.to_know':            'savoir pour être',
    'landing.signin_arrow':       'Se connecter →',
    'landing.join_free_link':     'Rejoindre gratuitement — ',
    'landing.free_join_note':     'Gratuit · Pas de carte · Annule à tout moment',
    'landing.month_label':        '/ mois',
    'landing.per_mo':             '/mois',

    // Auth onboarding strings
    'auth.step_age_sub2':         'Tu dois avoir 18 ans ou plus pour rejoindre BeeLove.',
    'auth.network_connect':       'Les réseaux te connectent avec des gens dans ta région.',
    'auth.step_of':               'Étape {n} sur {total}',
    'auth.by_continuing':         'En continuant tu acceptes nos',
    'auth.terms_label':           'Conditions',
    'auth.and':                   'et',
    'auth.create_account':        'Créer un compte',
    'auth.sign_in_btn':           'Se connecter →',
    // Landing fixes
    'landing.all_rights':         '© 2025 BeeLove.me — Tous droits réservés.',
    'landing.give_best_shot':     'Nous donnons la meilleure chance aux personnes qui la méritent.',
    'landing.join_free_em':       'Rejoindre — ',
    'landing.ready_label':        'prêt(e)',
    'landing.the_one_find':       "L'être rare qui te trouve.",

    // Landing hero
    'landing.hero_bee_loved':     'Bee Loved.',
    'landing.hero_friends_know':  'Tes amis te connaissent. Leurs réponses révèlent ta vraie compatibilité.',
    // Auth steps
    'auth.complete_profile':      'Complète ton profil — {n} étape restante',
    'auth.complete_quiz':         'Complète le quiz de compatibilité',
    // Discover
    'discover.profiles_remaining':'profils restants · Swipe ou tape pour naviguer',
    'discover.label_adventure':   'Aventure',
    // Navbar
    'nav.beehive':                'La Ruche',
    // BeeHive
    'hive.earned_place':          "Des personnes qui ont gagné leur place.",
    'hive.my_journey':            'Mon Parcours & Badges',
    'hive.journey_in_profile':    'Voir ta progression et tes badges dans ton profil →',
    // Hive tab — Profile
    'hive.bee_role':              'Rôle Bee',
    'hive.bee_mood':              'Humeur Bee',
    'hive.journey_title':         'Mon Parcours',
    'hive.stages':                'étapes',
    'hive.remaining':             'restantes',
    'hive.next_stage':            'Prochain',
    'hive.my_badges':             'Mes Badges',
    'hive.obtained':              'obtenus',
    'hive.to_unlock':             'À débloquer',
    'hive.current_season':        'Saison actuelle',
    'hive.season_theme':          'Le thème de la semaine',
    'hive.share_to_hive':         'Partager dans la ruche',
    'hive.no_badges':             "Aucun badge pour l'instant",
    // Ratings tab
    'ratings.how_seen':           'Comment tes amis te voient',
    'ratings.no_data':            "Aucune note d'ami pour l'instant",
    'ratings.youll_rate':         "Partage ton lien — tes amis répondent à des questions sur toi. Leurs réponses façonnent ton profil de compatibilité.",
    'ratings.what_they_say':      'Ce qu\'ils disent',
    // Plan
    'plan.founder_tag':           'Accès Fondateur',
    'hive.season_adventure':      'Saison Aventure',
    // Profile badge labels
    'profile.badge_height':       '{n} cm',
    'profile.badge_bachelor':     'Baccalauréat',
    'profile.badge_music':        'Musique',
    'profile.badge_casual':       'Léger',
    'profile.badge_away':         'à {n} km',
    // Common
    'common.members':             'membres',
    // Fix testimonial phrasing

    // Auth step questions
    'auth.step_age_q2':           'Quel âge as-tu ?',
    'auth.step_gender_q2':        'Je me définis comme…',
    // Discover — report reasons
    'discover.report_fake':       'Faux profil ou usurpation',
    'discover.report_content':    'Contenu inapproprié ou offensant',
    'discover.report_spam':       "Spam ou tentative d'arnaque",
    'discover.report_harassment': 'Harcèlement ou comportement menaçant',
    'discover.report_underage':   'Personne mineure',
    // Discover — lifestyle
    'discover.not_sure':          'Pas sûr(e)',
    'discover.love_words':        "Mots d'affirmation",
    'discover.love_service':      'Actes de service',
    'discover.love_gifts':        'Cadeaux',
    'discover.love_time':         'Temps de qualité',
    'discover.love_touch':        'Contact physique',
    // Profile — prompts
    'profile.prompt_sunday':      'Un dimanche parfait ressemble à',
    'profile.prompt_truths':      'Deux vérités et un mensonge',
    'profile.prompt_along':       "Je m'entends le mieux avec",
    'profile.prompt_love_lang':   "Mon langage de l'amour est",
    'profile.prompt_opinion':     'Une opinion impopulaire que je soutiens',
    'profile.prompt_heart':       'Le chemin vers mon coeur est',
    'profile.prompt_friends':     'Mes amis me décrivent comme',
    'profile.prompt_dealbreaker': 'Mon plus grand dealbreaker est',
    // Profile — dropdowns
    'profile.edu_phd':            'Doctorat',
    'profile.edu_other2':         'Autre',
    'profile.rel_agnostic':       'Agnostique',
    'profile.rel_atheist':        'Athée',
    'profile.rel_catholic':       'Catholique',
    'profile.pol_very_liberal':   'Très libéral(e)',
    'profile.pol_prefer_not':     'Préfère ne pas dire',
    'profile.cannabis_allergic':  'Allergique',
    'profile.pet_allergic':       'Allergique aux animaux',
    'profile.exercise_1_2':       '1–2×/semaine',
    'profile.exercise_3_4':       '3–4×/semaine',
    'profile.exercise_daily2':    'Quotidiennement',
    // Messages
    'messages.sort_name':         'Nom',
    // Matches axes
    'matches.repair_speed':       'Vitesse de réparation',
    'matches.social_energy':      'Énergie sociale',
    'matches.stakes_tolerance':   'Tolérance aux enjeux',
    'matches.send_notice':        "Ton message est envoyé quand tu appuies sur envoyer — rien n'est envoyé automatiquement.",
    // BeeHive
    'hive.welcome':               'Bienvenue dans la ruche',
    'hive.all_complete':          'Toutes les étapes complètes — Bee Loved',
    // Events
    'events.reserve':             'Réserver ma place ✦',
    'events.free_ticket':         'Gratuit ✦',
    // Upgrade
    'upgrade.back_free':          'Retour au gratuit',
    'upgrade.plan_active':        "Ton forfait actuel reste actif jusqu'à la fin de la période de facturation.",
    'upgrade.done':               'Terminé ✦',
    // ReportBlock
    'report.block_warning2':      'Il/Elle ne pourra pas voir ton profil ni te contacter.',
    'report.then_block':          'Signaler puis bloquer immédiatement',
    // Navbar
    'nav.discover_label':         'Découvrir',
    'nav.events_label':           'Événements',
    'nav.matches_label':          'Matchs',
    'nav.messages_label':         'Messages',
    'nav.profile_label':          'Profil',
    'nav.network_label':          'Réseau',

    'profile.prompt_proud':       "Ce dont je suis le plus fier/fière",
    'profile.prompt_looking':     "Je cherche quelqu'un qui",
    'profile.prompt_skill':       "Un talent dont je suis (bizarrement) fier/fière",
    'profile.prompt_teach':       "Quelque chose que j'adorerais t'apprendre",
    'profile.prompt_along2':      "Je m'entends mieux avec des gens qui",

    // ProfileCompletion steps
    'completion.add_photo':       'Ajouter au moins une photo',
    'completion.write_bio':       'Écrire ta bio',
    'completion.add_prompt':      'Répondre à un prompt',
    'completion.add_interests':   "Ajouter tes centres d'intérêt",
    'completion.take_quiz':       'Compléter le quiz de compatibilité',
    'completion.invite_friend':   'Inviter 1 ami à te noter',
    'completion.fill_basics':     'Remplir tes infos de base (âge, taille)',
    'completion.rated_profile':   'a noté ton profil',
    'completion.liked_profile':   'a liké ton profil',
    'completion.almost':          'Presque complet ! Les profils à plus de 75% obtiennent 5× plus de matchs.',
    'completion.boost_note':      'Les profils à plus de 75% obtiennent 5× plus de matchs. ·',
    // Profile tabs
    'profile.tab_score':          'Mon Score',
    // Profile sections
    'profile.prompts_label':      'Prompts',
    'profile.interests_label':    "Centres d'intérêt",
    // Interest categories
    'interest.strategy':          'Stratégie',
    'interest.coffee':            'Café',
    'interest.urban_cycling':     'Vélo urbain',
    'interest.design':            'Design',
    'interest.photography':       'Photographie',
    'interest.jazz':              'Jazz',
    // Landing
    'landing.hero_sub2':          'Tes amis te connaissent. Leurs réponses révèlent ta vraie compatibilité.',
    // Navbar settings
    'nav.lang_label2':            'Langue',
    'nav.theme_label2':           'Thème',
    'nav.font_label2':            'Taille du texte',
    'nav.font_reset2':            'Réinitialiser',

    // Profile milestones
    'completion.m25':             "Bon début ! Ajoute quelques détails pour attirer de meilleurs matchs.",
    'completion.m50':             "Tu es à mi-chemin. Les profils à 50%+ reçoivent 3× plus de likes.",
    'completion.m75':             'Presque complet ! Les profils à plus de 75% obtiennent 5× plus de matchs.',
    'completion.m100':            "Profil parfait ! Tu te montres sous ton meilleur jour. 🐝",
    // Profile completion CTA labels
    'completion.cta_photo':       'Ajouter photo',
    'completion.cta_bio':         'Écrire bio',
    'completion.cta_prompt':      'Ajouter prompt',
    'completion.cta_interests':   "Ajouter centres d'intérêt",
    'completion.cta_quiz':        'Passer quiz',
    'completion.cta_friends':     'Inviter ami',
    'completion.cta_details':     'Ajouter détails',
    // Notifications
    'notif.rated_profile':        'a noté ton profil',
    'notif.liked_profile':        'a liké ton profil',
    // Profile interests section label
    'profile.interests_section':  "Centres d'intérêt",
    'profile.prompts_section':    'Prompts',
    // Profile dropdown missing values
    'profile.cat_none':           'Aucun',
    'profile.cat_dog':            'Chien',
    'profile.cat_cat':            'Chat',
    'profile.cat_both':           'Les deux',
    'profile.gifts':              'Cadeaux',
    // Profile tab score
    'profile.tab_score':          'Mon Score',
    // Profile share options
    'profile.share_whatsapp':     'Partager sur WhatsApp',
    'profile.share_link':         'Partager le lien',
    'profile.copy_link':          'Copier le lien',
    // Upgrade features list
    'upgrade.feat_see_liked':     "Voir qui t'a liké",
    'upgrade.feat_invites':       "Invitations d'amis illimitées",
    'upgrade.feat_boost':         'Boost de visibilité hebdomadaire',
    'upgrade.feat_priority':      'Priorité dans le matching',
    'upgrade.feat_receipts':      'Accusés de lecture',

    // Monetization plan features
    'plan.free_f1':   'Swipes illimités',
    'plan.free_f2':   "Jusqu'à 5 matchs/mois",
    'plan.free_f3':   'Messagerie de base (10 msg/conv)',
    'plan.free_f4':   'Accès lecture La Ruche',
    'plan.free_f5':   '1 prompt de profil',
    'plan.free_f6':   "3 invitations d'amis",
    'plan.free_f7':   'Badges de base',
    'plan.honey_f1':  'Matchs illimités',
    'plan.honey_f2':  "Voir qui t'a liké",
    'plan.honey_f3':  'Boost de visibilité hebdomadaire',
    'plan.honey_f4':  "Invitations d'amis illimitées",
    'plan.honey_f5':  '3 prompts de profil',
    'plan.honey_f6':  'Filtres avancés (MBTI, langues, valeurs)',
    'plan.honey_f7':  'Mode incognito',
    'plan.honey_f8':  'Accusés de lecture',
    'plan.honey_f9':  "Billets d'événements à -10%",
    'plan.honey_f10': '5 Super Likes/jour',
    'plan.gold_f1':   'Tout dans Bee Honey',
    'plan.gold_f2':   'Voir les profils complets avant le match',
    'plan.gold_f3':   'Super Likes illimités',
    'plan.gold_f4':   'Boost de visibilité quotidien',
    'plan.gold_f5':   'Accès prioritaire aux matchs (nouveaux membres en premier)',
    'plan.gold_f6':   'Tableau de bord analytique du profil',
    'plan.gold_f7':   "Messagerie prioritaire (apparaître en premier)",
    'plan.gold_f8':   'Badge Gold sur le profil public',
    'plan.gold_f9':   '1 événement VIP gratuit/trimestre',
    'plan.gold_f10':  "Réductions illimitées sur les événements (20%)",
    'plan.free_name':   'Gratuit',
    'plan.honey_tag':   'Pour les sérieux',
    'plan.gold_tag':    'Visibilité maximale',
    // Ala carte
    'alacarte.boost_1':    'Boost 24h',
    'alacarte.boost_1_d':  "Pousse ton profil en tête du fil pendant 24 heures",
    'alacarte.boost_5':    'Pack 5 Boosts',
    'alacarte.boost_5_d':  "Utilise à tout moment — n'expire jamais",
    'alacarte.boost_5_b':  'Économise 33%',
    'alacarte.sl_10':      '10 Super Likes',
    'alacarte.sl_10_d':    'Démarque-toi',
    'alacarte.sl_sub':     'Super Likes illimités',
    'alacarte.sl_sub_d':   "Option mensuelle — annule à tout moment",
    'alacarte.stats':      'Analytique de profil',
    'alacarte.stats_d':    'Vues, taux de match, performance des photos',
    'alacarte.verified':   'Vérification avancée',
    'alacarte.verified_d': "Vérification vidéo ou identité — badge permanent",

    // Notifications
    'notif.new_match':      'Nouveau match avec {name} !',
    'notif.rated':          '{name} a noté ton profil',
    'notif.liked_anon':     "Quelqu'un a liké ton profil",
    'notif.boost_active':   'Boost quotidien activé ! 🚀',
    'notif.msg_music':      "Hé ! J'ai vu ton profil et j'adore tes goûts musicaux 🎵",
    'notif.msg_common':     "On a vraiment beaucoup en commun ! J'adorerais discuter.",
    'notif.msg_reviews':    "Tes amis t'ont fait de si bons retours. Curieux/se de te connaître !",
    'notif.msg_reach':      "J'espère que ça ne te dérange pas que je t'écrive directement 🙂",

    // Badge names and descriptions
    'badge.verified_name':    'Vérifié(e)',
    'badge.verified_desc':    'Ta photo correspond à une vraie personne.',
    'badge.mirror_name':      'Miroir',
    'badge.mirror_desc':      "Tu as répondu au même quiz que tes amis.",
    'badge.honest_name':      'Honnête',
    'badge.honest_desc':      'Ton profil est complété avec soin.',
    'badge.active_name':      'Actif/ve',
    'badge.active_desc':      'Tu utilises BeeLove cette semaine.',
    'badge.convo_name':       'Conversationnel(le)',
    'badge.convo_desc':       "Tu t'engages vraiment avec tes matchs.",
    'badge.aligned_name':     'Aligné(e)',
    'badge.aligned_desc':     'Tu te vois comme tes amis te voient.',
    'badge.selfaware_name':   'Lucide',
    'badge.selfaware_desc':   "Tu as consulté ta carte de cohérence.",
    'badge.anchored_name':    'Ancré(e)',
    'badge.anchored_desc':    'Tes amis te connaissent vraiment.',
    'badge.connector_name':   'Connecteur/trice',
    'badge.connector_desc':   'Ta communauté est active autour de toi.',
    'badge.growing_name':     'En croissance',
    'badge.growing_desc':     "Tu as réduit un écart soi/amis ce mois-ci.",
    // Pillar labels
    'pillar.authenticity':    'Authenticité',
    'pillar.engagement':      'Engagement',
    'pillar.lucidity':        'Lucidité',
    'pillar.social':          'Social',
    'pillar.evolution':       'Croissance',
    // Interests / activities
    'interest.music':         'Musique',
    'interest.coffee':        'Café',
    'interest.climbing':      'Escalade',
    'interest.reading':       'Lecture',
    'interest.hiking':        'Randonnée',
    'interest.cooking':       'Cuisine',
    'interest.travel':        'Voyage',
    'interest.yoga':          'Yoga',
    'interest.cinema':        'Cinéma',
    'interest.art':           'Art',
    'interest.gaming':        'Jeux vidéo',
    'interest.running':       'Course à pied',
    'interest.cycling':       'Vélo',
    'interest.dancing':       'Danse',
    'interest.writing':       'Écriture',
    'interest.meditation':    'Méditation',
    'interest.volunteering':  'Bénévolat',
    'interest.wine':          'Vin',
    'interest.theater':       'Théâtre',
    'interest.photography2':  'Photographie',
    // Completion quiz step
    'completion.quiz_cta':    'Compléter le quiz de compatibilité',

    // HiveGroups
    'hive.group_interests_name':  'Intérêts communs',
    'hive.group_interests_desc':  "Basé sur ce que les membres listent comme intérêts",
    'hive.group_vibe_name':       'Ambiance communautaire',
    'hive.group_vibe_desc':       "Basé sur l'humeur et la façon dont les membres s'impliquent",
    'hive.group_lifestyle_name':  'Affinité de style de vie',
    'hive.group_values_name':     'Valeurs communes',
    'hive.group_compat_name':     'Haute compatibilité',
    'hive.group_compat_desc':     'Basé sur les scores de compatibilité du quiz',
    'hive.group_hood_name':       'Quartier',
    'hive.group_hood_desc':       'Basé sur le quartier ou le secteur de la ville',
    'hive.group_active_name':     'Actif & Plein air',
    'hive.group_active_desc':     'Randonneurs, grimpeurs, coureurs et amateurs de plein air',
    'hive.group_food_name':       'Gastronomie & Boissons',
    'hive.group_food_desc':       "Amateurs de cuisine, chefs maison, passionnés de vin",
    'hive.group_arts_name':       'Arts & Créativité',
    'hive.group_arts_desc':       "Photographes, musiciens, écrivains, designers et penseurs visuels",
    'hive.group_wellness_name':   'Bien-être & Pleine conscience',
    'hive.group_wellness_desc':   "Membres attirés par l'équilibre, la nature, la santé",
    'hive.group_curious_name':    'Esprits Curieux',
    'hive.group_curious_desc':    "Lecteurs, penseurs, créateurs, gamers et apprenants",
    'hive.group_night_name':      'Oiseaux de nuit',
    'hive.group_plant_name':      'Mode de vie végétal',
    'hive.group_plant_desc':      "Membres vivant une alimentation végétale, végane ou végétarienne",
    'hive.group_open_name':       'Ouvert & En exploration',
    'hive.group_french_name':     'Francophones',
    'hive.group_french_desc':     'Francophones et apprenants du français dans la communauté',
    'hive.group_highcompat_name': 'Haute Compatibilité',
    // Events
    'event.desc_premat':          'Pré-matchés par algorithme de compatibilité',
    'event.perk_verified':        'Membres vérifiés uniquement',
    'event.desc_cooking':         'Cuisinez ensemble, connectez naturellement',
    'event.perk_ingredients':     'Ingrédients fournis',
    'event.desc_vip':             'Expérience VIP pour membres Gold',
    'event.perk_openbar':         'Open bar toute la nuit',
    'event.perk_jazz':            'Trio de jazz live',
    // Safety report reasons
    'report.reason_fake':         'Faux profil ou photos',
    'report.reason_harassment':   'Harcèlement ou intimidation',
    'report.reason_content':      'Contenu inapproprié',
    'report.reason_spam':         'Spam ou arnaque',
    'report.reason_underage':     'Semble être mineur(e)',
    'report.reason_offensive':    'Langage ou photos offensants',
    'report.reason_other':        'Autre',
    // BioGenerator questions
    'bio.q1':                     'Ton samedi parfait ?',
    'bio.q2':                     "Une chose sur laquelle tu ne peux absolument pas faire de compromis ?",
    'bio.q3':                     'Quelque chose dont tu es discrètement fier/fière ?',
    'bio.q4':                     "Quel type de connexion espères-tu trouver ?",
    // Icebreakers
    'ice.q1':                     "Quelle est l'histoire derrière leur meilleure photo ?",
    'ice.q2':                     'À quoi ressemblerait leur samedi parfait ?',
    'ice.q3':                     "Demande-leur ce que leurs amis diraient être leur qualité la plus surprenante",
    'ice.q4':                     "Quelle est une chose que leur profil ne révèle pas ?",
    'ice.q5':                     "Quelle est la dernière chose qui les a fait rire aux éclats ?",
    'ice.q6':                     "Quel est leur repas réconfortant après une longue semaine ?",
    // CMS page titles
    'cms.how':                    'Comment ça marche',
    'cms.privacy':                'Politique de confidentialité',
    'cms.terms':                  "Conditions d'utilisation",
    'cms.safety':                 'Sécurité',
    'cms.contact':                'Contact',
    'cms.pricing':                'Tarifs',
    'cms.help':                   "Centre d'aide",
    'cms.press':                  'Presse & Médias',
    'cms.careers':                'Carrières',
    // BeeJourney stages
    'journey.s1_desc':            'Tu as rejoint la ruche. Ton aventure commence.',
    'journey.s1_reward':          'Profil visible par la communauté',
    'journey.s3_reward':          'Badge Friend Score débloqué',
    'journey.s5_reward':          'Placement prioritaire dans Découvrir',
    // BeeloveLogo tagline
    'logo.tagline':               "L'APP DE RENCONTRES SOCIALE",

    // Lifestyle section labels
    'life.drinking':      'Alcool',
    'life.smoking':       'Tabac',
    'life.cannabis':      'Cannabis',
    'life.exercise':      'Sport',
    'life.diet':          'Alimentation',
    'life.pets':          'Animaux',
    'life.love_lang':     "Langage d'amour",
    'life.sleep':         'Sommeil',
    'life.religion':      'Religion',
    'life.politics':      'Politique',
    'life.education':     'Études',
    'life.goal':          'Je cherche',
    // Section headers
    'section.prompts':    'Prompts',
    'section.interests':  "Centres d'intérêt",
    'section.lifestyle':  'Mode de vie',
    'section.values':     'Valeurs',
    'section.about':      'À propos',
    'section.basics':     'Informations de base',
    'section.from_friends': 'De la part de ses amis',
    // Drink/smoke/exercise values
    'val.never':          'Jamais',
    'val.rarely':         'Rarement',
    'val.socially':       'Socialement',
    'val.regularly':      'Régulièrement',
    'val.daily':          'Quotidiennement',
    'val.sometimes':      'Parfois',
    'val.often':          'Souvent',
    // Education values
    'val.bachelors':      'Baccalauréat',
    'val.masters':        'Maîtrise',
    'val.phd':            'Doctorat',
    'val.trade':          'Formation professionnelle',
    // Goal values
    'val.relationship':   'Relation sérieuse',
    'val.casual':         'Quelque chose de léger',
    'val.marriage':       'Mariage',
    'val.friendship':     "D'abord des amis",
    // Pet values
    'val.cat':            'Chat 🐱',
    'val.dog':            'Chien 🐶',
    'val.both_pets':      'Les deux 🐾',
    'val.no_pets':        "Pas d'animaux",
    'val.allergic':       'Allergique',
    // Love language short
    'val.words':          "Mots d'affirmation",
    'val.acts':           'Actes de service',
    'val.gifts':          'Cadeaux',
    'val.quality':        'Temps de qualité',
    'val.touch':          'Contact physique',
    // Sleep
    'val.early_bird':     'Lève-tôt',
    'val.night_owl':      'Couche-tard',
    'val.flexible':       'Flexible',
    // Verified badge label
    'val.verified':       'Vérifié(e) ✓',

    // Matches compatibility section headers
    'matches.compat_section':     'Compatibilité',
    'matches.how_relate':         'Comment ils/elles interagissent',
    'matches.who_they_are':       'Qui ils/elles sont',
    // Matches axis full labels
    'matches.axis_repair':        'Réparation',
    'matches.axis_conflict3':     'Conflit',
    'matches.axis_account2':      'Responsabilité',
    'matches.axis_generosity2':   'Générosité',
    'matches.axis_presence2':     'Présence',
    'matches.axis_narrative':     'Identité narrative',
    'matches.axis_boredom3':      'Ennui',
    'matches.axis_stakes2':       'Enjeux',
    'matches.axis_social2':       'Social',
    // Discover card labels
    'discover.love_language_label': "Langage d'amour",
    'discover.looking_for_label': 'Je cherche',
    'discover.pass_btn2':         'Passer',
    // Profile tab labels
    'profile.tab_about2':         'À propos',
    // Upgrade feature short labels
    'upgrade.feat_see_liked2':    "Voir qui t'a liké",
    'upgrade.feat_invites2':      'Invitations illimitées',
    'upgrade.feat_boost2':        'Boost hebdomadaire',
    'upgrade.discover_cta':       'Découvrir des profils',
    // Completion banner short
    'completion.boost_note2':     'Les profils à plus de 75% obtiennent 5× plus de matchs.',
    // Networks
    'network.lorient':            'Lorient',
    'network.lorient_area':       'Lorient & Bretagne sud',

    // Scoring axis labels
    'axis.narrative':      'Identité narrative',
    'axis.stability':      'Stabilité identitaire',
    'axis.boredom':        "Tolérance à l'ennui",
    'axis.social':         'Énergie sociale',
    'axis.stakes':         'Tolérance aux enjeux',
    'axis.repair':         'Vitesse de réparation',
    'axis.conflict':       'Style de conflit',
    'axis.accountability': 'Responsabilité',
    'axis.generosity':     'Générosité',
    'axis.presence':       'Présence',
    // Scoring axis descs
    'axis.narrative_d':    "Assument-ils leur part dans les conflits passés ?",
    'axis.stability_d':    "Sont-ils la même personne selon les contextes ?",
    'axis.boredom_d':      "Ont-ils besoin de stimulation constante ?",
    'axis.social_d':       "Les foules les épuisent-ils ou les énergisent-ils ?",
    'axis.stakes_d':       "Sont-ils paralysés par l'incertitude ?",
    'axis.repair_d':       "Laissent-ils fermenter le conflit ou le réparent-ils vite ?",
    'axis.conflict_d':     "Se ferment-ils ou s'engagent-ils directement ?",
    'axis.account_d':      "Assument-ils leurs erreurs ou les dévient-ils ?",
    'axis.generosity_d':   "Donnent-ils autant qu'ils prennent ?",
    'axis.presence_d':     "Sont-ils pleinement présents ou à moitié distraits ?",
    // Layer labels
    'axis.layer_a':        'Qui ils/elles sont',
    'axis.layer_b':        'Comment ils/elles interagissent',

    // BeeJourney moods
    'mood.growing':       'En croissance',
    'mood.exploring':     'En exploration',
    'mood.ready':         'Prêt(e)',
    'mood.connecting':    'En connexion',
    'mood.low_key':       'Discret(e)',
    // BeeJourney roles
    'role.elder':         'Aîné(e) de la Ruche',
    'role.pillar':        'Pilier de la Ruche',
    'role.member':        'Membre de la Ruche',
    'role.new_bee':       'Nouvelle Abeille',
    // Quiz categories
    'quiz.cat_identity':  'Identité',
    'quiz.cat_lifestyle': 'Mode de vie',
    'quiz.cat_relational':'Relationnel',
    // Event types
    'event.type_speed':   'Speed Dating',
    'event.type_apero':   'Apéro réseau',
    'event.type_theme':   'Soirée thème',
    'event.type_vip':     'VIP',
    'event.type_partner': 'Partenaire',
    'event.type_outdoor': 'Plein air',
    // PremiumModal perks
    'perk.see_liked':     "Voir qui t'a liké",
    'perk.see_liked_sub': "Plus de doutes — vois chaque profil qui t'a noté ou liké.",
    'perk.invites':       "Invitations d'amis illimitées",
    'perk.invites_sub':   "Invite autant d'amis que tu veux pour noter ton profil.",
    'perk.boost':         'Boost de visibilité hebdomadaire',
    'perk.boost_sub':     'Ton profil est poussé en tête du fil Découvrir une fois par semaine.',
    'perk.priority':      'Matching prioritaire',
    'perk.priority_sub':  'Sois matché(e) en premier avec les profils très compatibles.',
    'perk.receipts':      'Accusés de lecture',
    'perk.receipts_sub':  'Sache quand tes messages ont été lus.',
    // Moderation actions
    'mod.warn':           "Envoyer un avertissement",
    'mod.suspend':        'Suspendre 7 jours',
    'mod.ban':            'Bannissement permanent',
    'mod.dismiss':        'Rejeter le signalement',

    'common.email_label': 'E-mail',
    'common.sms_label': 'SMS',

    // Quiz questions — axis-named keys (1-to-1 avec les axes radar)
    // RELATIONNEL
    'quiz.repair':        "Après avoir blessé quelqu'un que tu aimes — même involontairement — tu\u2026",
    'quiz.repair_o1':     "Laisses du temps — la tension finit par se dissoudre d'elle-même",
    'quiz.repair_o2':     "Attends le bon moment, puis tu en parles",
    'quiz.repair_o3':     "Te manifestes assez vite pour clarifier les choses",
    'quiz.repair_o4':     "Règles ça immédiatement — tu ne peux pas te reposer en sachant que tu as fait de la peine",
    'quiz.conflict':      "Quand il y a un vrai désaccord avec quelqu'un que tu aimes, tu\u2026",
    'quiz.conflict_o1':   "Te tais et espères que ça se résout tout seul",
    'quiz.conflict_o2':   "As besoin de temps seul(e) d'abord, puis tu peux en parler",
    'quiz.conflict_o3':   "Prends une pause, puis tu l'amènes calmement",
    'quiz.conflict_o4':   "L'abordes directement dès que possible",
    'quiz.account':       "Quand quelque chose se passe mal dans une relation à cause de toi, ta première réaction est de\u2026",
    'quiz.account_o1':    "Regarder ce que la situation ou l'autre personne a causé",
    'quiz.account_o2':    "Essayer de comprendre les deux côtés avant de prendre position",
    'quiz.account_o3':    "Réfléchir à ce que j'aurais pu faire différemment",
    'quiz.account_o4':    "Assumer ta part immédiatement — sans attendre d'être interpellé(e)",
    'quiz.generosity':    "Quand quelqu'un de proche traverse une période difficile, tu\u2026",
    'quiz.generosity_o1': "As du mal à savoir comment aider — exprimer de la sollicitude ne vient pas naturellement",
    'quiz.generosity_o2': "Lui laisses de l'espace sauf s'il/elle te demande du soutien",
    'quiz.generosity_o3': "Te manifestes, proposes ton aide et reprends régulièrement des nouvelles",
    'quiz.generosity_o4': "Laisses tout tomber et tu es là — être présent(e), c'est tout",
    'quiz.presence':      "Pendant une conversation qui compte vraiment pour toi, tu es\u2026",
    'quiz.presence_o1':   "Facilement distrait(e) — rester pleinement concentré(e) m'est difficile",
    'quiz.presence_o2':   "Souvent en train de faire autre chose — je peux quand même suivre",
    'quiz.presence_o3':   "Généralement présent(e), même si mon esprit s'échappe parfois",
    'quiz.presence_o4':   "Complètement là — téléphone rangé, tout le reste peut attendre",
    // IDENTITÉ
    'quiz.narrative':     "Quand une relation passée s'est mal terminée, comment tu racontes cette histoire ?",
    'quiz.narrative_o1':  "J'ai eu de la malchance — l'autre personne était le problème",
    'quiz.narrative_o2':  "Ils/Elles avaient des problèmes que je ne pouvais pas régler — j'ai fait de mon mieux",
    'quiz.narrative_o3':  "C'était compliqué — on avait tous les deux une part de responsabilité",
    'quiz.narrative_o4':  "C'était surtout de ma faute — je sais où j'ai eu tort",
    'quiz.stability':     "Est-ce que ta personnalité change beaucoup selon avec qui tu es ?",
    'quiz.stability_o1':  "Je suis très différent(e) selon le groupe",
    'quiz.stability_o2':  "Je change pas mal — je lis l'ambiance et m'adapte",
    'quiz.stability_o3':  "J'adapte mon ton mais reste fondamentalement moi-même",
    'quiz.stability_o4':  "À peu près la même personne dans toutes les situations",
    'quiz.boredom':       "Un samedi complètement vide, sans aucun plan. Tu te sens\u2026",
    'quiz.boredom_o1':    "Anxieux/se — j'ai besoin de structure pour me sentir bien",
    'quiz.boredom_o2':    "Agité(e) — j'ai besoin d'au moins une chose à faire",
    'quiz.boredom_o3':    "Bien — je vais probablement texter quelqu'un",
    'quiz.boredom_o4':    "Heureux/se — je vais remplir ça comme je veux",
    'quiz.social':        "Après un grand événement social avec beaucoup de monde, tu te sens\u2026",
    'quiz.social_o1':     "Épuisé(e) — il me faut une journée entière de calme pour récupérer",
    'quiz.social_o2':     "Vidé(e) — j'ai besoin de quelques heures seul(e) pour recharger",
    'quiz.social_o3':     "Content(e) mais prêt(e) pour une fin de soirée plus calme",
    'quiz.social_o4':     "Énergisé(e) — je pourrais continuer toute la nuit",
    'quiz.stakes':        "Un grand changement de vie t'arrive avec 48h de préavis. Tu\u2026",
    'quiz.stakes_o1':     "Te sens paralysé(e) — le changement sans préparation m'est très difficile",
    'quiz.stakes_o2':     "Te sens anxieux/se — j'ai besoin de temps pour traiter avant d'agir",
    'quiz.stakes_o3':     "Te sens stressé(e) mais tu gères étape par étape",
    'quiz.stakes_o4':     "Te sens excité(e) — je m'épanouis dans le changement",

    // Legacy numbered quiz keys (rétrocompatibilité)
    'quiz.q1':            "Quand une relation passée s'est mal terminée, comment racontes-tu généralement cette histoire ?",
    'quiz.q1_o1':         "C'était surtout de ma faute — je sais où j'ai eu tort",
    'quiz.q1_o2':         "C'était compliqué — on avait tous les deux une part de responsabilité",
    'quiz.q1_o3':         "Ils/Elles avaient des problèmes que je ne pouvais pas régler — j'ai fait de mon mieux",
    'quiz.q1_o4':         "J'ai eu de la malchance — l'autre personne était le problème",
    'quiz.q2':            'Un samedi complètement vide, sans aucun plan. Tu te sens…',
    'quiz.q2_o1':         "En paix — je vais remplir ça comme je veux",
    'quiz.q2_o2':         "Bien, mais je vais probablement texter quelqu'un",
    'quiz.q2_o3':         "Un peu agité(e) — j'ai besoin d'au moins une chose à faire",
    'quiz.q2_o4':         "Anxieux/se — j'ai besoin de structure pour me sentir bien",
    'quiz.q3':            'Après un grand événement social avec beaucoup de monde, tu te sens…',
    'quiz.q3_o1':         "Énergisé(e) — je pourrais continuer toute la nuit",
    'quiz.q3_o2':         "Content(e) mais prêt(e) pour une fin de soirée plus calme",
    'quiz.q3_o3':         "Vidé(e) — j'ai besoin de quelques heures seul(e) pour recharger",
    'quiz.q3_o4':         "Épuisé(e) — il me faut une journée entière de rien pour récupérer",
    'quiz.q4':            "Est-ce que ta personnalité change beaucoup selon avec qui tu es ?",
    'quiz.q4_o1':         "Je suis à peu près le/la même dans n'importe quelle situation",
    'quiz.q4_o2':         "J'adapte mon ton mais reste fondamentalement moi-même",
    'quiz.q4_o3':         "Je change pas mal — je lis l'ambiance et m'adapte",
    'quiz.q4_o4':         "Je suis une personne différente selon les contextes",
    'quiz.q5':            "Un grand changement de vie arrive avec 48h de préavis. Tu…",
    'quiz.q5_o1':         "Te sens excité(e) — je m'épanouis dans le changement",
    'quiz.q5_o2':         "Te sens stressé(e) mais tu gères étape par étape",
    'quiz.q5_o3':         "Te sens anxieux/se — j'ai besoin de temps pour traiter avant d'agir",
    'quiz.q5_o4':         "Te sens paralysé(e) — le changement sans préparation est très difficile",
    'quiz.q6':            'Les enfants — quelle est ta position ?',
    'quiz.q6_o1':         "Je veux des enfants et c'est une priorité pour moi",
    'quiz.q6_o2':         "J'aimerais des enfants mais ce n'est pas un critère éliminatoire",
    'quiz.q6_o3':         "Ouvert(e) si la bonne personne se présente",
    'quiz.q6_o4':         "Je ne veux pas d'enfants",
    'quiz.q7':            'Ton rythme quotidien naturel ?',
    'quiz.q7_o1':         "Lève-tôt — je suis au mieux avant midi",
    'quiz.q7_o2':         "Plutôt matinal(e) mais flexible",
    'quiz.q7_o3':         "Couche-tard — je m'anime le soir",
    'quiz.q7_o4':         "Imprévisible — ça change constamment",
    'quiz.q8':            "Où te vois-tu dans 5 ans ?",
    'quiz.q8_o1':         "J'ai un plan clair et je travaille vers lui",
    'quiz.q8_o2':         "J'ai une direction mais reste ouvert(e) au changement",
    'quiz.q8_o3':         "J'ai des idées mais rien de concret",
    'quiz.q8_o4':         "Je préfère voir où la vie me mène",
    'quiz.q9':            "Quand il y a un vrai désaccord avec quelqu'un que tu aimes, tu…",
    'quiz.q9_o1':         "L'abordes directement dès que possible",
    'quiz.q9_o2':         "Prends une pause, puis l'amènes calmement",
    'quiz.q9_o3':         "As besoin de temps pour réfléchir avant de dire quoi que ce soit",
    'quiz.q9_o4':         "Tends à te taire en espérant que ça se résout tout seul",
    'quiz.q10':           'À quoi ressemble ta relation idéale au quotidien ?',
    'quiz.q10_o1':        "Très imbriquée — routines communes, décisions communes",
    'quiz.q10_o2':        "Proche mais avec notre propre espace et nos propres projets",
    'quiz.q10_o3':        "Deux personnes indépendantes qui se choisissent chaque jour",
    'quiz.q10_o4':        "Je le découvre encore",
    // FriendRating criteria
    'fr.repair_q':        "Après un conflit, à quelle vitesse cherche-t-il/elle à réparer les choses ?",
    'fr.repair_lo':       "Laisse mijoter pendant des jours",
    'fr.repair_hi':       "Répare en quelques heures",
    'fr.conflict_q':      "Face à un vrai désaccord, que fait-il/elle typiquement ?",
    'fr.conflict_lo':     "Se ferme ou évite",
    'fr.conflict_hi':     "S'engage directement mais équitablement",
    'fr.account_q':       "Quand quelque chose tourne mal à cause de lui/elle, que se passe-t-il ?",
    'fr.account_lo':      "Dévie ou blâme les autres",
    'fr.account_hi':      "Assume immédiatement",
    'fr.generosity_q':    "Dans les amitiés et relations, comment est l'équilibre donner/recevoir ?",
    'fr.generosity_lo':   "Prend plus qu'il/elle ne donne",
    'fr.generosity_hi':   "Donne autant qu'il/elle reçoit",
    'fr.presence_q':      "Quand vous êtes ensemble, à quel point est-il/elle mentalement présent(e) ?",
    'fr.presence_lo':     "Souvent distrait(e), à moitié là",
    'fr.presence_hi':     "Pleinement concentré(e) et engagé(e)",
    'fr.narrative_q':     "Quand il/elle parle de conflits passés, assume-t-il/elle sa part ?",
    'fr.narrative_lo':    "Toujours le héros ou la victime",
    'fr.narrative_hi':    "Reconnaît honnêtement son rôle",
    'fr.stability_q':     "Est-il/elle la même personne quel que soit l'entourage ?",
    'fr.stability_lo':    "Très différent(e) selon les contextes",
    'fr.stability_hi':    "Cohérent(e) dans toutes les situations",
    'fr.boredom_q':       "À quel point est-il/elle à l'aise avec un emploi du temps vide ?",
    'fr.boredom_lo':      "A besoin de stimulation constante",
    'fr.boredom_hi':      "Parfaitement à l'aise à ne rien faire",
    'fr.social_q':        "Après un long événement social, de quoi a-t-il/elle besoin ?",
    'fr.social_lo':       "D'un isolement total pour récupérer",
    'fr.social_hi':       "Il/Elle est encore énergisé(e), veut continuer",
    'fr.stakes_q':        "Face à une décision majeure incertaine, comment gère-t-il/elle ça ?",
    'fr.stakes_lo':       "Paralysé(e), a besoin que tout soit planifié",
    'fr.stakes_hi':       "Calme et décisif/ve sous pression",
    // BeeJourney stage labels
    'journey.s1_label':   'Bee Enregistré(e)',
    'journey.s2_label':   'Bee Honnête',
    'journey.s3_label':   'Bee Endossé(e)',
    'journey.s4_label':   'Bee Connecté(e)',
    'journey.s5_label':   'Bee Loved',
    // BeeWise read time
    'beewise.min_read':   '{n} min de lecture',
    // Dynamic year
    'common.year':        '2025',
    // Auth BEE_WORDS
    'auth.bee_word1':     'Bee Loved',
    'auth.bee_word2':     'Bee Patient(e)',
    'auth.bee_word3':     'Bee Authentique',
    'auth.bee_word4':     'Bee Vu(e)',
    'auth.bee_word5':     'Bee Courageux/se',
    'auth.bee_word6':     "Bee L'élu(e)",
    // Events city
    'events.city_label':  'Montréal',

    // Additional interests
    'interest.vinyl': 'Vinyle',
    'interest.games': 'Jeux',
    'interest.live_music': 'Musique live',
    'interest.surfing': 'Surf',
    'interest.indie_rock': 'Indie rock',
    'interest.alpinism': 'Alpinisme',
    'interest.skiing': 'Ski',
    'interest.sunrise': 'Lever du soleil',
    'interest.herbal_tea': 'Tisane',
    'interest.indie_music': 'Musique indie',
    'interest.law': 'Droit',
    'interest.african_cuisine': 'Cuisine africaine',
    'interest.activism': 'Activisme',
    'interest.organic_farming': 'Agriculture bio',
    'interest.dogs': 'Chiens',
    'interest.folk_music': 'Musique folk',
    'interest.space': 'Espace',
    'interest.astronomy': 'Astronomie',
    'interest.gym': 'Gym',
    'interest.architecture': 'Architecture',
    'interest.urban_photo': 'Photo urbaine',
    'interest.theatre': 'Théâtre',
    'interest.crossfit': 'CrossFit',
    'interest.nutrition': 'Nutrition',
    'interest.true_crime': 'True crime',
    'interest.cats': 'Chats',
    'interest.novels': 'Romans',
    'interest.nomadic': 'Vie nomade',
    'interest.techno': 'Techno',
    'interest.tech': 'Tech',
    'interest.anime': 'Anime',
    'interest.spicy_food': 'Cuisine épicée',
    'interest.makeup': 'Maquillage',
    'interest.bollywood': 'Bollywood',
    'interest.indian_cuisine': 'Cuisine indienne',
    'interest.electronic': 'Musique électronique',
    'interest.production': 'Production',
    'interest.street_food': 'Street food',
    'interest.contemporary_art': 'Art contemporain',
    'interest.champagne': 'Champagne',
    'interest.construction': 'Construction',
    'interest.urban_planning': 'Urbanisme',
    'interest.improv': 'Impro théâtrale',
    'interest.animals': 'Animaux',
    'interest.tea': 'Thé',
    'interest.ghibli': 'Studio Ghibli',
    'interest.chess': 'Échecs',
    'interest.philosophy': 'Philosophie',
    'interest.craft_beer': 'Bière artisanale',
    'interest.sailing': 'Voile',
    'interest.ocean': 'Océan',
    'interest.diving': 'Plongée',
    'interest.sunsets': 'Couchers de soleil',
    'interest.piano': 'Piano',
    'interest.asian_cuisine': 'Cuisine asiatique',
    'interest.children': 'Enfants',
    'interest.gardening': 'Jardinage',
    'interest.swimming': 'Natation',
    'interest.herbalism': 'Herboristerie',
    'interest.ai': 'IA',
    'interest.abstract_games': 'Jeux abstraits',
    'interest.ambient': 'Musique ambiante',
    'interest.literature': 'Littérature',
    'interest.natural_wine': 'Vin naturel',
    'interest.trail': 'Trail',
    'interest.series': 'Séries',
    'interest.conferences': 'Conférences',
    'interest.opera': 'Opéra',
    'interest.intellectual': 'Dîners intellectuels',
    'interest.triathlon': 'Triathlon',
    'interest.sushi': 'Sushi',
    'interest.comics': 'BD/Comics',
    'interest.rpg': 'JDR',
    'interest.stargazing': 'Astronomie amateur',
    'interest.motorcycles': 'Motos',
    'interest.mechanics': 'Mécanique',
    'interest.rock': 'Rock',
    'interest.performance': 'Performance',
    'interest.fashion': 'Mode',
    'interest.queer': 'Culture queer',
    'interest.farming': 'Agriculture',
    'interest.livestock': 'Élevage',
    'interest.countryside': 'Campagne',
    'interest.pastry': 'Pâtisserie',
    'interest.japanese_tea': 'Thé japonais',
    'interest.japanese_culture': 'Culture japonaise',
    'interest.origami': 'Origami',
    'interest.punk': 'Punk',
    'interest.printmaking': 'Sérigraphie',
    'interest.paris_night': 'Paris by night',
    'interest.concerts': 'Concerts',
    'interest.naturopathy': 'Naturopathie',
    'interest.gastronomy': 'Gastronomie',
    'interest.sun': 'Soleil',
    'interest.veganism': 'Véganism',
    'interest.trees': 'Arbres',
    'interest.camping': 'Camping',
    'interest.ecology': 'Écologie',
    'interest.data': 'Data',
    'interest.board_games': 'Jeux de société',
    'interest.ramen': 'Ramen',
    'interest.circus': 'Cirque',
    'interest.acrobatics': 'Acrobatie',
    'interest.performing_arts': 'Arts du spectacle',
    'interest.navigation': 'Navigation',
    'interest.islands': 'Îles',
    'interest.meteorology': 'Météorologie',
    'interest.health2': 'Santé',
    'interest.brewing': 'Brassage maison',
    'interest.tattoo': 'Art du tatouage',
    'interest.metal': 'Métal',
    'interest.reptiles': 'Reptiles',
    'interest.goth': 'Culture goth',
    'interest.puzzles': 'Puzzles',
    'interest.press': 'Presse',
    'interest.cities': 'Villes',
    'interest.salsa': 'Salsa',
    'interest.history': 'Histoire',
    'interest.archives': 'Archives',
    'interest.trains': 'Trains',
    'interest.classical': 'Musique classique',
    'interest.mountains': 'Montagnes',
    'interest.archery': "Tir à l'arc",
    'interest.poker': 'Poker',
    'interest.pizza': 'Pizza',
    'interest.sports': 'Sports',
    'interest.podcasts': 'Podcasts',
    'interest.bbq': 'BBQ',
    'interest.fishing': 'Pêche',
    'interest.science': 'Science',
    'interest.nature': 'Nature',
    'interest.baking': 'Pâtisserie',
    'interest.dance': 'Danse',

    // Ratings tab
    'ratings.quality_breakdown':  'Évaluation détaillée',
    'ratings.based_on':           "Basé sur {n} évaluations d'amis · Les votes individuels ne sont jamais affichés",
    'ratings.unlock_compat':      'Débloquer les scores de compatibilité',
    'ratings.quiz_prompt':        'Passe le quiz de 3 min pour voir ton % de match avec chaque profil.',
    'ratings.start_btn':          'Commencer ✦',
    'ratings.who_rated':          "Qui t'a noté",
    'ratings.anonymous':          'Anonyme',
    'ratings.no_ratings':         "Pas encore de notes — invite des amis depuis l'onglet Inviter.",
    // Invite tab
    'invite.friends_who_rate':    "Amis qui te notent",
    'invite.get_more':            'Obtenir plus de notes',
    'invite.rate_me_msg':         'Note-moi sur BeeLove !',
    'invite.referral_title':      'Invite des amis et gagne des récompenses',
    'invite.referral_desc':       '2 amis rejoignent = 1 mois de Bee Honey offert.',
    'invite.how_it_works':        'Comment fonctionnent les évaluations',
    'invite.how_desc':            'Une fois que 5 amis ont complété le quiz, ton profil de compatibilité est révélé.',
    // Journey tab
    'journey.welcome':            'Bienvenue',
    'journey.progress':           'Ton parcours',
    'journey.stages_left':        '{n} restant(e)s',
    'journey.next_badge':         'Suivant',
    'journey.no_role':            'Aucun rôle encore',
    'journey.hive_score':         'Score de la Ruche',
    'journey.season_theme':       'Thème de la saison',
    'journey.weekly_q_title':     'Question de la semaine',
    'journey.weekly_visible':     'Visible sur ton profil pendant 7 jours',
    'journey.mood_title':         'Ton humeur cette semaine',
    'journey.share_answer':       'Partage ta réponse…',
    // Settings tab
    'settings.compat_quiz_title': 'Quiz de compatibilité',
    'settings.quiz_completed':    '✦ Complété',
    'settings.update_answers':    'Mettre à jour tes réponses',
    'settings.discovery':         'Découverte',
    'settings.max_distance':      'Distance max',
    'settings.age_range':         "Tranche d'âge",
    'settings.network_label2':    'Réseau',
    'settings.pause_title':       'Mettre en pause',
    'settings.pause_desc':        "Tu n'apparaîtras pas dans Découvrir. Tes matchs et messages sont conservés.",
    // Profile About tab
    'about.add_job':              'Ajouter ton métier',
    'about.height_label':         'Taille',
    'about.field_study':          "Domaine d'études",
    'about.school':               'École / Université',
    'about.job_title':            'Titre du poste',
    'about.languages':            'Langues',
    'about.zodiac':               'Zodiaque',
    'about.toujours_visible':     'Toujours visible',
    'about.friends_note':         "Plus tes amis répondent, plus ton profil devient riche et précis.",
    // Common visibility
    'common.visible':             'Visible',
    'common.hidden':              'Caché(e)',
    'common.everyone':            'Tout le monde',
    'common.anonymous':           'Anonyme',
    'common.quality_breakdown':   'Évaluation détaillée',

    // BeeJourney stage extra
    'journey.s2_desc': "Au moins 5 amis t'ont noté(e). La ruche te connaît.",
    'journey.s3_desc': 'Ton profil est complet — bio, prompts, et qui tu es vraiment.',
    'journey.s4_desc': 'Tu as matché et eu de vraies conversations.',
    'journey.s5_desc': 'Tu as trouvé quelque chose de vrai. ✦',

    // BeeJourney roles
    'role.scout':           'Éclaireur/se',
    'role.scout_desc':      "Tu explores et invites — toujours à la recherche de la prochaine belle connexion.",
    'role.builder':         'Bâtisseur/se',
    'role.builder_desc':    "Tu crées les conditions pour que les autres se connectent et s'épanouissent.",
    'role.connector':       'Connecteur/trice',
    'role.connector_desc':  "Tu rapproches les gens — les relations s'épanouissent autour de toi.",
    'role.queenbee':        'Reine des Abeilles',
    'role.queenbee_desc':   "Tu mènes par l'exemple — ta présence élève tout le monde dans la ruche.",
    'role.guardian':        'Gardien/ne',
    'role.elder2':          'Aîné(e)',

    // Testimonials
    'testimonial.1_quote':  "J'ai matché avec quelqu'un à 91% de compatibilité. On se disputait sur les mêmes livres, on détestait les mêmes choses, et on s'est fiancés six mois plus tard. BeeLove l'avait su avant nous.",
    'testimonial.1_role':   'Lyon · Membre depuis 2024',
    'testimonial.2_quote':  "Deux semaines sur BeeLove et j'ai rencontré quelqu'un qui s'est vraiment présenté. Le système de notation par les amis est génial — ça filtre le bruit instantanément.",
    'testimonial.2_role':   'Paris · Membre depuis 2024',
    'testimonial.3_quote':  "Je sais déjà ce que ses amis pensent de lui avant même de dire bonjour. Il y a quelque chose d'incroyablement rassurant dans ça. Ça a changé ma façon d'aborder chaque conversation.",
    'testimonial.3_role':   'Nantes · Membre depuis 2023',
    'testimonial.4_quote':  "Quand plus d'amis ont répondu au quiz, j'ai commencé à comprendre ce que les gens apprécient vraiment en moi. Ça a changé ma façon de rencontrer des gens, pas seulement sur BeeLove.",
    'testimonial.4_role':   'Lille · Membre depuis 2024',

    // Quiz remaining options
    'quiz.q3_o3b':        "Un peu vidé(e) — j'ai besoin de temps calme",
    'quiz.q3_o4b':        "Épuisé(e) — il me faut toute une journée seul(e)",
    'quiz.q4_o1b':        "Pas du tout — je suis la même personne partout",
    'quiz.q4_o3b':        "J'ai tendance à m'adapter — c'est plus facile",
    'quiz.q4_o4b':        "Je suis très différent(e) selon le groupe",
    'quiz.q5_o1b':        "Un élan d'excitation — le changement me stimule",
    'quiz.q5_o2b':        "Je m'adapte vite — je trouverai une solution",
    'quiz.q5_o4b':        "J'ai du mal — j'ai besoin de temps et d'un plan",
    'quiz.q6_o1b':        "Je veux des enfants — c'est une priorité",
    'quiz.q6_o3b':        "Indécis(e) — je verrai comment la vie se déroule",
    'quiz.q6_o4b':        "Je ne veux pas d'enfants — mon choix est fait",
    'quiz.q7_o1b':        "Lève-tôt — debout à 6h, productif/ve dès 7h",
    'quiz.q7_o3b':        "Couche-tard — je m'anime après 22h",
    'quiz.q7_o4b':        "Complètement chaotique — je n'ai pas d'horaire",
    'quiz.q8_city':       "Vie citadine — énergie, culture, tout à portée",
    'quiz.q8_suburbs':    "Banlieue — calme mais connecté(e)",
    'quiz.q8_rural':      "Petite ville ou campagne — espace et simplicité",
    'quiz.q8_abroad':     "À l'étranger — je veux vivre ailleurs",
    'quiz.q9_o1b':        "Immédiatement — je ne peux pas me reposer tant que c'est réglé",
    'quiz.q9_o3b':        "J'ai besoin d'espace d'abord — ensuite je peux en parler",
    'quiz.q10_o1b':       "Très imbriqués — on partage presque tout",
    'quiz.q10_o4b':       "Je le découvre encore — je le saurai quand je le sentirai",
    // Icebreakers templates
    'ice.t_interest1':    'Vous aimez tous les deux {interest} — demande-leur leur expérience préférée à Montréal ?',
    'ice.t_interest2':    "Tous les deux fans de {interest} ! Qu'est-ce qui a déclenché ça ?",
    'ice.t_interest3':    "Fan de {interest} comme toi — c'est quoi leur journée {interest} idéale ?",
    'ice.t_prompt1':      "Ils ont répondu '{prompt}' avec '{answer}' — qu'en penses-tu ?",
    'ice.t_prompt2':      "Leur prompt '{prompt}' m'a accroché — demande-leur d'en dire plus.",
    'ice.t_lifestyle1':   'Vous avez tous les deux ce mode de vie — parfait pour un premier rendez-vous ?',
    'ice.t_lifestyle2':   'Deux personnes similaires — comparez vos routines ?',
    'ice.t_mbti1':        "Tu es {myMbti} et il/elle est {theirMbti} — combo classique. Demande comment il/elle se ressource.",
    'ice.t_mbti2':        "Votre dynamique {myMbti} / {theirMbti} pourrait être intéressante — qu'en pense-t-il/elle ?",
    'ice.t_zodiac':       "Un(e) {myZodiac} et un(e) {theirZodiac} — que pense-t-il/elle de l'astrologie ?",
    'ice.t_lang':         "Vous parlez tous les deux {lang} — surprends-le/la avec un message en {lang} ?",
    'ice.t_age':          "Demande-leur la meilleure chose qu'ils aient faite à {age} ans.",
    'ice.t_local':        "Demande-leur : qu'est-ce que les Montréalais savent que les visiteurs ne découvrent jamais ?",
    'ice.t_dinner':       "S'ils pouvaient dîner avec n'importe qui — vivant ou non — qui serait-ce ?",
    // Engagement messages
    'engage.no_more_title':   "Tu es une belle découverte — reviens demain",
    'engage.no_more_sub':     "On ajoute de nouveaux profils chaque matin. Ton match parfait est peut-être en train de s'inscrire.",
    'engage.patient_title':   'Bee Patient(e) — les bonnes choses prennent du temps',
    'engage.patient_sub':     'Les meilleures relations commencent lentement. Élargis ta distance pour découvrir plus.',
    'engage.seen_all_title':  "Tu as vu tout le monde dans le coin",
    'engage.seen_all_sub':    "Essaie d'élargir tes filtres — la bonne personne est parfois juste un peu plus loin.",
    'engage.score_title':     'Ton score travaille pour toi',
    'engage.score_sub':       "Même ici, des gens voient ton profil. Quelqu'un est peut-être en train d'écrire.",
    // BeeHive stories
    'hive.story1':        "Deux Connecteurs se sont rencontrés pendant la Saison Connexion. Ils ont échangé plus de 340 messages.",
    'hive.story2':        "Un Éclaireur et un Bâtisseur ont découvert qu'ils vivaient à 3 pâtés de maisons et partageaient 8 intérêts.",
    'hive.story3':        "Deux Reines des Abeilles matchées à 94% de compatibilité. 'Présence' était leur trait commun.",
    'hive.story4':        "Un Bâtisseur a invité 7 amis. Trois sont maintenant membres actifs.",
    'hive.story5':        "Ils ont tous les deux répondu à la question de la semaine avec presque les mêmes mots.",
    'hive.story6':        "Tous deux en humeur 'Connexion', tous deux Bâtisseurs — ils se sont rencontrés pour un café en amis.",
    // BeeHive weekly questions
    'hive.wq1':           "Quelle est la chose la plus courageuse que tu aies jamais faite pour une connexion ?",
    'hive.wq2':           "Décris un moment où un inconnu a changé ta perspective.",
    'hive.wq3':           "Quelle est une chose que la plupart des gens ne savent pas sur toi et que tu aimerais qu'ils sachent ?",
    'hive.wq4':           "À quoi ressemble un matin parfait — dans les moindres détails ?",
    'hive.wq5':           "Quelle est la chose la plus spontanée que tu aies faite l'année dernière ?",
    'hive.wq6':           "Sur quoi as-tu complètement changé d'avis ?",
    'hive.wq7':           "Quelle énergie apportes-tu dans une pièce sans le vouloir ?",
    'hive.wq8':           "Quelle serait ta meilleure qualité selon ton/ta meilleur(e) ami(e) ?",
    // Discover
    'discover.compat_high':   '✦ Vous scorez tous les deux haut sur',
    'discover.super_like':    'Super like',
    'discover.write_direct':  'Écrire directement',
    'discover.view_profile':  'Voir le profil',
    'discover.report_btn':    'Signaler',
    'discover.report_title':  'Signaler ce profil',
    'discover.report_anon':   'Ton signalement est anonyme. Nous examinons chaque signalement sous 24h.',
    'discover.report_recv':   'Signalement reçu',
    'discover.verified_chip': '✓ Vérifié(e)',
    'discover.star_btn':      'STAR ★',
    'discover.prev_btn':      '← Précédent',
    // Matches
    'matches.expired':        'Expiré',
    'matches.high_on':        '✦ Vous scorez tous les deux haut sur',
    // Messages
    'messages.icebreakers':   '✦ Brise-glaces suggérés',
    'messages.ice_title':     'Suggestions de brise-glaces',
    // FriendRating
    'fr.greatest_quality':    'Sa plus grande qualité selon toi :',
    'fr.youll_rate':          "Tu vas l'évaluer sur",
    'fr.how_relate':          'Comment il/elle interagit',
    'fr.drives_score':        'Détermine le score de profil',
    'fr.who_are':             'Qui il/elle est',
    'fr.drives_compat':       'Détermine la compatibilité',
    'fr.your_name':           'Ton prénom (optionnel — reste anonyme pour {name})',
    'fr.be_honest':           'Bee Honnête',
    'fr.tap_number':          "Appuie sur un chiffre. Sois honnête — ça compte plus que tu ne le penses.",
    'fr.overall_rating':      'Ton évaluation globale',
    'fr.optional':            '(optionnel)',
    'fr.example_review':      'La personne la plus authentique que je connaisse — toujours là.',
    // BeeHive page
    'beehive.unlocked':       '✓ débloqué',
    'beehive.next':           '↑ suivant',
    'beehive.weekly_visible': 'Visible sur ton profil pendant 7 jours',
    'beehive.stories_title':  'Histoires de la ruche',
    'beehive.your_stages':    'Tes étapes',
    'beehive.bee_mood':       'Bee Humeur',
    'beehive.mood_visible':   'Visible sur ta carte, tes matchs et dans les messages.',
    'beehive.bee_role':       'Bee Rôle',
    'beehive.role_desc':      'Ton rôle se révèle au fil de ton engagement.',
    'beehive.role_list':      'Éclaireur · Bâtisseur · Connecteur · Reine',
    'beehive.hive_score':     'Score de la Ruche',
    'beehive.score_desc':     "Gagné grâce à un engagement sincère. Ne diminue jamais.",
    'beehive.bee_wise':       'Bee Sage',
    'beehive.wise_sub':       "Conseils sur l'amour, la communication & la connexion",
    'beehive.current_season': 'Saison actuelle',
    'beehive.season_boost':   'Les profils compatibles gagnent en visibilité',
    'beehive.hive_week':      'La Ruche cette semaine',
    // Profile remaining
    'profile.main_photo':     'Principale',
    'profile.bio_prompts':    'Réponds à quelques prompts pour une meilleure bio :',
    'profile.friend_score':   'Score Amis',
    'profile.your_profile':   'Ton profil',
    'profile.answer_ph':      'Ta réponse…',
    'profile.answer_all':     'Réponds à tout pour sauvegarder',
    'profile.blocked_users':  'Utilisateurs bloqués',
    'profile.invite_friends': 'Inviter des amis sur BeeLove 🐝',
    'profile.prive':          'privé',
    'profile.upgrade_honey':  'Bee Honey',
    'profile.upgrade_perks':  "Voir qui t'a liké · Invitations illimitées · Boost hebdo",
    'profile.upgrade_cta':    'Passer à la version sup — 7 jours gratuits',
    // Upgrade
    'upgrade.free_label':     'Gratuit',
    'upgrade.free_label2':    'Gratuit',
    'upgrade.cad_mo':         'CAD/mois',
    'upgrade.dating_game':    'app de rencontres',
    'upgrade.one_time':       "Achats uniques et options mensuelles. Cumulables avec n'importe quel forfait.",
    // Events
    'events.export':          'Exporter :',
    // Navbar
    'nav.more_cities':        "D'autres villes bientôt. Les réseaux te connectent avec des gens près de toi.",
    'nav.display_settings':   "Paramètres d'affichage",
    'nav.bee_the_one':        "Bee L'élu(e) ✦",
    // ReportBlock
    'report.block_note':      "Il/elle ne pourra pas te voir ni te contacter",
    'report.whats_going':     "Que se passe-t-il ? Ton signalement est anonyme et aide à garder BeeLove sûr.",
    'report.blocked_note':    "Il/elle ne pourra pas voir ton profil, t'envoyer des messages, ni apparaître dans Découvrir.",
    'report.title':           'Signaler ou bloquer',
    // RadarChart
    'radar.how_relate':       'Comment ils interagissent',
    'radar.who_are':          'Qui ils sont',
    // Icons
    'icons.score_conf':       'Indice de confiance :',
    // Auth
    'auth.bee_loved':         'Bee Loved',
    // StaticPage / Landing brand
    'static.bee_loved':       'Bee Loved',
    'landing.bee_loved_h':    'Bee Loved',
    'landing.bee_royal':      'Bee Royal',
    'landing.here':           'Ici',
    'landing.honey_plan':     'Honey',
    'landing.royal_plan':     'Royal',
    'landing.bee_the_one':    "Bee L'élu(e) ✦",
    // AppContext
    'conv.listener':          'Super auditeur/trice !',
    'conv.reliable':          'Tellement fiable',

    // Additional interests batch 2
    'interest.literary_cafes': 'Cafés littéraires',
    'interest.geology': 'Géologie',
    'interest.herbal_infusions': 'Infusions',
    'interest.plants': 'Plantes',
    'interest.forest': 'Forest',

    // BeeHive page titles
    'beehive.title':          'La Ruche',
    'beehive.all_groups':     'Tous les groupes',

    // Journey/Profile/Settings missing strings
    'journey.register_btn': "S'inscrire",
    'journey.discover_cta': 'Découvrir des profils',
    'journey.weekly_q_current': "Sur quoi as-tu complètement changé d'avis ?",
    'about.public_info_note': 'Ces informations apparaissent sur ton profil public. Plus tu partages, meilleurs sont tes matchs.',
    'about.comp_science': 'Informatique',
    'about.relationship_label': 'Relation sérieuse',
    'invite.compat_active': "Ton profil de compatibilité est actif. Plus d'amis = insights plus riches.",
    'invite.anon_note': 'Leurs réponses sont agrégées anonymement — les réponses individuelles ne sont jamais affichées',
    'invite.reveal_note': 'Une fois que 5 amis ont complété le quiz, ton profil de compatibilité est révélé',
    'invite.more_friends_note': 'Plus tes amis répondent, plus ton profil devient riche et précis',
    'journey.s3_desc_full': 'Ton profil est complet — bio, prompts, et qui tu es vraiment.',
    'journey.s4_desc_full': 'Tu as matché et eu une vraie conversation — 10+ messages.',
    'journey.s5_friend_desc': "Amis ont rejoint BeeLove via ton lien d'invitation.",
    'journey.s6_desc': "La ruche t'aime en retour — top score, super likes ou match exceptionnel.",
    'role.hive_member': 'Membre de la Ruche',
    'settings.more_networks': "D'autres réseaux bientôt — Toronto, Vancouver, Paris et plus.",
    'settings.pause_desc2': "Ton profil n'apparaîtra pas dans Découvrir en pause. Tes matchs et messages sont conservés.",
    'settings.active_network': 'Ton réseau actif',
    'journey.s6_label': 'Bee Loved',
    'edu.computer_science': 'Informatique',
    'edu.bachelors2': 'Baccalauréat',

    // BeeJourney remaining stage rewards/descs
    'journey.s4_reward':      'Radar de compatibilité débloqué pour tous les matchs',
    'journey.s5_hive_label':  'Bee Ruche',
    'journey.s5_hive_desc':   "3 amis ont rejoint BeeLove via ton lien d'invitation.",
    'journey.s5_hive_reward': 'Rôle Bâtisseur débloqué + badge Ruche permanent',
    'journey.s6_reward':      'Statut Reine/Roi des Abeilles + cadre de profil doré',
    // Profile About
    'about.public_info':      "Ces informations apparaissent sur ton profil public. Plus tu partages, meilleurs sont tes matchs.",
    'about.languages_val':    'Français, Anglais',
    // Settings network
    'settings.active_network2': 'Ton réseau actif',
    'settings.pause_note':    "Ton profil n'apparaîtra pas dans Découvrir en pause. Tes matchs et messages sont conservés.",
    // Invite remaining
    'invite.anon_agg':        'Leurs réponses sont agrégées anonymement — les réponses individuelles ne sont jamais affichées',
    'invite.reveal_5':        'Une fois que 5 amis ont complété le quiz, ton profil de compatibilité est révélé',
    // Weekly question current
    'journey.this_weeks_q':   'Question de la semaine',
    // Register button
    'auth.register':          "S'inscrire",
    // Discover CTA in journey
    'profile.discover_cta':   'Découvrir des profils',

    // Match overlay popup
    'match.its_a_match':      "✦ C'est un Match !",
    'match.liked_each_other': 'Toi et {name} vous vous êtes likés mutuellement.',
    'match.send_message':     'Envoyer un message',
    'match.keep_exploring':   'Continuer à explorer',
    'match.compatibility':    'Compatibilité',
    // Quiz category labels
    'quiz.cat_label_identity':  'Identité',
    'quiz.cat_label_lifestyle': 'Mode de vie',
    'quiz.cat_label_relational':'Relationnel',

    'discover.quiz_left_1':   'Seulement ',
    'discover.quiz_left_2':   ' question{plural} restante{plural} — moins de 2 minutes.',
    'discover.report_thanks': "Merci de protéger BeeLove. Nous examinerons ce profil sous 24h.",
    'match.view_profile':     'Voir le profil',

    // Quiz interstitial popup
    'quiz.modal_title':       'BeeLove — Compatibilité',
    'quiz.match_prompt':      "Tu viens d'avoir un match ! Dis-nous en plus sur toi pour débloquer les scores de compatibilité.",
    'quiz.resume_prompt':     "✦ Tu veux améliorer ton score ? Continue là où tu t'étais arrêté(e).",
    'quiz.skip_now2':         "Passer pour l'instant",

    // Events — Past/Upcoming tabs + QR
    'events.tab_upcoming':    'À venir',
    'events.tab_past':        'Passés',
    'events.qr_title':        'Ton billet',
    'events.qr_registered':   'Inscrit(e)',
    'events.qr_show':         "Montre ce QR code à l'entrée",
    'events.qr_validated':    '✓ Présence confirmée',
    'events.attendees_title': 'Qui était là',
    'events.attendees_sub':   'Personnes présentes — visible uniquement aux participants',
    'events.no_past':         "Tu n'as encore assisté à aucun événement",
    'events.attended_badge':  '✓ Présent(e)',
    'events.capacity_label':  '{n} présences confirmées',
    'events.you_attended':    'Tu étais là',
    'events.scan_note':       "Scanner à l'entrée pour confirmer ta présence",

    // CML dashboard
    'cml.title':              'Gestionnaire de communauté',
    'cml.subtitle':           'Tableau de bord de ton réseau local',
    'cml.network_label':      'Réseau',
    'cml.stage_ambassador':   'Ambassadeur/rice',
    'cml.stage_coordinator':  'Coordinateur/rice',
    'cml.stage_director':     'Directeur/rice local(e)',
    'cml.revenue_month':      'Revenus ce mois',
    'cml.revenue_total':      'Total gagné',
    'cml.revenue_pending':    'En attente',
    'cml.members_active':     'Membres actifs',
    'cml.members_new':        'Nouveaux ce mois',
    'cml.events_hosted':      'Événements organisés',
    'cml.next_payout':        'Prochain versement',
    'cml.my_events':          'Mes événements',
    'cml.new_event':          'Nouvel événement',
    'cml.sponsors_title':     'Sponsors',
    'cml.add_sponsor':        'Ajouter un sponsor',
    'cml.notif_title':        'Envoyer une notification',
    'cml.notif_compose':      'Rédiger le message',
    'cml.notif_send':         'Envoyer à {n} membres',
    'cml.notif_sent':         '✓ Envoyé !',
    'cml.stats_title':        'Stats du réseau',
    'cml.stats_members':      'Membres',
    'cml.stats_retention':    'Rétention',
    'cml.stats_avg_score':    'Score moyen',
    'cml.stats_matches':      'Matchs créés',
    'cml.stats_events':       'Événements total',
    'cml.sponsor_prospect':   'Prospect',
    'cml.sponsor_contacted':  'Contacté',
    'cml.sponsor_signed':     'Signé',
    'cml.sponsor_active':     'Actif',
    'cml.budget_request':     'Demande de budget',
    'cml.budget_sent':        'Demande envoyée',
    'cml.rev_events':         'Événements',
    'cml.rev_sponsors':       'Sponsoring',
    'cml.rev_subscriptions':  'Abonnements',
    'cml.rev_alacarte':       'À la carte',
    'cml.breakdown':          'Répartition des revenus',
    'cml.qr_scanner':         'Scanner QR',
    'cml.qr_scan_prompt':     "Scanne le billet QR d\'un membre",
    'cml.qr_validated':       '✓ Présence confirmée',
    'cml.qr_invalid':         '✗ QR code invalide',
    'cml.members_present':    '{n} membres présence confirmée',

    // Payment flow
    'payment.title':          'Paiement sécurisé',
    'payment.card_info':      'Informations de carte',
    'payment.name_on_card':   'Nom sur la carte',
    'payment.email_label':    'Email pour la confirmation',
    'payment.pay_now':        'Payer {amount}',
    'payment.processing':     'Traitement en cours...',
    'payment.success':        '✓ Paiement confirmé',
    'payment.failed':         'Paiement refusé — réessaie',
    'payment.total':          'Total',
    'payment.secure':         'Sécurisé par Stripe · SSL 256 bits',
    'payment.refund_policy':  "Remboursement intégral jusqu'à 48h avant l'événement",
    'payment.subtotal':       'Sous-total',
    'payment.discount':       'Réduction membre',
    'payment.email_conf':     'Confirmation envoyée à',
    'payment.ticket_ready':   'Ton billet est prêt',
    'payment.add_calendar':   'Ajouter au calendrier',
    'payment.view_ticket':    'Voir le billet',
    'payment.cancel_title':   "Annuler l'inscription",
    'payment.cancel_confirm': 'Annuler ma place',
    'payment.cancel_policy':  "Les annulations jusqu'à 48h avant l'événement sont entièrement remboursées.",
    'payment.cancel_too_late':'Moins de 48h restantes — remboursement non disponible.',
    'payment.refund_confirm': 'Remboursement de {amount} traité sous 5 à 7 jours ouvrables.',
    'payment.refunded':       '✓ Remboursement initié',
    'payment.or_free':        'Gratuit pour les membres Gold',
    'payment.spots_left':     '{n} place{s} restante{s}',
    'payment.waitlist_join':  "Rejoindre la liste d'attente",
    'payment.waitlisted':     "✓ Sur liste d'attente — tu seras notifié(e) si une place se libère",

    // Scout (anciennement Scout)
    'circles.title':            'Scout',
    'circles.subtitle':         'Un groupe curatif, un moment partagé',
    'circles.desc':             "L'algo compose un groupe de 4 à 8 personnes compatibles autour d'intérêts communs. Vous vous rencontrez d'abord — les scores sont révélés après.",
    'circles.join_cta':         "Rejoindre la liste d'attente",
    'circles.joined':           'Tu es sur la liste',
    'circles.prefs_title':      'Tes préférences',
    'circles.type_label':       "Type d'activité",
    'circles.avail_label':      'Disponibilités',
    'circles.avail_weekday':    'Soirs de semaine',
    'circles.avail_weekend':    'Week-end',
    'circles.avail_both':       'Les deux',
    'circles.how_title':        'Comment ça marche',
    'circles.how_1':            "Tu rejoins la liste d'attente avec tes préférences",
    'circles.how_2':            "L'algo trouve un groupe compatible (4–8 personnes)",
    'circles.how_3':            'Tu reçois une proposition — date, heure, lieu',
    'circles.how_4':            'Confirme en moins de 48h pour réserver ta place',
    'circles.how_5':            'Rencontre le groupe — sans voir les scores',
    'circles.how_6':            'Vote anonymement après — les matchs sont révélés',
    'circles.proposal_title':   'Proposition de Circle',
    'circles.proposal_sub':     'Un groupe a été trouvé pour toi',
    'circles.confirm_cta':      'Confirmer ma place',
    'circles.decline_cta':      'Je ne peux pas',
    'circles.confirmed':        '✓ Place confirmée',
    'circles.declined':         'Décliné',
    'circles.deadline':         'Confirme avant le',
    'circles.min_warning':      'Minimum 4 confirmations nécessaires',
    'circles.cancelled':        'Circle annulé — pas assez de confirmations',
    'circles.waitlist_pos':     'Position {n} sur la liste',
    'circles.interests_match':  '{n} intérêts communs',
    'circles.post_title':       'Ton Circle a eu lieu',
    'circles.post_vote':        'Qui aimerais-tu revoir ?',
    'circles.post_match':       "C'est un match mutuel",
    'circles.post_no_match':    'Pas de match mutuel cette fois',
    'circles.reveal_scores':    'Voir les scores de compatibilité',
    'circles.score_with':       'Ton score avec {name}',
    'circles.free_honey_gold':  'Gratuit pour les membres Honey & Gold',
    'circles.price_free':       'Gratuit',
    'circles.status_waiting':   "Recherche d'un groupe...",
    'circles.status_proposed':  'Proposition en attente de ta confirmation',
    'circles.status_confirmed': 'Circle confirmé',
    'circles.status_past':      'Circle passé',
    'circles.type_dinner':      'Dîner',
    'circles.type_apero':       'Apéro',
    'circles.type_coffee':      'Café / Brunch',
    'circles.type_outdoor':     'Plein air / Sport',
    'circles.type_culture':     'Culture',
    'circles.type_board_games': 'Jeux de société',
    'circles.type_sport':       'Sport',
    'circles.type_workshop':    'Atelier',
    // Circle waitlist — jours
    'circles.day_mon': 'Lun', 'circles.day_tue': 'Mar', 'circles.day_wed': 'Mer',
    'circles.day_thu': 'Jeu', 'circles.day_fri': 'Ven', 'circles.day_sat': 'Sam', 'circles.day_sun': 'Dim',
    // Circle waitlist — créneaux
    'circles.slot_lunch':      'Déjeuner',
    'circles.slot_after_work': 'Après-boulot',
    'circles.slot_dinner':     'Dîner',
    'circles.slot_brunch':     'Brunch',
    // Circle waitlist — langue
    'circles.lang_fr': 'Français', 'circles.lang_en': 'English', 'circles.lang_both': 'Les deux',
    // Circle waitlist — taille du groupe
    'circles.size_small': 'Petit (4–5)', 'circles.size_large': 'Grand (6–8)', 'circles.size_any': 'Pas de préférence',
    // Circle waitlist — en-têtes de section
    'circles.days_label':      'Jours disponibles',
    'circles.time_label':      'Moment de la journée',
    'circles.lang_label':      'Langue',
    'circles.size_label':      'Taille du groupe',
    'circles.waitlist_info':   'L\'algo cherchera un groupe de 4 à 8 personnes compatibles. Tu recevras une proposition de date et de lieu 1 semaine à l\'avance. Tu auras 48h pour confirmer.',
    // Features réseau — section CML
    'feature.city_features':      'Fonctionnalités par ville',
    'feature.city_features_info': 'Active ou désactive les fonctionnalités pour ta ville. Les changements prennent effet immédiatement pour tous les membres de ton réseau.',
    'circles.members_confirmed':'{n} sur {total} confirmé(e)s',
    'circles.venue_tbd':        'Lieu — communiqué aux participants',
    'circles.expires_in':       'Expire dans {h}h',
    // Features réseau / app
    'feature.events':         'Événements',
    'feature.events_desc':    'Événements publics ouverts à tous les membres (concerts, meetups, visites guidées…)',
    'feature.circles':        'Scout',
    'feature.circles_desc':   'Événements privés et de groupe — assemblés par algo ou invités par l\'hôte',
    'feature.beeconnect':     'BeeConnect',
    'feature.beeconnect_desc':'Découverte en connexion directe entre membres compatibles',
    'feature.beehive':        'BeeHive',
    'feature.beehive_desc':   'Espace communautaire : questions hebdo, histoires de la ruche, saisons',
    // Saisons Hive
    'hive.season_depth':      'Saison Profondeur',
    'hive.season_adventure':  'Saison Aventure',
    'hive.season_connection': 'Saison Connexion',
    'hive.season_reflection': 'Saison Réflexion',
    // Niveaux Hive Score
    'hive.tier_elder':  'Ancien de la Ruche',
    'hive.tier_pillar': 'Pilier de la Ruche',
    'hive.tier_member': 'Membre de la Ruche',
    'hive.tier_newbee': 'Nouvelle Abeille',

    // Identity verification
    'verify.email_title':      'Vérifie ton email',
    'verify.email_sub':        'On a envoyé un code à 6 chiffres à {email}',
    'verify.sms_title':        'Vérifie ton téléphone',
    'verify.sms_sub':          'On a envoyé un code au {phone}',
    'verify.code_label':       'Code de vérification',
    'verify.code_placeholder': 'Code à 6 chiffres',
    'verify.confirm':          'Vérifier',
    'verify.resend':           'Renvoyer le code',
    'verify.resend_in':        'Renvoyer dans {s}s',
    'verify.success_email':    '✓ Email vérifié',
    'verify.success_phone':    '✓ Téléphone vérifié',
    'verify.error':            'Code incorrect — réessaie',
    'verify.expired':          'Code expiré — demandes-en un nouveau',
    'verify.badge_email':      'Email vérifié',
    'verify.badge_phone':      'Téléphone vérifié',
    'verify.phone_label':      'Numéro de téléphone',
    'verify.phone_placeholder':'+1 (514) 000-0000',
    'verify.send_code':        'Envoyer le code',
    'verify.skip':             'Plus tard',
    'verify.why':              'Les profils vérifiés ont 3× plus de visibilité',
    'verify.both_done':        '✓ Identité vérifiée',

    // Referral — structuré
    'referral.title':          'Inviter & gagner',
    'referral.subtitle':       'Chaque ami qui rejoint te rapporte des récompenses',
    'referral.your_code':      'Ton code de parrainage',
    'referral.your_link':      "Ton lien d'invitation",
    'referral.copy_link':      'Copier le lien',
    'referral.copied':         '✓ Copié !',
    'referral.share_whatsapp': 'Partager sur WhatsApp',
    'referral.share_sms':      'Partager par SMS',
    'referral.share_email':    'Partager par email',
    'referral.stats_invited':  'Invité(e)s',
    'referral.stats_joined':   'Inscrit(e)s',
    'referral.stats_earned':   'Mois gagnés',
    'referral.reward_referrer':'1 mois Honey par 2 ami(e)s qui rejoignent',
    'referral.reward_invitee': 'Ton ami(e) reçoit 7 jours Honey gratuits',
    'referral.double_sided':   'Vous deux en bénéficiez',
    'referral.milestone_1':    'Premier ami inscrit !',
    'referral.milestone_2':    '1 mois Honey débloqué',
    'referral.milestone_5':    'Super recruteur·se — 2,5 mois gagnés',
    'referral.progress_to':    'encore {n} pour la prochaine récompense',
    'referral.history':        'Historique des parrainages',
    'referral.pending':        'En attente',
    'referral.rewarded':       'Récompensé',
    'referral.message_tmpl':   "J'utilise BeeLove, une appli de rencontres basée sur la vraie compatibilité. Rejoins avec mon code {code} et obtiens 7 jours gratuits : {link}",

    // Viral share cards
    'viral.share_score':       'Partager mon score',
    'viral.share_quiz':        'Partager mon profil',
    'viral.share_circle':      'Partager mon Circle',
    'viral.card_score_title':  'Mon score BeeLove',
    'viral.card_quiz_title':   'Mon profil de compatibilité',
    'viral.card_event_title':  "J'étais à ce Circle",
    'viral.copy_image':        "Copier l'image",
    'viral.download':          'Télécharger',
    'viral.or_share':          'Ou partager directement',
    'viral.tagline':           'Trouve des personnes compatibles sur BeeLove.me',

    // BeeConnect — mode amical
    'beeconnect.title':           'BeeConnect',
    'beeconnect.subtitle':        'Des connexions sincères, des intérêts partagés',
    'beeconnect.mode_switch':     'Changer de mode',
    'beeconnect.mode_romantic':   'BeeLove — romantique',
    'beeconnect.mode_friendly':   'BeeConnect — amical',
    'beeconnect.mode_label':      'Connexions amicales',
    'mode.romantic':              'Romantique',
    'mode.friendly':              'Amical',
    'beeconnect.discover_title':  'Explorer',
    'beeconnect.interests_first': 'Intérêts communs',
    'beeconnect.connect_cta':     'Connecter',
    'beeconnect.connected':       '✓ Demande envoyée',
    'beeconnect.connections':     'Connexions',
    'beeconnect.connection':      'Connexion',
    'beeconnect.filter_new':      'Nouveaux',
    'beeconnect.filter_near':     'Proches',
    'beeconnect.filter_shared':   'Intérêts communs',
    'beeconnect.filter_field':    'Même domaine',
    'beeconnect.no_score':        '{n} intérêts communs',
    'beeconnect.icebreaker_1':    'Sur quoi tu travailles ces derniers temps ?',
    'beeconnect.icebreaker_2':    "Quel livre t'a changé la perspective récemment ?",
    'beeconnect.icebreaker_3':    'Quelle activité recommanderais-tu à tout le monde ?',
    'beeconnect.icebreaker_4':    "Quel projet t'enthousiasme le plus en ce moment ?",
    'beeconnect.icebreaker_5':    "Qu'as-tu fait le week-end dernier que tu referais ?",
    'beeconnect.filter_label':    'Filtrer par intérêt',
    'beeconnect.empty_title':     'Pas encore de connexions',
    'beeconnect.empty_sub':       'Sois le premier à faire le premier pas',
    'beeconnect.switch_prompt':   'Tu cherches plus ? Passe en mode romantique',
    'beeconnect.profile_badge':   'Ouvert(e) aux amitiés',
    'beeconnect.mutual_friends':  '{n} connexions en commun',

    // Vérification d'âge
    'auth.age_gate_title':    "Vérification d\'âge",
    'auth.age_gate_sub':      'BeeLove est réservé aux adultes de 18 ans et plus.',
    'auth.age_dob_label':     'Date de naissance',
    'auth.age_too_young':     'Tu dois avoir 18 ans ou plus pour rejoindre BeeLove.',
    'auth.age_too_young_sub': 'Nous prenons cette exigence au sérieux pour protéger notre communauté.',
    'auth.age_valid':         'Tu peux continuer',
    'auth.age_why':           "Pourquoi vérifier l\'âge ?",
    'auth.age_why_text':      "BeeLove est une plateforme adulte. Nous vérifions l\'âge pour garder notre communauté sécurisée.",
    'auth.age_legal':         'En continuant tu confirmes avoir 18 ans ou plus.',
    'auth.age_day':           'Jour',
    'auth.age_month':         'Mois',
    'auth.age_year':          'Année',

    // Labels de navigation
    'nav.discover':    'Découvrir',
    'nav.matches':     'Matchs',
    'nav.messages':    'Messages',
    'nav.beehive':     'BeeHive',
    'nav.profile':     'Profil',
    'nav.events':      'Événements',
    'nav.circles':     'Scout',
    'nav.scout':       'Scout',
    'nav.beeconnect':  'Connecter',
    'nav.network':     'Réseau',
    'nav.notifications':'Notifications',

    // BeeConnect chaînes codées en dur
    'beeconnect.interests_label': 'Intérêts',
    'beeconnect.suggested_opener': "Suggestion d\'ouverture",

    // Circles chaînes codées en dur
    'circles.your_group':      'Ton groupe',
    'circles.love_circles':    'Love Circles',
    'circles.pending_proposal':'Proposition en attente',

    // ── Newly added FR translations (complete coverage) ──────────────────
    'about.field_study': 'Domaine d\'études',
    'about.friends_note': 'Plus tes amis répondent, plus ton profil est riche et précis.',
    'about.public_info': 'Ces informations apparaissent sur ton profil public. Plus tu partages, plus tes matchs sont précis.',
    'alacarte.boost_1_d': 'Passe en tête du feed pendant 30 minutes.',
    'alacarte.boost_5_d': 'Utilise quand tu veux — n\'expirent jamais.',
    'alacarte.sl_sub_d': 'Module mensuel — annulable à tout moment.',
    'alacarte.verified_d': 'Vérification vidéo ou pièce d\'identité — badge permanent.',
    'auth.age_gate_title': 'Vérification de l\'âge',
    'auth.age_why': 'Pourquoi on vérifie l\'âge ?',
    'auth.age_why_text': 'BeeLove est une plateforme adulte. Nous vérifions l\'âge pour la sécurité de la communauté.',
    'auth.bee_word6': 'Bee The One',
    'auth.network_montreal': 'Montréal',
    'auth.network_montreal_area': 'Grand Montréal',
    'auth.register': 'S\'inscrire',
    'auth.sign_in_email': 'Se connecter par email',
    'auth.step_gender_q': 'Je m\'identifie comme…',
    'auth.step_quiz_label': 'Dernière étape — nous aide à trouver tes meilleurs matchs',
    'auth.step_seek_sub': 'Sélectionne tout ce qui s\'applique.',
    'auth.welcome_title': 'Trouve quelqu\'un déjà recommandé par ses amis.',
    'axis.account_d': 'Assume-t-il·elle ses erreurs ou les rejette-t-il·elle ?',
    'axis.boredom': 'Goût du changement',
    'axis.boredom_d': 'A-t-il·elle besoin de stimulation constante ?',
    'axis.conflict_d': 'Se ferme-t-il·elle ou s\'engage-t-il·elle directement ?',
    'axis.generosity_d': 'Donne-t-il·elle autant qu\'il·elle prend ?',
    'axis.narrative_d': 'Assume-t-il·elle sa part dans les conflits passés ?',
    'axis.presence_d': 'Est-il·elle vraiment là ou à moitié distrait·e ?',
    'axis.repair_d': 'Laisse-t-il·elle les conflits traîner ou les règle-t-il·elle vite ?',
    'axis.social_d': 'Les foules l\'épuisent-elles ou l\'énergisent-elles ?',
    'axis.stability_d': 'Est-il·elle la même personne dans tous les contextes ?',
    'axis.stakes_d': 'Est-il·elle paralysé·e par l\'incertitude ?',
    'badge.convo_desc': 'Tu t\'engages sincèrement avec tes matchs.',
    'badge.growing_desc': 'Tu as réduit un écart entre toi et tes amis ce mois-ci.',
    'badge.mirror_desc': 'Tu as répondu au même quiz que tes amis.',
    'beeconnect.icebreaker_2': 'Quel livre a changé ta perspective récemment ?',
    'beeconnect.icebreaker_4': 'Sur quel projet es-tu le·la plus enthousiaste en ce moment ?',
    'beeconnect.icebreaker_5': 'Qu\'as-tu fait le week-end dernier que tu referais volontiers ?',
    'beeconnect.suggested_opener': 'Suggestion pour briser la glace',
    'beehive.score_desc': 'Obtenu grâce à un engagement sincère. Ne diminue jamais.',
    'beehive.wise_sub': 'Regards sur l\'amour, la communication et les relations',
    'beewise.art1_sub': 'Les premières impressions par écrit comptent énormément.',
    'beewise.art1_title': 'Les 5 choses que ton premier message dit de toi',
    'beewise.art2_sub': 'L\'écart entre comment tu te vois et comment les autres te voient.',
    'beewise.art2_title': 'Ce que tes avis d\'amis révèlent vraiment',
    'beewise.art3_sub': 'La recherche montre ce qui prédit vraiment la satisfaction relationnelle.',
    'beewise.art3_title': 'Pourquoi les valeurs comptent plus que les hobbies',
    'beewise.art4_sub': 'Pas ce qu\'elle dit — ce qu\'elle fait quand personne ne regarde.',
    'beewise.art4_title': 'Les 7 signes précoces qu\'une personne est sincèrement bienveillante',
    'beewise.art5_sub': 'Recommandations locales selon les profils de compatibilité.',
    'beewise.art5_title': 'Le meilleur premier rendez-vous à Montréal selon ta personnalité',
    'beewise.art6_sub': 'Le bon moment pour proposer un vrai rendez-vous.',
    'beewise.art6_title': 'Comment savoir quand passer de l\'appli à la vraie vie',
    'beewise.cat_comm': 'Communication',
    'beewise.cat_dates': 'Premiers rendez-vous',
    'beewise.cat_dating': 'Psychologie des rencontres',
    'beewise.cat_flags': 'Signaux d\'alarme',
    'beewise.cat_self': 'Connaissance de soi',
    'beewise.more': 'Plus d\'articles',
    'beewise.subtitle': 'Regards sur l\'amour, la communication et les relations',
    'bio.q2': 'Un truc sur lequel tu ne feras jamais de compromis ?',
    'bio.q4': 'Quel type de connexion tu espères trouver ici ?',
    'circles.desc': 'L\'algo compose un groupe de 4 à 8 personnes compatibles autour d\'intérêts communs.',
    'circles.how_1': 'Tu rejoins la liste avec tes préférences',
    'circles.how_2': 'L\'algo trouve un groupe compatible (4–8 personnes)',
    'circles.join_cta': 'Rejoindre la liste d\'attente',
    'circles.status_waiting': 'Recherche d\'un groupe…',
    'circles.type_label': 'Type d\'activité',
    'cms.help': 'Centre d\'aide',
    'cms.terms': 'Conditions d\'utilisation',
    'completion.add_interests': 'Ajouter des intérêts',
    'completion.cta_interests': 'Ajouter des intérêts',
    'completion.m25': 'Bon début ! Ajoute quelques détails pour attirer de meilleurs matchs.',
    'discover.bee_patient': 'Bee Patient',
    'discover.filter_friends': 'Amis d\'abord',
    'discover.label_interests': 'Intérêts',
    'discover.love_language_label': 'Langage de l\'amour',
    'discover.love_words': 'Mots d\'affirmation',
    'discover.quiz_resume': '✦ Tu veux améliorer ton score ? Reprends là où tu t\'es arrêté·e.',
    'discover.quiz_skip': 'Passer pour l\'instant →',
    'discover.report_spam': 'Spam ou tentative d\'arnaque',
    'discover.skip_now': 'Passer pour l\'instant',
    'discover.submit_report': 'Envoyer le signalement',
    'engage.no_more_sub': 'Nous ajoutons de nouveaux profils chaque matin. Ton match parfait est peut-être en train de s\'inscrire.',
    'engage.seen_all_sub': 'Essaie d\'élargir tes filtres — parfois la bonne personne ne ressemble pas à ce qu\'on imagine.',
    'events.qr_show': 'Montre ce QR code à l\'entrée',
    'events.register': 'S\'inscrire',
    'events.register_cta': 'S\'inscrire — {price} {currency}',
    'events.scan_note': 'Scan à la porte pour confirmer ta présence',
    'events.waitlist': 'Rejoindre la liste d\'attente',
    'fr.account_hi': 'Assume immédiatement',
    'fr.account_lo': 'Dévie ou reporte la faute',
    'fr.account_q': 'Quand quelque chose tourne mal à cause de lui·elle, que se passe-t-il ?',
    'fr.boredom_hi': 'Parfaitement à l\'aise à ne rien faire',
    'fr.boredom_lo': 'A besoin de stimulation constante',
    'fr.boredom_q': 'À l\'aise avec un agenda vide et le silence ?',
    'fr.conflict_hi': 'S\'engage directement mais équitablement',
    'fr.conflict_lo': 'Se ferme ou évite le sujet',
    'fr.conflict_q': 'Face à un vrai désaccord, que fait-il·elle habituellement ?',
    'fr.generosity_hi': 'Donne autant qu\'il·elle reçoit',
    'fr.generosity_lo': 'Prend plus qu\'il·elle ne donne',
    'fr.generosity_q': 'Dans ses relations, l\'équilibre donner-recevoir est…',
    'fr.narrative_hi': 'Reconnaît son rôle honnêtement',
    'fr.narrative_lo': 'Toujours le·la héros ou la victime',
    'fr.narrative_q': 'Quand il·elle parle de conflits passés, assume-t-il·elle son rôle ?',
    'fr.presence_hi': 'Entièrement concentré·e et engagé·e',
    'fr.presence_lo': 'Souvent distrait·e, à moitié là',
    'fr.presence_q': 'Quand vous êtes ensemble, il·elle est…',
    'fr.repair_hi': 'Règle ça en quelques heures',
    'fr.repair_lo': 'Laisse les choses traîner des jours',
    'fr.repair_q': 'Après un conflit, à quelle vitesse tend-il·elle la main ?',
    'fr.social_hi': 'Encore énergisé·e, en veut plus',
    'fr.social_lo': 'Isolation totale pour récupérer',
    'fr.social_q': 'Après un long événement social, il·elle a besoin de…',
    'fr.stability_hi': 'Cohérent·e dans toutes les situations',
    'fr.stability_lo': 'Très différent·e selon les contextes',
    'fr.stability_q': 'Est-il·elle la même personne peu importe avec qui il·elle est ?',
    'fr.stakes_hi': 'Est calme et décisif·ve sous pression',
    'fr.stakes_lo': 'Est paralysé·e, a besoin que tout soit planifié',
    'fr.stakes_q': 'Face à une décision importante et incertaine, il·elle…',
    'fr.tap_number': 'Appuie sur un chiffre. Sois honnête — ça compte plus que tu ne crois.',
    'hive.beewise_sub': 'Regards sur l\'amour, la communication et les relations',
    'hive.earned_desc': 'Obtenu grâce à un engagement sincère. Ne diminue jamais.',
    'hive.group_arts_desc': 'Photographes, musiciens, écrivains, designers et penseurs visuels',
    'hive.group_curious_desc': 'Lecteurs, penseurs, créateurs, gamers et apprenants à vie',
    'hive.group_food_desc': 'Amateurs de cuisine, chefs à la maison, amateurs de vin et aventuriers culinaires',
    'hive.group_interests_desc': 'Basé sur les intérêts listés par les membres',
    'hive.group_plant_desc': 'Membres adoptant un mode de vie végétal, végétalien ou végétarien',
    'hive.group_vibe_desc': 'Basé sur l\'humeur et la façon dont les membres se montrent',
    'hive.group_wellness_desc': 'Membres attirés par l\'équilibre, la nature, la santé et la vie intentionnelle',
    'hive.no_members': 'Aucun membre dans ce groupe pour l\'instant.',
    'hive.role_desc': 'Ton rôle se révèle au fil de ton engagement.',
    'hive.stat_matches': 'Matchs',
    'hive.stat_stories': 'Histoires',
    'hive.story2': 'Un Éclaireur et un Bâtisseur ont découvert qu\'ils vivaient à 3 rues d\'écart.',
    'hive.story4': 'Un Bâtisseur a invité 7 amis. Trois sont maintenant membres actifs.',
    'hive.wq2': 'Décris un moment où un·e inconnu·e a changé ta perspective.',
    'hive.wq4': 'À quoi ressemble une matinée parfaite — dans tous les détails ?',
    'hive.wq7': 'Quelle énergie apportes-tu dans une pièce sans le vouloir ?',
    'ice.t_age': 'Demande-leur la meilleure chose qu\'ils·elles ont faite dans leurs {age} ans.',
    'ice.t_dinner': 'S\'ils·elles pouvaient dîner avec n\'importe qui — vivant ou non — qui ce serait ?',
    'ice.t_interest2': 'Vous aimez tous les deux {interest} ! Comment ça a commencé pour toi ?',
    'ice.t_lang': 'Vous parlez tous les deux {lang} — surprends-le·la avec un message en {lang} ?',
    'ice.t_zodiac': 'Un·e {myZodiac} et un·e {theirZodiac} — que penses-tu de l\'astrologie ?',
    'interest.archery': 'Tir à l\'arc',
    'invite.compat_active': 'Ton profil de compatibilité est actif. Plus d\'amis = des résultats plus riches.',
    'invite.friends_who_rate': 'Amis qui t\'ont noté',
    'invite.how_title': 'Comment fonctionnent les avis d\'amis',
    'invite.progress_live': '✦ Ton profil de compatibilité est actif. Plus d\'amis = des résultats plus riches.',
    'invite.progress_needed': 'Il te faut encore {n} réponse{s} d\'amis pour débloquer ton profil de compatibilité.',
    'journey.register_btn': 'S\'inscrire',
    'journey.s2_desc': 'Au moins 5 amis t\'ont noté. La ruche te connaît.',
    'journey.s5_friend_desc': '3 amis ont rejoint BeeLove grâce à ton lien.',
    'journey.s5_hive_desc': '3 amis ont rejoint BeeLove grâce à ton lien d\'invitation.',
    'journey.s6_desc': 'La ruche t\'aime en retour — score élevé, super likes ou match exceptionnel.',
    'landing.bee_the_one': 'Bee The One ✦',
    'landing.bee_the_one_find': 'qu\'ils·elles trouvent.',
    'landing.bee_together': 'Maintenant ils sont',
    'landing.bee_together_2': 'ensemble.',
    'landing.broken_1': 'Les apps de rencontres',
    'landing.broken_2': 'sont cassées.',
    'landing.concept': 'Le concept',
    'landing.every_other_app': 'Toutes les autres apps donnent les mêmes chances à tout le monde. Nous donnons les meilleures chances aux mieux préparés.',
    'landing.everything_need': 'Tout ce que tu dois savoir',
    'landing.faq_a1': 'Tes amis reçoivent un lien privé et répondent à 10 questions sur toi — de façon anonyme.',
    'landing.faq_a2': 'Par conception, c\'est impossible à grande échelle. Obtenir un faux résultat nécessiterait de convaincre plusieurs vraies personnes de mentir.',
    'landing.faq_a3': 'Le quiz comporte 10 questions en trois catégories — Style de vie, Valeurs et Relations. Tu peux le passer, mais tes matchs seront moins précis.',
    'landing.faq_a4': 'BeeLove est disponible dans les pays francophones et anglophones : Canada, France, Belgique, Suisse, États-Unis, Royaume-Uni, Irlande, Australie et Nouvelle-Zélande. Montréal, Paris et Lorient sont les premiers réseaux actifs.',
    'landing.faq_a5': 'Leurs retours sont privés — seul toi vois les insights agrégés. Pense à ça comme une évaluation 360°.',
    'landing.faq_a6': 'Toutes les autres grandes apps donnent le même point de départ à tout le monde. BeeLove donne la meilleure chance aux profils les mieux préparés.',
    'landing.faq_q1': 'Comment fonctionne le quiz des amis ?',
    'landing.faq_q2': 'Peut-on tricher avec le quiz des amis ?',
    'landing.faq_q3': 'Qu\'est-ce que le quiz de compatibilité ?',
    'landing.faq_q4': 'BeeLove est-il disponible dans ma ville ?',
    'landing.faq_q6': 'En quoi BeeLove est-il différent de Hinge, Bumble ou Tinder ?',
    'landing.feat1_desc': 'Au minimum 5 amis répondent à un quiz sur toi avant que ton profil de compatibilité soit actif.',
    'landing.feat1_title': 'Profils vérifiés par les amis',
    'landing.feat2_desc': 'Notre quiz de compatibilité va au-delà des hobbies. On matche sur ce qui compte vraiment.',
    'landing.feat2_title': 'Matching par valeurs',
    'landing.feat3_desc': 'On ne peut pas tricher avec un avis d\'ami. Aucun bot ne peut convaincre de vraies personnes de te recommander.',
    'landing.feat3_title': 'Protection anti-faux intégrée',
    'landing.feat4_desc': 'Réponds à 5 questions rapides. L\'IA rédige ta bio dans ton style — chaleureuse, sincère, toi.',
    'landing.feat4_title': 'Rédaction de bio par IA',
    'landing.fixed': 'Nous les avons réparées.',
    'landing.footer_about': 'À propos',
    'landing.footer_blog': 'Blog',
    'landing.footer_careers': 'Carrières',
    'landing.footer_contact': 'Contact',
    'landing.footer_cookies': 'Politique de cookies',
    'landing.footer_how': 'Comment ça marche',
    'landing.footer_press': 'Presse',
    'landing.footer_pricing': 'Tarifs',
    'landing.footer_privacy': 'Politique de confidentialité',
    'landing.footer_product': 'Produit',
    'landing.footer_signin': 'Se connecter',
    'landing.footer_signup': 'S\'inscrire',
    'landing.footer_terms': 'Conditions d\'utilisation',
    'landing.found_love': 'Ils ont trouvé l\'amour.',
    'landing.four_steps': 'Quatre étapes pour',
    'landing.free_join': 'Gratuit · Sans carte bancaire · Annulable à tout moment',
    'landing.hero_sub': 'BeeLove est la seule app de rencontres où tes amis révèlent ta vraie compatibilité.',
    'landing.hero_title': 'Trouve quelqu\'un déjà recommandé par ses amis.',
    'landing.how_arrow': 'Comment ça marche →',
    'landing.how_step3_body': 'Ton profil de compatibilité s\'approfondit. L\'algorithme met en avant les personnes qui te ressemblent vraiment.',
    'landing.how_step4_body': 'Rencontre-les en vrai aux événements BeeLove. Construis quelque chose de réel.',
    'landing.hundreds': 'Des centaines de profils de plus — vérifiés, notés, réels.',
    'landing.join_free': 'Rejoins les premiers membres de BeeLove. Gratuit pour commencer.',
    'landing.join_free_cta': 'Rejoindre gratuitement — ',
    'landing.knowing': 'la peine d\'être connues',
    'landing.no_dark': 'Pas de manipulation. Pas de fausse urgence. Améliore seulement quand ça fait sens pour toi.',
    'landing.people_worth': 'Des personnes qui valent',
    'landing.plan_feat_ai_bio': 'Générateur de bio IA',
    'landing.plan_feat_boost': 'Boost hebdomadaire',
    'landing.plan_feat_hive': 'Accès communauté BeeHive',
    'landing.plan_feat_invites': '5 invitations d\'amis',
    'landing.plan_feat_liked': 'Voir qui t\'a liké',
    'landing.plan_feat_likes': '10 likes par jour',
    'landing.plan_feat_quiz': 'Quiz de compatibilité',
    'landing.plan_feat_unlimited': 'Invitations illimitées',
    'landing.plan_honey_liked': 'Voir qui t\'a liké',
    'landing.plans_label': 'Plans',
    'landing.profiles_nav': 'Profils',
    'landing.see_waiting': 'Vois qui t\'attend ✦',
    'landing.start_free': 'Commence gratuitement.',
    'landing.start_trial': 'Commencer l\'essai gratuit de 7 jours ✦',
    'landing.stat_networks': 'réseaux actifs',
    'landing.step1_title': 'Bee Présenté·e',
    'landing.step2_title': 'Bee Endossé·e',
    'landing.step3_title': 'Bee Élevé·e',
    'landing.step4_title': 'Bee Amoureux·se',
    'landing.stop_waiting': 'Arrête d\'attendre.',
    'landing.tagline': 'L\'app de rencontres sociale',
    'landing.the_one': 'La bonne personne',
    'landing.the_one_find': 'La bonne personne qu\'ils·elles trouvent.',
    'landing.they_found': 'Ils ont trouvé l\'amour.',
    'landing.to_bee_ready': 'pour être',
    'landing.trust_feature1_desc': 'Au minimum 5 amis répondent à un quiz sur toi avant que ton profil soit complet.',
    'landing.trust_feature2_desc': 'Quiz de 10 questions par des gens qui te connaissent vraiment, pas auto-écrit.',
    'landing.trust_feature3_desc': 'Tous les profils sont modérés. Signaler et bloquer toujours disponibles.',
    'landing.why_title': '✦ Pourquoi BeeLove',
    'life.love_lang': 'Langage de l\'amour',
    'logo.tagline': 'L\'APP DE RENCONTRES SOCIALE',
    'matches.axis_boredom': 'Goût du changement',
    'matches.axis_boredom2': 'Besoin de changement',
    'matches.axis_conflict2': 'Gestion des conflits',
    'matches.axis_stability2': 'Stabilité',
    'matches.axis_stakes': 'Ambition',
    'matches.send_notice': 'Ton message est envoyé dès que tu appuies sur envoyer.',
    'messages.empty_matched': 'Pas encore de conversations.',
    'messages.incoming_sub': 'Quand quelqu\'un t\'écrit avant le match, ça apparaît ici.',
    'messages.no_matches': 'Pas encore de matchs',
    'messages.wrote_to_you': '· t\'a écrit',
    'mod.warn': 'Envoyer un avertissement',
    'my_profile.interests_label': 'Mes intérêts',
    'nav.bee_the_one': 'Bee The One ✦',
    'nav.display_settings': 'Paramètres d\'affichage',
    'nav.settings_label': 'Paramètres d\'affichage',
    'notif.liked_anon': 'Quelqu\'un a aimé ton profil',
    'notif.msg_common': 'On a l\'air d\'avoir beaucoup en commun ! Ce serait sympa de discuter.',
    'notif.msg_music': 'Hey ! J\'ai vu ton profil et j\'adore tes goûts musicaux 🎵',
    'notif.msg_reviews': 'Tes amis t\'ont donné de si bons avis. Curieux·se de te connaître !',
    'payment.cancel_policy': 'Les annulations avant 48h sont intégralement remboursées.',
    'payment.cancel_title': 'Annuler mon inscription',
    'payment.refund_policy': 'Remboursement complet jusqu\'à 48h avant l\'événement',
    'payment.waitlist_join': 'Rejoindre la liste d\'attente',
    'perk.invites': 'Invitations amis illimitées',
    'perk.invites_sub': 'Invite autant d\'amis que tu veux pour noter ton profil.',
    'perk.see_liked': 'Voir qui t\'a liké',
    'perk.see_liked_sub': 'Fini de deviner — vois tous les profils qui t\'ont liké ou noté.',
    'plan.free_f2': 'Jusqu\'à 5 matchs par mois',
    'plan.free_f6': '3 invitations d\'amis',
    'plan.gold_f10': 'Réductions illimitées sur les événements (-20%)',
    'plan.gold_f7': 'Messagerie prioritaire (apparaît en premier)',
    'plan.honey_f2': 'Voir qui t\'a liké',
    'plan.honey_f4': 'Invitations d\'amis illimitées',
    'plan.honey_f9': 'Billets événements à -10%',
    'profile.cannabis_no': 'Non',
    'profile.cannabis_sometimes': 'Parfois',
    'profile.cannabis_yes': 'Oui',
    'profile.choose_prompt': 'Choisir une question…',
    'profile.diet_glutenfree': 'Sans gluten',
    'profile.diet_halal': 'Halal',
    'profile.diet_kosher': 'Casher',
    'profile.diet_omnivore': 'Omnivore',
    'profile.diet_pescatarian': 'Pescétarien·ne',
    'profile.diet_vegan': 'Végane',
    'profile.diet_vegetarian': 'Végétarien·ne',
    'profile.drink_never': 'Jamais',
    'profile.drink_rarely': 'Rarement',
    'profile.drink_regularly': 'Régulièrement',
    'profile.drink_socially': 'En société',
    'profile.edit_email': 'Modifier l\'email',
    'profile.edu_bachelor': 'Licence / Baccalauréat',
    'profile.edu_college': 'Études supérieures en cours',
    'profile.edu_highschool': 'Lycée',
    'profile.edu_master': 'Master',
    'profile.edu_other': 'Autre',
    'profile.edu_trade': 'Formation professionnelle',
    'profile.exercise_daily': 'Tous les jours',
    'profile.exercise_never': 'Jamais',
    'profile.exercise_often': 'Souvent',
    'profile.exercise_sometimes': 'Parfois',
    'profile.identity_values': 'Identité & Valeurs',
    'profile.intent_casual': 'Quelque chose de décontracté',
    'profile.intent_friends': 'Amitié d\'abord',
    'profile.intent_marriage': 'Mariage',
    'profile.intent_notsure': 'Pas encore sûr·e',
    'profile.intent_relationship': 'Relation sérieuse',
    'profile.interests_label': 'Mes intérêts',
    'profile.interests_section': 'Intérêts',
    'profile.lang_arabic': 'Arabe',
    'profile.lang_english': 'Anglais',
    'profile.lang_french': 'Français',
    'profile.lang_mandarin': 'Mandarin',
    'profile.lang_portuguese': 'Portugais',
    'profile.lang_spanish': 'Espagnol',
    'profile.lifestyle_label': 'Style de vie',
    'profile.love_gifts': 'Cadeaux',
    'profile.love_service': 'Actes de service',
    'profile.love_time': 'Qualité de temps partagé',
    'profile.love_touch': 'Contact physique',
    'profile.love_words': 'Mots d\'affirmation',
    'profile.pet_cat': 'Chat(s)',
    'profile.pet_dog': 'Chien(s)',
    'profile.pet_none': 'Pas d\'animaux',
    'profile.pet_other': 'Autres animaux',
    'profile.politics_apolitical': 'Apolitique',
    'profile.politics_conservative': 'Conservateur·trice',
    'profile.politics_liberal': 'Libéral·e',
    'profile.politics_moderate': 'Modéré·e',
    'profile.prompt_along': 'Je m\'entends le mieux avec',
    'profile.prompt_love_lang': 'Mon langage de l\'amour est',
    'profile.religion_buddhist': 'Bouddhiste',
    'profile.religion_christian': 'Chrétien·ne',
    'profile.religion_hindu': 'Hindou·e',
    'profile.religion_jewish': 'Juif·ve',
    'profile.religion_muslim': 'Musulman·e',
    'profile.religion_none': 'Non religieux·se',
    'profile.religion_spiritual': 'Spirituel·le',
    'profile.sleep_early': 'Lève-tôt',
    'profile.sleep_flexible': 'Flexible',
    'profile.sleep_night': 'Couche-tard',
    'profile.smoke_daily': 'Tous les jours',
    'profile.smoke_never': 'Jamais',
    'profile.smoke_rarely': 'Rarement',
    'profile.smoke_socially': 'Occasionnellement',
    'profile.upgrade_perks': 'Voir qui t\'a liké · Invitations illimitées · Boost hebdomadaire',
    'profile.use_this_bio': 'Utiliser cette bio',
    'profile.verify_identity': 'Vérifier mon identité',
    'profile_view.interests_label': 'Intérêts',
    'profile_view.love_language': 'Langage de l\'amour',
    'quiz.q1': 'Quand une relation passée s\'est mal terminée, comment tu racontes cette histoire ?',
    'quiz.q10_o1': 'Très imbriqués — routines partagées, décisions partagées',
    'quiz.q10_o1b': 'Profondément imbriqués — on partage presque tout',
    'quiz.q10_o2': 'Proches mais avec notre propre espace et nos projets',
    'quiz.q10_o3': 'Deux personnes indépendantes qui se choisissent chaque jour',
    'quiz.q10_o4': 'Je suis encore en train de trouver ce que ça veut dire pour moi',
    'quiz.q1_o1': 'J\'avais surtout tort — je sais où j\'ai mal fait',
    'quiz.q1_o2': 'C\'était compliqué — on avait tous les deux une part de responsabilité',
    'quiz.q1_o4': 'J\'ai eu de la malchance — l\'autre personne était le problème',
    'quiz.q2_o1': 'Le bonheur — je ferai ce qui me plaît',
    'quiz.q2_o2': 'Bien, mais je vais probablement écrire à quelqu\'un pour sortir',
    'quiz.q2_o3': 'Un peu agité·e — j\'ai besoin d\'au moins une chose à faire',
    'quiz.q2_o4': 'Anxieux·se — j\'ai besoin de structure pour me sentir bien',
    'quiz.q3_o1': 'Énergisé·e — je pourrais continuer toute la nuit',
    'quiz.q3_o2': 'Content·e mais prêt·e pour une fin de soirée plus calme',
    'quiz.q3_o3': 'Épuisé·e — j\'ai besoin de quelques heures seul·e pour recharger',
    'quiz.q3_o3b': 'Un peu épuisé·e — j\'ai besoin de décompresser',
    'quiz.q3_o4': 'Vidé·e — j\'ai besoin d\'une journée entière de rien pour récupérer',
    'quiz.q3_o4b': 'Vidé·e — j\'ai besoin d\'une journée entière seul·e pour récupérer',
    'quiz.q4_o1': 'Je suis à peu près le·la même dans toutes les situations',
    'quiz.q4_o2': 'J\'adapte mon ton mais reste fondamentalement moi-même',
    'quiz.q4_o3': 'Je change assez — je lis la situation et m\'ajuste',
    'quiz.q4_o4': 'Je suis une personne différente selon les contextes',
    'quiz.q5': 'Un grand changement de vie t\'arrive avec 48h de préavis. Tu…',
    'quiz.q5_o1': 'Te sens enthousiaste — je m\'épanouis dans le changement',
    'quiz.q5_o1b': 'Te sens stimulé·e — le changement est excitant',
    'quiz.q5_o2': 'Te sens stressé·e mais tu gères étape par étape',
    'quiz.q5_o3': 'Te sens anxieux·se — j\'ai besoin de temps pour traiter avant d\'agir',
    'quiz.q5_o4': 'Te sens bloqué·e — le changement sans préparation m\'est très difficile',
    'quiz.q5_o4b': 'Tu te bats — tu as besoin de temps et d\'un plan pour te sentir bien',
    'quiz.q6_o1b': 'Je veux définitivement des enfants — c\'est une priorité',
    'quiz.q6_o3': 'Ouvert·e si la bonne personne se présente',
    'quiz.q6_o3b': 'Indécis·e — je verrai comment la vie se déroule',
    'quiz.q6_o4b': 'Je ne veux pas d\'enfants — mon choix est fait',
    'quiz.q7_o1': 'Lève-tôt — je suis au meilleur de ma forme avant midi',
    'quiz.q7_o1b': 'Lève-tôt — debout à 6h, productif·ve à 7h',
    'quiz.q7_o2': 'Plutôt matin mais flexible',
    'quiz.q7_o3': 'Couche-tard — je prends vie en soirée',
    'quiz.q7_o3b': 'Couche-tard — je prends vie après 22h',
    'quiz.q7_o4': 'Imprévisible — ça change constamment',
    'quiz.q7_o4b': 'Complètement chaotique — je n\'ai pas d\'horaire fixe',
    'quiz.q8': 'Où te vois-tu dans 5 ans ?',
    'quiz.q8_abroad': 'À l\'étranger — je veux vivre ailleurs',
    'quiz.q8_city': 'Vie en ville — énergie, culture, tout à portée',
    'quiz.q8_o2': 'J\'ai une direction mais je reste ouvert·e au changement',
    'quiz.q8_o3': 'J\'ai quelques idées mais rien de concret',
    'quiz.q8_o4': 'Je préfère voir où la vie me mène',
    'quiz.q8_rural': 'Petite ville ou campagne — espace et simplicité',
    'quiz.q8_suburbs': 'Banlieue — calme mais connecté·e',
    'quiz.q9_o1': 'L\'aborder directement dès que possible',
    'quiz.q9_o2': 'Prendre une grande respiration, puis en parler calmement',
    'quiz.q9_o3': 'Avoir besoin de temps pour réfléchir avant de dire quoi que ce soit',
    'quiz.q9_o3b': 'Besoin d\'espace d\'abord — ensuite j\'arrive à en parler',
    'quiz.q9_o4': 'Avoir tendance à me taire et espérer que ça se résolve seul',
    'quiz.resume_prompt': '✦ Tu veux améliorer ton score de compatibilité ? Reprends là où tu t\'es arrêté·e.',
    'quiz.skip_now2': 'Passer pour l\'instant',
    'ratings.based_on': 'Basé sur {n} avis d\'amis · Les votes individuels ne sont jamais affichés.',
    'ratings.no_ratings': 'Pas encore d\'avis — invite des amis depuis l\'onglet Inviter.',
    'ratings.who_rated': 'Qui t\'a noté',
    'referral.message_tmpl': 'J\'utilise BeeLove, une app de rencontres basée sur la vraie compatibilité. Rejoins-moi :',
    'referral.your_link': 'Ton lien d\'invitation',
    'report.done_sub': 'Merci d\'aider BeeLove à rester sûr. Notre équipe examine tous les signalements sous 24h.',
    'section.interests': 'Intérêts',
    'settings.age_range': 'Tranche d\'âge',
    'settings.edit_email': 'Modifier l\'email',
    'settings.more_networks': 'D\'autres réseaux arrivent bientôt — Toronto, Vancouver, Londres, New York et plus.',
    'settings.verify_identity': 'Vérifier mon identité',
    'upgrade.addons_sub': 'Achats uniques et modules mensuels. Cumulables avec n\'importe quel plan.',
    'upgrade.feat_invites': 'Invitations amis illimitées',
    'upgrade.feat_see_liked': 'Voir qui t\'a liké',
    'upgrade.feat_see_liked2': 'Voir qui t\'a liké',
    'upgrade.no_fees': 'Annulable à tout moment. Sans frais cachés.',
    'upgrade.plan_active': 'Ton plan actuel reste actif jusqu\'à la fin de la période de facturation.',
    'upgrade.trial': '{n} jours d\'essai gratuit',
    'val.friendship': 'Amitié',
    'val.no_pets': 'Pas d\'animaux',
    'val.words': 'Mots d\'affirmation',
    'viral.card_event_title': 'J\'étais à ce Circle',
    'viral.copy_image': 'Copier l\'image',



    // ── AGENDA ──────────────────────────────────────────────────────────────
    'nav.agenda':              'Agenda',
    'agenda.tab_upcoming':     'À venir',
    'agenda.tab_mine':         'Mon agenda',
    'agenda.tab_past':         'Passés',
    'agenda.coming_soon':      'Événements à venir',
    'agenda.none_upcoming':    "Rien à venir pour l'instant",
    'agenda.none_upcoming_sub':'Les événements et Circles de ton réseau apparaîtront ici.',
    'agenda.none_mine':        "Rien dans ton agenda pour l'instant",
    'agenda.none_mine_sub':    "Inscris-toi à un événement ou rejoins la liste d'un Circle.",
    'agenda.none_past':        "Pas encore d'événements passés",

    // ── MESSAGES (nouvelles clés) ────────────────────────────────────────
    'messages.all':            'Tout',
    'messages.romantic':       'Matchs',
    'messages.friendly':       'Connexions',
    'messages.no_connections': 'Pas encore de connexions',
    'messages.no_connections_sub': 'Connecte-toi avec des gens dans BeeConnect pour démarrer une conversation.',
    'messages.no_matches_sub': "Like quelqu'un en retour dans Découvrir pour démarrer une conversation.",
    'messages.icebreakers':    'Suggestions pour briser la glace',
    'messages.placeholder':    'Écris un message…',

    // ── SUBSCRIPTIONS ────────────────────────────────────────────────────────
    'subs.launch_free':       'Gratuit au lancement',
    'subs.launch_free_sub':   'Toutes les fonctionnalités sont gratuites pendant la croissance. Les abonnements arrivent plus tard.',
    'upgrade.launch_free':    "Tu es Founding Member — tout est gratuit, à vie.",
    'upgrade.launch_free_sub':"Tu as rejoint pendant la phase fondatrice. Quand les forfaits payants lanceront plus tard cette année, tu gardes l'accès complet — sans abonnement, sans essai, sans expiration.",

    // ── INVITE SYSTEM ────────────────────────────────────────────────────────
    'invite.step_title':            "Entre ton code d'invitation",
    'invite.step_sub':              "BeeLove est uniquement sur invitation pour l'instant. Entre le code que tu as reçu.",
    'invite.invalid_code':          'Code invalide ou déjà utilisé.',
    'invite.expired_code':          'Ce code a expiré.',
    'invite.founding_welcome':      '✦ Code membre fondateur — bienvenue dans la première vague.',
    'invite.valid_welcome':         '✓ Code valide — bienvenue sur BeeLove.',
    'invite.waitlist_title':        'Demande ton invitation',
    'invite.waitlist_sub':          "BeeLove grandit par invitation. Laisse ton email et on t'envoie un code.",
    'invite.waitlist_confirmed_title': 'Tu es sur la liste',
    'invite.waitlist_confirmed_sub': "On t'enverra un code d'invitation dès que ta place est prête.",
    'invite.already_have':          'Tu as déjà un code ?',
    'invite.remaining':             '{n} invitation{s} restante{s}',
    'invite.share_title':           'Invite tes amis sur BeeLove',
    'invite.share_sub':             'Chaque ami qui rejoint renforce ton profil.',
    'invite.copy_link':             "Copier le lien d'invitation",
    'invite.founding_badge':        'Membre fondateur',

    // ── CML RELEVÉS FISCAUX ───────────────────────────────────────────────────
    'cml.tax_report':       'Relevé fiscal',
    'cml.tax_year':         'Année fiscale',
    'cml.tax_generate':     'Générer le relevé',
    'cml.tax_print':        'Imprimer / Sauvegarder PDF',
    'cml.tax_legal_name':   'Nom légal',
    'cml.tax_number':       'Numéro fiscal / NAS / SIRET',
    'cml.tax_address':      'Adresse professionnelle',
    'cml.tax_note':         'Ce document est à des fins fiscales. Consultez votre comptable.',
    'gamification.streak':  'Jours consécutifs',
    'gamification.journey': 'Ton parcours',
    'gamification.next':    'Prochain palier',
    'gamification.milestone_first_match': 'Premier match !',
    'gamification.milestone_first_like':  'Premier like envoyé',

    // ── ADMIN CENTRE DE CONTRÔLE ─────────────────────────────────────────────
    'admin.control_center':     'Centre de contrôle',
    'admin.pages_sections':     'Pages & sections',
    'admin.system_features':    'Fonctionnalités système',
    'admin.plan_matrix':        'Plan × fonctionnalité',
    'admin.founders_lab':       'Founders Lab',
    'admin.deploy_to_all':      'Déployer à tous',
    'admin.founders_only':      'Fondateurs seulement',
    'admin.wave':               'Vague',

    // ── FRIEND RATING (questionnaire public) ─────────────────────────────
    'rate.social_proof':        '{n} {word} ont déjà répondu pour {name}',
    'rate.social_word_one':     'personne a',
    'rate.social_word_many':    'personnes ont',
    'rate.phase_break_title':   'À mi-chemin !',
    'rate.phase_break_sub':     'Tu as répondu aux 5 questions clés. Tu peux soumettre maintenant ou continuer avec 5 questions plus approfondies.',
    'rate.phase_continue':      'Continuer — 5 questions de plus →',
    'rate.phase_submit_now':    'Soumettre avec 5 réponses',
    'rate.share_title':         'Tu connais quelqu\'un d\'autre qui pourrait aider {name} ?',
    'rate.share_sub':           'Transfère ce lien — les réponses s\'accumulent.',
    'rate.share_copy':          'Copier le lien',
    'rate.share_copied':        '✓ Copié !',
    'rate.share_native':        'Partager →',
    'rate.answers_saved':       'Tes réponses sont déjà enregistrées.',
  },
};

// ── Translation helpers ────────────────────────────────────────────────────
export function t(lang, key, vars = {}) {
  const strings = getEffectiveTranslations(lang);
  let str = strings[key] || TRANSLATIONS.en[key] || key;
  Object.entries(vars).forEach(([k, v]) => { str = str.replace(`{${k}}`, v); });
  return str;
}

export function getActiveLang() {
  try { return localStorage.getItem('beeloveLang') || 'en'; } catch { return 'en'; }
}

export function setActiveLang(code) {
  try { localStorage.setItem('beeloveLang', code); } catch {}
}

export function saveTranslationOverrides(lang, overrides) {
  try { localStorage.setItem(`beeloveTranslations_${lang}`, JSON.stringify(overrides)); } catch {}
}

export function loadTranslationOverrides(lang) {
  try { return JSON.parse(localStorage.getItem(`beeloveTranslations_${lang}`) || '{}'); } catch { return {}; }
}

export function getEffectiveTranslations(lang) {
  const base = TRANSLATIONS[lang] || TRANSLATIONS.en;
  const overrides = loadTranslationOverrides(lang);
  return { ...base, ...overrides };
}

export function getTranslationPairs(lang) {
  const en = TRANSLATIONS.en;
  const target = getEffectiveTranslations(lang);
  return Object.keys(en).map(key => ({
    key,
    en: en[key],
    value: target[key] || '',
    missing: !(TRANSLATIONS[lang]?.[key]) && !loadTranslationOverrides(lang)[key],
    overridden: !!loadTranslationOverrides(lang)[key],
    group: key.split('.')[0],
  }));
}

// Group keys by their prefix for the admin UI
export function getTranslationGroups(lang) {
  const pairs = getTranslationPairs(lang);
  const groups = {};
  pairs.forEach(p => {
    if (!groups[p.group]) groups[p.group] = [];
    groups[p.group].push(p);
  });
  return groups;
}

// ── Translation helpers (non-React) ────────────────────────────────────────
let _AppCtx = null;
export function registerAppContext(ctx) { _AppCtx = ctx; }
export function getAppCtx() { return _AppCtx; }

// ── PATCH: Additional strings from comprehensive audit ─────────────────────
// Appended to both EN and FR sections via TRANSLATIONS_PATCH below.
// These are merged into getEffectiveTranslations() automatically.


// TRANSLATIONS_PATCH merged into TRANSLATIONS above
export const TRANSLATIONS_PATCH = { en: {}, fr: {} };

// ── Network → default language ─────────────────────────────────────────────
// Global default is 'en'. French networks listed explicitly.
export const NETWORK_LANGS = {
  montreal: 'fr',
  ca_montreal: 'fr',
  lorient: 'fr',
  fr_lorient: 'fr',
  paris: 'fr',
  fr_paris: 'fr',
  brussels: 'fr',
  be_brussels: 'fr',
};

export function getNetworkDefaultLang(networkId) {
  return NETWORK_LANGS[networkId] || 'en';
}

// Priority: stored user preference → browser language → 'en'
export function getInitialLang() {
  try {
    const stored = localStorage.getItem('beeloveLang');
    if (stored) return stored;
  } catch { /* SSR / private browsing */ }
  const browser = (navigator?.language || '').toLowerCase();
  const supported = SUPPORTED_LANGS.map(l => l.code);
  // Match full code (e.g. 'fr') or BCP-47 prefix (e.g. 'fr-CA' → 'fr')
  const match = supported.find(c => browser === c) || supported.find(c => browser.startsWith(c + '-'));
  return match || 'en';
}
